import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  cancelScheduleVote,
  checkScheduleAttendance,
  createSchedule,
  deleteSchedule,
  fetchScheduleAttendances,
  fetchTeamSchedules,
  fetchScheduleLineup,
  fetchUpcomingSchedules,
  saveScheduleLineup,
  sendScheduleVoteReminder,
  updateSchedule,
  voteSchedule,
} from "../../api/schedule/scheduleApi";
import {
  closeTeam,
  getMyTeams,
  updateTeamInfo,
  uploadTeamLogoForTeam,
} from "../../api/team/teamApi";
import {
  delegateTeamChair,
  fetchTeamMembers,
  leaveTeam,
  removeTeamMember,
  updateTeamMemberRole,
} from "../../api/team/teamMemberApi";
import {
  approveTeamJoinRequest,
  fetchTeamJoinRequests,
  rejectTeamJoinRequest,
} from "../../api/team/joinRequestApi";
import {
  createScheduleTemplate,
  deleteScheduleTemplate,
  fetchScheduleTemplates,
  updateScheduleTemplate,
} from "../../api/schedule/templateApi";
import {
  normalizeScheduleCategory,
  normalizeSchedulePayloadItem,
  toNullableCoordinate,
} from "../../api/schedule/scheduleModel";
import { searchAddressCoordinates } from "../../api/location/kakaoLocationApi";
import { loadKakaoPostcodeScript } from "../../api/location/kakaoPostcodeApi";
import ScheduleVoteCard from "../../components/schedule-vote-card/ScheduleVoteCard";
import useAutoClearMessage from "../../hooks/useAutoClearMessage";
import useBodyScrollLock from "../../hooks/useBodyScrollLock";

const ROLE_OPTIONS = [
  { value: "CHAIR", label: "회장" },
  { value: "STAFF", label: "운영진" },
  { value: "MEMBER", label: "회원" },
];
const STAFF_ROLES = new Set(["CHAIR", "STAFF"]);
const DAY_NAMES = ["일", "월", "화", "수", "목", "금", "토"];
const TAB_ITEMS = [
  { key: "upcoming", label: "다가오는 일정" },
  { key: "calendar", label: "캘린더" },
  { key: "members", label: "회원" },
  { key: "team", label: "팀관리" },
  { key: "manage", label: "일정 관리" },
  { key: "template", label: "템플릿 설정" },
  { key: "join", label: "가입 신청" },
];
const MEMBER_VISIBLE_TAB_KEYS = new Set(["upcoming", "calendar", "members"]);
const MANAGE_FILTER_OPTIONS = [
  { value: "UPCOMING", label: "다가오는 일정" },
  { value: "ALL", label: "전체 일정" },
  { value: "PAST", label: "지난 일정" },
];
const TEMPLATE_NAME_MAX_LENGTH = 40;
const UPCOMING_PAGE_SIZE = 8;
const DEFAULT_TEMPLATE_VOTE_CLOSE_HOURS = 6;
const TEAM_MEMBER_ROLE_ORDER = {
  CHAIR: 0,
  STAFF: 1,
  MEMBER: 2,
};
const TEAM_AGE_BAND_OPTIONS = [
  "10대 후반",
  "20대 초반",
  "20대 후반",
  "30대 초반",
  "30대 후반",
  "40대 이상",
];
const TEAM_GENDER_TYPE_OPTIONS = [
  { value: "MIXED", label: "혼성" },
  { value: "MALE", label: "남성" },
  { value: "FEMALE", label: "여성" },
];

const DEFAULT_TEAM_PROFILE = {
  id: "",
  name: "팀",
  chair: "-",
  logoUrl: "",
  updatedAt: "",
  activityWeekdays: [],
  timeRange: "",
  ageBand: "-",
  genderType: "혼성",
  role: "MEMBER",
};

const DEFAULT_TEAM_LOGO_SVG = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120" fill="none">
    <rect width="120" height="120" rx="24" fill="#111827"/>
    <circle cx="60" cy="46" r="18" fill="#D1FAE5"/>
    <path d="M30 93c0-14 13-24 30-24s30 10 30 24v3H30v-3z" fill="#A7F3D0"/>
    <text x="60" y="112" text-anchor="middle" fill="#F8FAFC" font-family="Arial, sans-serif" font-size="10" font-weight="700">AH SHOOT</text>
  </svg>`,
);
const DEFAULT_TEAM_LOGO_URL = `data:image/svg+xml;utf8,${DEFAULT_TEAM_LOGO_SVG}`;

const INITIAL_SCHEDULES = [];

const INITIAL_JOIN_REQUESTS = [];

const INITIAL_FORM_STATE = {
  title: "",
  category: "TRAINING",
  venueName: "",
  address: "",
  detailAddress: "",
  latitude: "",
  longitude: "",
  startsAt: "",
  endsAt: "",
  voteEndAt: "",
  description: "",
};
const REQUIRED_SCHEDULE_FIELDS = [
  "title",
  "venueName",
  "address",
  "startsAt",
  "endsAt",
  "voteEndAt",
];

const WEEKDAY_TO_INDEX = {
  일: 0,
  월: 1,
  화: 2,
  수: 3,
  목: 4,
  금: 5,
  토: 6,
};
const WEEKDAY_ENUM_TO_LABEL = {
  SUNDAY: "일",
  MONDAY: "월",
  TUESDAY: "화",
  WEDNESDAY: "수",
  THURSDAY: "목",
  FRIDAY: "금",
  SATURDAY: "토",
};
const WEEKDAY_LABEL_TO_ENUM = {
  일: "SUNDAY",
  월: "MONDAY",
  화: "TUESDAY",
  수: "WEDNESDAY",
  목: "THURSDAY",
  금: "FRIDAY",
  토: "SATURDAY",
};

const DEFAULT_ACTIVE_PLAYER_COUNT = 11;
const DEFAULT_LINEUP_SEGMENT_COUNT = 4;
const MIN_LINEUP_SEGMENT_COUNT = 1;
const MAX_LINEUP_SEGMENT_COUNT = 8;
const CUSTOM_LINEUP_PLAYER_ID_PREFIX = "guest-player-";
const DEFAULT_LINEUP_COORDINATES = [
  { x: 50, y: 88 }, // GK
  { x: 18, y: 68 },
  { x: 38, y: 72 },
  { x: 62, y: 72 },
  { x: 82, y: 68 },
  { x: 28, y: 50 },
  { x: 50, y: 52 },
  { x: 72, y: 50 },
  { x: 22, y: 30 },
  { x: 50, y: 24 },
  { x: 78, y: 30 },
];
const LINEUP_AUTO_PLACE_X_COORDINATES = [16, 38, 62, 84];
const LINEUP_AUTO_PLACE_Y_COORDINATES = [18, 34, 50, 66, 82];
const LINEUP_AUTO_PLACE_MIN_GAP = 8;
const KAKAO_POSTCODE_LAYER_ID = "ahshoot-kakao-postcode-layer";
const IOS_TEXT_SELECTION_GUARD_STYLE = {
  userSelect: "none",
  WebkitUserSelect: "none",
  WebkitTouchCallout: "none",
};
const LINEUP_TOUCH_GUARD_STYLE = {
  ...IOS_TEXT_SELECTION_GUARD_STYLE,
  touchAction: "none",
  WebkitTapHighlightColor: "transparent",
};

// 날짜를 캘린더 key(YYYY-MM-DD)로 맞춰 월/일정 매핑에 사용한다.
function toDateKey(dateValue) {
  const date = new Date(dateValue);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// datetime-local input과 호환되는 문자열 포맷으로 변환한다.
function formatDateTimeLocal(dateValue) {
  const date = new Date(dateValue);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

// 팀 템플릿 시간 문자열(HH:mm)을 숫자 시/분으로 파싱한다.
function parseTimeText(timeText) {
  const [hourText = "19", minuteText = "00"] = String(timeText || "")
    .trim()
    .split(":");
  return {
    hours: Number.parseInt(hourText, 10) || 19,
    minutes: Number.parseInt(minuteText, 10) || 0,
  };
}

// 팀 기본 시간대('19:30 ~ 22:00')를 시작/종료 시각으로 분해한다.
function parseTeamTimeRange(timeRangeText) {
  const [startTimeText = "19:30", endTimeText = "22:00"] = String(
    timeRangeText || "",
  )
    .split("~")
    .map((text) => text.trim());

  return { startTimeText, endTimeText };
}

function appendCacheKeyToImageUrl(imageUrl, cacheKey) {
  if (!imageUrl || !cacheKey || String(imageUrl).startsWith("data:")) {
    return imageUrl;
  }
  const delimiter = imageUrl.includes("?") ? "&" : "?";
  return `${imageUrl}${delimiter}v=${encodeURIComponent(String(cacheKey))}`;
}

function closeExistingKakaoPostcodeLayer() {
  const existingLayer = document.getElementById(KAKAO_POSTCODE_LAYER_ID);
  if (existingLayer) {
    existingLayer.remove();
  }
}

// iOS 인앱브라우저/웹뷰에서 popup open 차단 이슈를 피하기 위해 주소 검색창을 페이지 내 레이어로 띄운다.
function openKakaoPostcodeLayer({ onComplete, onError, onClose }) {
  if (typeof window === "undefined" || !window.daum?.Postcode) {
    throw new Error("카카오 주소 검색창을 열지 못했습니다.");
  }

  closeExistingKakaoPostcodeLayer();

  const previousBodyOverflow = document.body.style.overflow;
  const overlay = document.createElement("div");
  overlay.id = KAKAO_POSTCODE_LAYER_ID;
  overlay.style.position = "fixed";
  overlay.style.inset = "0";
  overlay.style.width = "100vw";
  overlay.style.height = "100dvh";
  overlay.style.background = "rgba(15, 23, 42, 0.5)";
  overlay.style.zIndex = "2147483000";
  overlay.style.display = "flex";
  overlay.style.alignItems = "center";
  overlay.style.justifyContent = "center";
  overlay.style.padding = "16px";
  overlay.style.boxSizing = "border-box";
  overlay.style.overscrollBehavior = "contain";

  const panel = document.createElement("div");
  panel.style.width = "420px";
  panel.style.height = "560px";
  panel.style.background = "#ffffff";
  panel.style.borderRadius = "14px";
  panel.style.overflow = "hidden";
  panel.style.boxShadow = "0 20px 50px rgba(15, 23, 42, 0.35)";
  panel.style.display = "flex";
  panel.style.flexDirection = "column";
  panel.style.boxSizing = "border-box";
  panel.style.maxWidth = "100%";
  panel.style.maxHeight = "100%";
  panel.style.overscrollBehavior = "contain";
  panel.style.minWidth = "0";

  const header = document.createElement("div");
  header.style.height = "44px";
  header.style.padding = "0 12px";
  header.style.display = "flex";
  header.style.alignItems = "center";
  header.style.justifyContent = "space-between";
  header.style.borderBottom = "1px solid #e4e7ec";
  header.style.flexShrink = "0";

  const title = document.createElement("strong");
  title.textContent = "주소 검색";
  title.style.fontSize = "14px";
  title.style.color = "#101828";

  const closeButton = document.createElement("button");
  closeButton.type = "button";
  closeButton.textContent = "X";
  closeButton.style.border = "0";
  closeButton.style.background = "transparent";
  closeButton.style.color = "#475467";
  closeButton.style.fontSize = "14px";
  closeButton.style.fontWeight = "700";
  closeButton.style.cursor = "pointer";

  const container = document.createElement("div");
  container.style.flex = "1 1 auto";
  container.style.minHeight = "0";
  container.style.minWidth = "0";
  container.style.width = "100%";
  container.style.height = "100%";
  container.style.overflow = "hidden";

  const applyResponsivePanelLayout = () => {
    const visualViewport = window.visualViewport;
    const widthCandidates = [
      visualViewport?.width,
      window.innerWidth,
      document.documentElement.clientWidth,
    ].filter((value) => Number.isFinite(value) && value > 0);
    const heightCandidates = [
      visualViewport?.height,
      window.innerHeight,
      document.documentElement.clientHeight,
    ].filter((value) => Number.isFinite(value) && value > 0);
    const viewportWidth =
      (widthCandidates.length > 0 ? Math.min(...widthCandidates) : null) || 390;
    const viewportHeight =
      (heightCandidates.length > 0 ? Math.min(...heightCandidates) : null) || 700;
    const isCompactViewport = viewportWidth <= 900;
    const horizontalPadding = isCompactViewport ? 0 : 16;
    const verticalPadding = isCompactViewport ? 0 : 16;
    const availableWidth = Math.max(
      280,
      viewportWidth - horizontalPadding * 2,
    );
    const availableHeight = Math.max(
      320,
      viewportHeight - verticalPadding * 2,
    );

    if (isCompactViewport) {
      overlay.style.padding = "0";
      overlay.style.alignItems = "center";
      overlay.style.justifyContent = "center";
      panel.style.width = "100vw";
      panel.style.height = "100dvh";
      panel.style.maxWidth = "100vw";
      panel.style.maxHeight = "100dvh";
      panel.style.borderRadius = "0";
      panel.style.boxShadow = "none";
      panel.style.margin = "0";
      panel.style.border = "0";
      header.style.height = "50px";
      header.style.padding = "0 14px";
      return;
    }

    overlay.style.padding = `${verticalPadding}px ${horizontalPadding}px`;
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    panel.style.width = `${Math.min(560, availableWidth)}px`;
    panel.style.height = `${Math.min(760, availableHeight)}px`;
    panel.style.maxWidth = "560px";
    panel.style.maxHeight = "760px";
    panel.style.borderRadius = "14px";
    panel.style.boxShadow = "0 20px 50px rgba(15, 23, 42, 0.35)";
    header.style.height = "44px";
    header.style.padding = "0 12px";
  };

  header.append(title, closeButton);
  panel.append(header, container);
  overlay.appendChild(panel);
  document.body.appendChild(overlay);
  applyResponsivePanelLayout();
  document.body.style.overflow = "hidden";

  let isClosed = false;
  const cleanup = () => {
    if (isClosed) {
      return;
    }
    isClosed = true;
    window.removeEventListener("resize", applyResponsivePanelLayout);
    window.visualViewport?.removeEventListener("resize", applyResponsivePanelLayout);
    window.visualViewport?.removeEventListener("scroll", applyResponsivePanelLayout);
    overlay.remove();
    document.body.style.overflow = previousBodyOverflow;
    if (typeof onClose === "function") {
      onClose();
    }
  };

  closeButton.addEventListener("click", cleanup);
  overlay.addEventListener("click", (event) => {
    if (event.target === overlay) {
      cleanup();
    }
  });
  panel.addEventListener("click", (event) => {
    event.stopPropagation();
  });
  window.addEventListener("resize", applyResponsivePanelLayout);
  window.visualViewport?.addEventListener("resize", applyResponsivePanelLayout);
  window.visualViewport?.addEventListener("scroll", applyResponsivePanelLayout);

  const postcode = new window.daum.Postcode({
    width: "100%",
    height: "100%",
    oncomplete: (data) => {
      Promise.resolve(onComplete?.(data))
        .catch((error) => {
          if (typeof onError === "function") {
            onError(
              error instanceof Error
                ? error
                : new Error("주소 선택 처리에 실패했습니다."),
            );
          }
        })
        .finally(cleanup);
    },
    onclose: cleanup,
  });

  postcode.embed(container, {
    autoClose: false,
  });
}

function normalizeTeamGenderType(value) {
  const normalized = String(value || "")
    .trim()
    .toUpperCase();

  if (normalized === "MALE" || value === "남성") {
    return "MALE";
  }
  if (normalized === "FEMALE" || value === "여성") {
    return "FEMALE";
  }
  return "MIXED";
}

function buildTeamManageFormState(teamProfile, team) {
  const { startTimeText, endTimeText } = parseTeamTimeRange(
    teamProfile?.timeRange,
  );
  const normalizedWeekdays = getSortedWeekdays(teamProfile?.activityWeekdays);
  const resolvedAgeBand =
    String(teamProfile?.ageBand || "").trim() && teamProfile?.ageBand !== "-"
      ? String(teamProfile.ageBand).trim()
      : TEAM_AGE_BAND_OPTIONS[0];

  return {
    name: teamProfile?.name || "",
    chairName: teamProfile?.chair || "",
    activityWeekdays:
      normalizedWeekdays.length > 0 ? normalizedWeekdays : ["수"],
    startTime: startTimeText,
    endTime: endTimeText,
    ageBand: resolvedAgeBand,
    genderType: normalizeTeamGenderType(teamProfile?.genderType),
    description: typeof team?.description === "string" ? team.description : "",
    logoUrl: teamProfile?.logoUrl || "",
  };
}

// 요일 목록을 중복 제거 + 주간 순서로 정렬한다.
function getSortedWeekdays(weekdayList) {
  const uniqueWeekdays = Array.from(
    new Set(
      Array.isArray(weekdayList)
        ? weekdayList.filter((day) => WEEKDAY_TO_INDEX[day] !== undefined)
        : [],
    ),
  );
  return uniqueWeekdays.sort(
    (a, b) => WEEKDAY_TO_INDEX[a] - WEEKDAY_TO_INDEX[b],
  );
}

// 팀 프로필을 기준으로 일정 등록 기본 템플릿을 계산한다.
function getDefaultRecurringTemplate(teamProfile = DEFAULT_TEAM_PROFILE) {
  const { startTimeText, endTimeText } = parseTeamTimeRange(
    teamProfile.timeRange,
  );
  return {
    id: "",
    name: "기본 템플릿",
    weekdays: getSortedWeekdays(teamProfile.activityWeekdays),
    category: "TRAINING",
    title: "",
    venueName: "",
    address: "",
    detailAddress: "",
    latitude: "",
    longitude: "",
    startTime: startTimeText,
    endTime: endTimeText,
    description: "",
  };
}

function normalizeTemplateWeekdays(weekdayList, fallbackWeekdays = []) {
  const normalizedWeekdayLabels = (
    Array.isArray(weekdayList) ? weekdayList : []
  )
    .map((weekday) => {
      const weekdayText = String(weekday || "").trim();
      return WEEKDAY_ENUM_TO_LABEL[weekdayText] || weekdayText;
    })
    .filter((weekday) => WEEKDAY_TO_INDEX[weekday] !== undefined);

  const sortedWeekdays = getSortedWeekdays(normalizedWeekdayLabels);
  if (sortedWeekdays.length > 0) {
    return sortedWeekdays;
  }
  return getSortedWeekdays(fallbackWeekdays);
}

// API/저장 포맷 차이를 흡수해 템플릿 공통 모델로 정규화한다.
function normalizeRecurringTemplate(
  template,
  fallback = getDefaultRecurringTemplate(),
) {
  const normalizedName = String(
    template?.name || template?.templateName || fallback.name || "",
  ).trim();
  const rawCategory =
    template?.category ??
    template?.type ??
    template?.scheduleType ??
    template?.eventType ??
    fallback.category;
  const normalizedTitle = String(
    template?.title ||
      template?.defaultTitle ||
      template?.scheduleTitle ||
      fallback.title ||
      "",
  ).trim();
  const templateLatitude = toNullableCoordinate(
    template?.latitude ??
      template?.lat ??
      template?.locationLatitude ??
      fallback.latitude,
  );
  const templateLongitude = toNullableCoordinate(
    template?.longitude ??
      template?.lng ??
      template?.lon ??
      template?.locationLongitude ??
      fallback.longitude,
  );
  return {
    id:
      template?.id != null
        ? String(template.id)
        : template?.templateId != null
          ? String(template.templateId)
          : "",
    name: normalizedName || "이름 없는 템플릿",
    weekdays: normalizeTemplateWeekdays(
      template?.weekdays ||
        template?.activityWeekdays ||
        template?.activityDays,
      fallback.weekdays,
    ),
    category: normalizeScheduleCategory(rawCategory),
    title: normalizedTitle,
    venueName: String(
      template?.venueName || template?.stadiumName || fallback.venueName || "",
    ).trim(),
    address: String(template?.address || fallback.address || "").trim(),
    detailAddress: String(
      template?.detailAddress ||
        template?.addressDetail ||
        template?.addressLine2 ||
        fallback.detailAddress ||
        "",
    ).trim(),
    latitude: templateLatitude === null ? "" : String(templateLatitude),
    longitude: templateLongitude === null ? "" : String(templateLongitude),
    startTime: String(template?.startTime || fallback.startTime),
    endTime: String(template?.endTime || fallback.endTime),
    description:
      typeof template?.description === "string"
        ? template.description
        : fallback.description,
  };
}

function buildDefaultTemplateFormState(defaultTemplate) {
  return {
    ...defaultTemplate,
    id: "",
    name: "",
    weekdays: [...(defaultTemplate.weekdays || [])],
    description: defaultTemplate.description || "",
  };
}

function validateTemplateForm(templateForm) {
  if (!String(templateForm.name || "").trim()) {
    return "템플릿 이름을 입력해 주세요.";
  }

  if (String(templateForm.name).trim().length > TEMPLATE_NAME_MAX_LENGTH) {
    return `템플릿 이름은 ${TEMPLATE_NAME_MAX_LENGTH}자 이내로 입력해 주세요.`;
  }

  if (
    !Array.isArray(templateForm.weekdays) ||
    templateForm.weekdays.length === 0
  ) {
    return "최소 1개의 활동 요일을 선택해 주세요.";
  }

  if (!templateForm.startTime || !templateForm.endTime) {
    return "시작 시간과 종료 시간을 입력해 주세요.";
  }

  if (String(templateForm.address || "").trim()) {
    const latitude = toNullableCoordinate(templateForm.latitude);
    const longitude = toNullableCoordinate(templateForm.longitude);
    if (latitude === null || longitude === null) {
      return "템플릿 주소를 선택했다면 좌표도 함께 확인해 주세요.";
    }
  }

  return "";
}

function getAutoTemplateName(templateCount) {
  return `템플릿 ${templateCount + 1}`;
}

function toTemplatePayload(templateForm, teamId) {
  const normalizedName = String(templateForm.name || "").trim();
  const normalizedDescription = String(templateForm.description || "").trim();
  const normalizedVenueName = String(templateForm.venueName || "").trim();
  const normalizedAddress = String(templateForm.address || "").trim();
  const normalizedDetailAddress = String(
    templateForm.detailAddress || "",
  ).trim();
  const normalizedTitle = String(templateForm.title || "").trim();
  const normalizedWeekdays = normalizeTemplateWeekdays(templateForm.weekdays);
  const latitude = toNullableCoordinate(templateForm.latitude);
  const longitude = toNullableCoordinate(templateForm.longitude);

  return {
    name: normalizedName,
    templateName: normalizedName,
    title: normalizedTitle || null,
    defaultTitle: normalizedTitle || null,
    category: templateForm.category,
    type: templateForm.category,
    weekdays: normalizedWeekdays,
    activityWeekdays: normalizedWeekdays,
    activityDays: normalizedWeekdays
      .map((weekday) => WEEKDAY_LABEL_TO_ENUM[weekday])
      .filter(Boolean),
    startTime: templateForm.startTime,
    endTime: templateForm.endTime,
    venueName: normalizedVenueName || null,
    stadiumName: normalizedVenueName || null,
    address: normalizedAddress || null,
    detailAddress: normalizedDetailAddress || null,
    addressDetail: normalizedDetailAddress || null,
    latitude,
    longitude,
    lat: latitude,
    lng: longitude,
    locationLatitude: latitude,
    locationLongitude: longitude,
    description: normalizedDescription || null,
    memo: normalizedDescription || null,
    ...(teamId ? { teamId: String(teamId) } : {}),
  };
}

function extractTemplateItems(responseData) {
  if (Array.isArray(responseData)) {
    return responseData;
  }
  if (Array.isArray(responseData?.content)) {
    return responseData.content;
  }
  if (Array.isArray(responseData?.items)) {
    return responseData.items;
  }
  if (Array.isArray(responseData?.templates)) {
    return responseData.templates;
  }
  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }
  return [];
}

function normalizeTeamMemberRole(rawRole) {
  const normalizedRole = String(rawRole || "")
    .trim()
    .toUpperCase();
  if (normalizedRole === "CHAIR") {
    return "CHAIR";
  }
  if (normalizedRole === "STAFF") {
    return "STAFF";
  }
  return "MEMBER";
}

function normalizeTeamMemberItem(member) {
  return {
    userId: toUserKey(member?.userId ?? member?.id ?? member?.memberUserId),
    nickname: member?.nickname || member?.name || "회원",
    role: normalizeTeamMemberRole(
      member?.role ?? member?.memberRole ?? member?.myRole,
    ),
    status: String(member?.status || "APPROVED").toUpperCase(),
  };
}

function extractTeamMemberItems(responseData) {
  if (Array.isArray(responseData)) {
    return responseData;
  }
  if (Array.isArray(responseData?.content)) {
    return responseData.content;
  }
  if (Array.isArray(responseData?.items)) {
    return responseData.items;
  }
  if (Array.isArray(responseData?.members)) {
    return responseData.members;
  }
  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }
  return [];
}

function normalizeJoinRequestStatus(rawStatus) {
  const normalizedStatus = String(rawStatus || "")
    .trim()
    .toUpperCase();

  if (normalizedStatus === "APPROVED") {
    return "APPROVED";
  }
  if (normalizedStatus === "REJECTED") {
    return "REJECTED";
  }
  return "PENDING";
}

function getJoinRequestStatusLabel(status) {
  if (status === "APPROVED") {
    return "승인";
  }
  if (status === "REJECTED") {
    return "반려";
  }
  return "대기";
}

function getJoinRequestStatusBadgeClass(status) {
  if (status === "APPROVED") {
    return "bg-[#ecfdf3] text-[#067647]";
  }
  if (status === "REJECTED") {
    return "bg-[#fef3f2] text-[#b42318]";
  }
  return "bg-[#fffaeb] text-[#b54708]";
}

// 백엔드 join-request 응답 스펙을 화면 공통 모델로 정규화한다.
function normalizeJoinRequestItem(joinRequest) {
  const requestId = joinRequest?.id ?? joinRequest?.joinRequestId;
  return {
    id: requestId == null ? "" : String(requestId),
    requesterUserId: toUserKey(
      joinRequest?.requesterUserId ?? joinRequest?.userId,
    ),
    requesterNickname:
      joinRequest?.requesterNickname || joinRequest?.nickname || "가입 신청자",
    message:
      typeof joinRequest?.message === "string" ? joinRequest.message : "",
    status: normalizeJoinRequestStatus(joinRequest?.status),
    createdAt: joinRequest?.createdAt || joinRequest?.requestedAt || null,
    reviewedByNickname: joinRequest?.reviewedByNickname || null,
    reviewedAt: joinRequest?.reviewedAt || null,
  };
}

function extractJoinRequestItems(responseData) {
  if (Array.isArray(responseData)) {
    return responseData;
  }
  if (Array.isArray(responseData?.content)) {
    return responseData.content;
  }
  if (Array.isArray(responseData?.items)) {
    return responseData.items;
  }
  if (Array.isArray(responseData?.joinRequests)) {
    return responseData.joinRequests;
  }
  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }
  return [];
}

function getTeamMemberRoleLabel(role) {
  if (role === "CHAIR") {
    return "회장";
  }
  if (role === "STAFF") {
    return "운영진";
  }
  return "회원";
}

function sortTeamMembersByRoleAndName(members) {
  return [...members].sort((a, b) => {
    const roleDiff =
      (TEAM_MEMBER_ROLE_ORDER[a.role] ?? TEAM_MEMBER_ROLE_ORDER.MEMBER) -
      (TEAM_MEMBER_ROLE_ORDER[b.role] ?? TEAM_MEMBER_ROLE_ORDER.MEMBER);
    if (roleDiff !== 0) {
      return roleDiff;
    }
    return String(a.nickname).localeCompare(String(b.nickname), "ko");
  });
}

// 다음 등장하는 특정 요일/시간 기준 datetime을 계산한다.
function getNextWeekdayDateTime(weekdayLabel, timeText, baseDate = new Date()) {
  const weekdayIndex = WEEKDAY_TO_INDEX[weekdayLabel];
  const { hours, minutes } = parseTimeText(timeText);
  const start = new Date(baseDate);
  const resultDate = new Date(start);
  const dayDiff = (weekdayIndex - start.getDay() + 7) % 7;
  resultDate.setDate(start.getDate() + dayDiff);
  resultDate.setHours(hours, minutes, 0, 0);

  if (resultDate <= start) {
    resultDate.setDate(resultDate.getDate() + 7);
  }

  return resultDate;
}

// 여러 요일 중 가장 먼저 다가오는 요일을 계산한다.
function getNearestWeekdayLabel(
  weekdayLabels,
  timeText,
  baseDate = new Date(),
) {
  const safeWeekdays = getSortedWeekdays(weekdayLabels);
  if (safeWeekdays.length === 0) {
    return null;
  }

  return (
    safeWeekdays
      .map((weekdayLabel) => ({
        weekdayLabel,
        nextDateTime: getNextWeekdayDateTime(weekdayLabel, timeText, baseDate),
      }))
      .sort((a, b) => a.nextDateTime - b.nextDateTime)[0]?.weekdayLabel || null
  );
}

function formatDateTime(dateValue) {
  return new Intl.DateTimeFormat("ko-KR", {
    month: "short",
    day: "numeric",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(dateValue));
}

function formatDateOnly(dateValue) {
  return new Intl.DateTimeFormat("ko-KR", {
    month: "long",
    day: "numeric",
    weekday: "short",
  }).format(new Date(dateValue));
}

function formatMonthLabel(dateValue) {
  return new Intl.DateTimeFormat("ko-KR", {
    year: "numeric",
    month: "long",
  }).format(dateValue);
}

function parseDateValue(dateValue) {
  if (!dateValue) {
    return null;
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }
  return parsedDate;
}

function getNextDayStartDate(baseDate = new Date()) {
  const nextDayStartDate = new Date(baseDate);
  nextDayStartDate.setHours(24, 0, 0, 0);
  return nextDayStartDate;
}

function toIsoStringOrEmpty(dateValue) {
  const parsedDate = parseDateValue(dateValue);
  return parsedDate ? parsedDate.toISOString() : "";
}

function getErrorText(error) {
  return String(
    error?.message ||
      error?.data?.message ||
      error?.data?.resultMessage ||
      error?.data?.error?.message ||
      "",
  );
}

function getCurrentDevicePosition() {
  return new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      reject(new Error("이 기기에서는 위치 정보를 사용할 수 없습니다."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        if (error?.code === error.PERMISSION_DENIED) {
          reject(new Error("위치 권한이 필요합니다. 설정에서 위치 권한을 허용해 주세요."));
          return;
        }
        reject(new Error("현재 위치를 가져오지 못했습니다. 잠시 후 다시 시도해 주세요."));
      },
      {
        enableHighAccuracy: true,
        timeout: 12000,
        maximumAge: 5000,
      },
    );
  });
}

function isDailyReminderLimitError(error) {
  const errorText = getErrorText(error);
  return (
    errorText.includes("하루 1회") ||
    errorText.toLowerCase().includes("once per day")
  );
}

function getAttendanceFriendlyErrorMessage(error) {
  const errorText = getErrorText(error);
  if (errorText.includes("이미 출석 체크")) {
    return "이미 출석 체크가 완료되었습니다.";
  }
  if (errorText.includes("반경")) {
    return errorText;
  }
  if (errorText.includes("위치 정보")) {
    return "일정 위치 정보가 없어 출석 체크를 진행할 수 없습니다.";
  }
  return errorText || "출석 체크에 실패했습니다.";
}

function extractReminderNextAvailableAtFromError(error) {
  const fromErrorData = toIsoStringOrEmpty(
    error?.data?.nextAvailableAt || error?.data?.data?.nextAvailableAt,
  );
  if (fromErrorData) {
    return fromErrorData;
  }

  const errorMessage = getErrorText(error);
  const nextAvailableMatch = errorMessage.match(
    /다음 가능 시각:\s*([0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9:.\-+Z]+)/,
  );
  if (nextAvailableMatch?.[1]) {
    return toIsoStringOrEmpty(nextAvailableMatch[1]);
  }

  return "";
}

function getVoteReminderFriendlyErrorMessage(error) {
  const errorMessage = getErrorText(error);
  if (isDailyReminderLimitError(error)) {
    return "투표 독촉은 날짜 기준 하루 1회만 보낼 수 있습니다.";
  }
  if (
    String(error?.status || "") === "403" ||
    errorMessage.includes("권한") ||
    errorMessage.includes("forbidden")
  ) {
    return "회장/운영진만 투표 독촉을 보낼 수 있습니다.";
  }
  return errorMessage || "투표 독촉 전송에 실패했습니다.";
}

function isVoteOpen(schedule) {
  const now = new Date();
  const voteEndDate = schedule?.voteEndAt ? new Date(schedule.voteEndAt) : null;
  if (!voteEndDate || Number.isNaN(voteEndDate.getTime())) {
    return false;
  }

  const voteStartDate = schedule?.voteStartAt
    ? new Date(schedule.voteStartAt)
    : null;
  if (!voteStartDate || Number.isNaN(voteStartDate.getTime())) {
    return now <= voteEndDate;
  }

  return voteStartDate <= now && now <= voteEndDate;
}

// 백엔드/프론트 어디서 오든 사용자 식별값을 string key로 통일한다.
function toUserKey(userId) {
  if (userId === null || userId === undefined) {
    return "";
  }
  return String(userId);
}

// 인증 응답 스펙 차이를 고려해 현재 로그인 사용자 키를 도출한다.
function getCurrentUserKey(currentUser) {
  return (
    toUserKey(currentUser?.userId) ||
    toUserKey(currentUser?.id) ||
    toUserKey(currentUser?.providerId)
  );
}

function getMemberDisplayName(
  userId,
  currentUser,
  currentUserId,
  memberNameByUserId,
) {
  const userKey = toUserKey(userId);
  if (userKey === currentUserId) {
    return currentUser?.nickname || "나";
  }

  const nameFromMembers = memberNameByUserId?.get(userKey);
  if (nameFromMembers) {
    return nameFromMembers;
  }

  return userKey || "알 수 없음";
}

function getAttendanceVotePriority(voteType) {
  if (voteType === "ATTEND") {
    return 0;
  }
  if (!voteType) {
    return 1;
  }
  return 2;
}

function getAttendanceVoteLabel(voteType) {
  if (voteType === "ATTEND") {
    return "참여 예정";
  }
  if (voteType === "ABSENT") {
    return "불참 응답";
  }
  return "투표 미응답";
}

function getAttendanceVoteBadgeClass(voteType) {
  if (voteType === "ATTEND") {
    return "bg-[#ecfdf3] text-[#027a48]";
  }
  if (voteType === "ABSENT") {
    return "bg-[#fef3f2] text-[#b42318]";
  }
  return "bg-[#f2f4f7] text-[#475467]";
}

function toSchedulePayload(formState, teamId) {
  const normalizedTitle = formState.title.trim();
  const normalizedDescription = formState.description.trim();
  const memoValue = normalizedDescription || null;
  const normalizedDetailAddress = String(formState.detailAddress || "").trim();
  const latitude = toNullableCoordinate(formState.latitude);
  const longitude = toNullableCoordinate(formState.longitude);

  return {
    // 신규/구버전 스펙 모두 수용: canonical + alias를 함께 전달.
    title: normalizedTitle,
    name: normalizedTitle,
    type: formState.category,
    category: formState.category,
    stadiumName: formState.venueName.trim(),
    venueName: formState.venueName.trim(),
    address: formState.address.trim(),
    detailAddress: normalizedDetailAddress || null,
    addressDetail: normalizedDetailAddress || null,
    latitude,
    longitude,
    lat: latitude,
    lng: longitude,
    locationLatitude: latitude,
    locationLongitude: longitude,
    startAt: formState.startsAt,
    startsAt: formState.startsAt,
    endAt: formState.endsAt,
    endsAt: formState.endsAt,
    voteEndAt: formState.voteEndAt,
    memo: memoValue,
    description: memoValue,
    ...(teamId ? { teamId: String(teamId) } : {}),
  };
}

function toScheduleFormState(schedule) {
  const latitude = toNullableCoordinate(
    schedule?.latitude ?? schedule?.lat ?? schedule?.locationLatitude,
  );
  const longitude = toNullableCoordinate(
    schedule?.longitude ??
      schedule?.lng ??
      schedule?.lon ??
      schedule?.locationLongitude,
  );

  return {
    title: schedule?.title || schedule?.name || schedule?.scheduleTitle || "",
    category: schedule?.category || "TRAINING",
    venueName: schedule?.venueName || "",
    address: schedule?.address || "",
    detailAddress:
      schedule?.detailAddress ||
      schedule?.addressDetail ||
      schedule?.addressLine2 ||
      "",
    latitude: latitude === null ? "" : String(latitude),
    longitude: longitude === null ? "" : String(longitude),
    startsAt: schedule?.startsAt ? formatDateTimeLocal(schedule.startsAt) : "",
    endsAt: schedule?.endsAt ? formatDateTimeLocal(schedule.endsAt) : "",
    voteStartAt: schedule?.voteStartAt
      ? formatDateTimeLocal(schedule.voteStartAt)
      : "",
    voteEndAt: schedule?.voteEndAt
      ? formatDateTimeLocal(schedule.voteEndAt)
      : "",
    description: schedule?.description || schedule?.memo || "",
  };
}

function validateScheduleForm(formState) {
  const hasEmptyField = REQUIRED_SCHEDULE_FIELDS.some(
    (field) => !formState[field],
  );
  if (hasEmptyField) {
    return "필수 항목을 모두 입력해 주세요.";
  }

  if (new Date(formState.startsAt) >= new Date(formState.endsAt)) {
    return "일정 종료 시간은 시작 시간보다 늦어야 합니다.";
  }

  const voteEndDate = new Date(formState.voteEndAt);
  const voteStartDate = String(formState.voteStartAt || "").trim()
    ? new Date(formState.voteStartAt)
    : new Date();
  if (voteStartDate >= voteEndDate) {
    return String(formState.voteStartAt || "").trim()
      ? "투표 종료 시간은 투표 시작 시간보다 늦어야 합니다."
      : "투표 종료 시간은 현재 시각보다 늦어야 합니다.";
  }

  const latitude = toNullableCoordinate(formState.latitude);
  const longitude = toNullableCoordinate(formState.longitude);
  if (latitude === null || longitude === null) {
    return "주소 검색으로 좌표를 확인해 주세요.";
  }

  return "";
}

// 페이지네이션 응답 래핑 형태(content/items/data)를 흡수한다.
function extractUpcomingItems(responseData) {
  if (Array.isArray(responseData)) {
    return responseData;
  }
  if (Array.isArray(responseData?.content)) {
    return responseData.content;
  }
  if (Array.isArray(responseData?.items)) {
    return responseData.items;
  }
  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }
  return [];
}

function extractAttendanceItems(responseData) {
  if (Array.isArray(responseData)) {
    return responseData;
  }
  if (Array.isArray(responseData?.content)) {
    return responseData.content;
  }
  if (Array.isArray(responseData?.items)) {
    return responseData.items;
  }
  if (Array.isArray(responseData?.attendances)) {
    return responseData.attendances;
  }
  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }
  return [];
}

// 백엔드 응답 포맷별로 다음 페이지 존재 여부를 계산한다.
function extractHasNextPage(responseData, itemCount) {
  if (typeof responseData?.hasNext === "boolean") {
    return responseData.hasNext;
  }
  if (typeof responseData?.last === "boolean") {
    return !responseData.last;
  }
  if (
    typeof responseData?.totalPages === "number" &&
    typeof responseData?.number === "number"
  ) {
    return responseData.number + 1 < responseData.totalPages;
  }
  return itemCount >= UPCOMING_PAGE_SIZE;
}

// 월간 캘린더(6주 x 7일) 렌더링용 날짜 셀 배열을 만든다.
function buildCalendarCells(monthDate) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const firstWeekday = firstDay.getDay();
  const startDate = new Date(year, month, 1 - firstWeekday);

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + index);
    return date;
  });
}

function clampPitchPercent(value) {
  return Math.max(6, Math.min(94, value));
}

function isCustomLineupPlayerId(playerId) {
  return String(playerId || "").startsWith(CUSTOM_LINEUP_PLAYER_ID_PREFIX);
}

function getNextLineupAutoPosition(players) {
  const normalizedPlayers = Array.isArray(players)
    ? players.map((player, index) =>
        normalizeLineupPlayer(player, `auto-player-${index + 1}`),
      )
    : [];
  const activePlayers = normalizedPlayers.filter((player) => player.active);

  for (const y of LINEUP_AUTO_PLACE_Y_COORDINATES) {
    for (const x of LINEUP_AUTO_PLACE_X_COORDINATES) {
      const isOccupied = activePlayers.some(
        (player) =>
          Math.abs(player.x - x) < LINEUP_AUTO_PLACE_MIN_GAP &&
          Math.abs(player.y - y) < LINEUP_AUTO_PLACE_MIN_GAP,
      );
      if (!isOccupied) {
        return { x, y };
      }
    }
  }

  const fallbackIndex = activePlayers.length;
  return {
    x: clampPitchPercent(14 + (fallbackIndex % 6) * 14),
    y: clampPitchPercent(18 + Math.floor(fallbackIndex / 6) * 10),
  };
}

function createCustomLineupPlayer(name, initialPosition = { x: 50, y: 50 }) {
  const safeName = String(name || "").trim() || "용병";
  return {
    id: `${CUSTOM_LINEUP_PLAYER_ID_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: safeName,
    x: clampPitchPercent(Number(initialPosition?.x ?? 50)),
    y: clampPitchPercent(Number(initialPosition?.y ?? 50)),
    active: false,
  };
}

function normalizeLineupPlayer(player, fallbackId) {
  return {
    id: toUserKey(player?.id || fallbackId),
    name: String(player?.name || "선수").trim() || "선수",
    x: clampPitchPercent(Number(player?.x ?? 50)),
    y: clampPitchPercent(Number(player?.y ?? 50)),
    active: Boolean(player?.active),
  };
}

function createLineupPlayersFromPool(playerPool) {
  if (!Array.isArray(playerPool) || playerPool.length === 0) {
    return [];
  }

  const uniquePlayers = [];
  const seenPlayerIds = new Set();

  playerPool.forEach((player, index) => {
    const isTextItem = typeof player === "string";
    const fallbackId = `player-${index + 1}`;
    const playerId = toUserKey(
      isTextItem ? fallbackId : player?.id || fallbackId,
    );
    if (!playerId || seenPlayerIds.has(playerId)) {
      return;
    }

    seenPlayerIds.add(playerId);
    uniquePlayers.push({
      id: playerId,
      name:
        String(isTextItem ? player : player?.name || "선수").trim() || "선수",
    });
  });

  return uniquePlayers.map((player, index) => {
    const defaultCoordinate = DEFAULT_LINEUP_COORDINATES[index];
    const fallbackRow = Math.floor(index / 6);
    const fallbackColumn = index % 6;

    return {
      id: player.id,
      name: player.name,
      x: clampPitchPercent(defaultCoordinate?.x ?? 12 + fallbackColumn * 14),
      y: clampPitchPercent(defaultCoordinate?.y ?? 24 + fallbackRow * 10),
      active:
        index < Math.min(DEFAULT_ACTIVE_PLAYER_COUNT, uniquePlayers.length),
    };
  });
}

// 편집기 초기 라인업(활성 인원 + 기본 좌표)을 생성한다.
function createDefaultLineupPlayers() {
  return [];
}

// 저장된 라인업이 있어도 기본 선수 풀 기준으로 누락 데이터를 보완한다.
function mergeLineupPlayers(savedPlayers, basePlayers) {
  const hasBasePlayers = Array.isArray(basePlayers) && basePlayers.length > 0;
  const normalizedBasePlayers = hasBasePlayers
    ? basePlayers.map((player, index) =>
        normalizeLineupPlayer(player, `base-player-${index + 1}`),
      )
    : [];

  if (!Array.isArray(savedPlayers)) {
    return hasBasePlayers ? normalizedBasePlayers : [];
  }

  const normalizedSavedPlayers = savedPlayers.map((player, index) =>
    normalizeLineupPlayer(player, `saved-player-${index + 1}`),
  );

  if (!hasBasePlayers) {
    return normalizedSavedPlayers;
  }

  const savedById = new Map(
    normalizedSavedPlayers.map((player) => [player.id, player]),
  );
  const savedByName = new Map(
    normalizedSavedPlayers.map((player) => [player.name, player]),
  );
  const basePlayerIds = new Set(
    normalizedBasePlayers.map((player) => player.id),
  );

  const mergedBasePlayers = normalizedBasePlayers.map((basePlayer) => {
    const savedByPlayerId = savedById.get(basePlayer.id);
    if (savedByPlayerId) {
      return {
        ...basePlayer,
        ...savedByPlayerId,
        id: basePlayer.id,
        name: basePlayer.name,
      };
    }

    const savedByPlayerName = savedByName.get(basePlayer.name);
    if (savedByPlayerName) {
      return {
        ...basePlayer,
        ...savedByPlayerName,
        id: basePlayer.id,
        name: basePlayer.name,
      };
    }

    return basePlayer;
  });

  const customPlayers = normalizedSavedPlayers.filter(
    (player) =>
      !basePlayerIds.has(player.id) && isCustomLineupPlayerId(player.id),
  );
  return [...mergedBasePlayers, ...customPlayers];
}

function collectCustomLineupPlayersFromSegments(segments, segmentCount) {
  const templates = [];
  const seenPlayerIds = new Set();

  for (let segmentIndex = 1; segmentIndex <= segmentCount; segmentIndex += 1) {
    const segmentKey = String(segmentIndex);
    const players = Array.isArray(segments?.[segmentKey])
      ? segments[segmentKey]
      : [];
    players.forEach((player, playerIndex) => {
      const normalizedPlayer = normalizeLineupPlayer(
        player,
        `${segmentKey}-player-${playerIndex + 1}`,
      );
      if (
        !isCustomLineupPlayerId(normalizedPlayer.id) ||
        seenPlayerIds.has(normalizedPlayer.id)
      ) {
        return;
      }
      seenPlayerIds.add(normalizedPlayer.id);
      templates.push({
        ...normalizedPlayer,
        active: false,
      });
    });
  }

  return templates;
}

function appendCustomPlayersToSegments(segments, segmentCount, customPlayers) {
  if (!Array.isArray(customPlayers) || customPlayers.length === 0) {
    return segments;
  }

  const nextSegments = {};
  for (let segmentIndex = 1; segmentIndex <= segmentCount; segmentIndex += 1) {
    const segmentKey = String(segmentIndex);
    const basePlayers = Array.isArray(segments[segmentKey])
      ? segments[segmentKey].map((player, playerIndex) =>
          normalizeLineupPlayer(
            player,
            `${segmentKey}-base-${playerIndex + 1}`,
          ),
        )
      : [];

    const basePlayerIds = new Set(basePlayers.map((player) => player.id));
    const missingCustomPlayers = customPlayers
      .filter((player) => !basePlayerIds.has(player.id))
      .map((player) => ({
        ...player,
        active: false,
      }));

    nextSegments[segmentKey] = [...basePlayers, ...missingCustomPlayers];
  }

  return nextSegments;
}

function normalizeSegmentCount(value) {
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_LINEUP_SEGMENT_COUNT;
  }

  return Math.max(
    MIN_LINEUP_SEGMENT_COUNT,
    Math.min(MAX_LINEUP_SEGMENT_COUNT, parsed),
  );
}

function createLineupSegments(
  segmentCount,
  sourceSegments = {},
  basePlayers = createDefaultLineupPlayers(),
) {
  const safeSegmentCount = normalizeSegmentCount(segmentCount);
  const segments = {};

  for (
    let segmentIndex = 1;
    segmentIndex <= safeSegmentCount;
    segmentIndex += 1
  ) {
    const segmentKey = String(segmentIndex);
    segments[segmentKey] = mergeLineupPlayers(
      sourceSegments[segmentKey],
      basePlayers,
    );
  }

  return segments;
}

function cloneLineupSegments(segments) {
  return Object.fromEntries(
    Object.entries(segments).map(([segmentKey, players]) => [
      segmentKey,
      players.map((player) => ({ ...player })),
    ]),
  );
}

function normalizeLineupResponseItem(lineupResponse) {
  const segmentCount = Number(lineupResponse?.segmentCount || 0);
  const segments = lineupResponse?.segments;

  if (!segmentCount || !segments || typeof segments !== "object") {
    return null;
  }

  const normalizedSegments = {};
  Object.entries(segments).forEach(([segmentKey, players]) => {
    normalizedSegments[String(segmentKey)] = Array.isArray(players)
      ? players.map((player, index) => ({
          id: toUserKey(
            player?.playerId || player?.id || `${segmentKey}-${index + 1}`,
          ),
          name: player?.name || "선수",
          x: clampPitchPercent(Number(player?.x ?? 50)),
          y: clampPitchPercent(Number(player?.y ?? 50)),
          active: Boolean(player?.active),
        }))
      : [];
  });

  return {
    segmentCount: normalizeSegmentCount(segmentCount),
    segments: normalizedSegments,
  };
}

function TeamDetailPage({
  currentUser,
  team,
  myTeams = [],
  onSelectTeam,
  onRefreshTeams,
}) {
  // 페이지 상태: 팀 프로필/일정/템플릿/투표/라인업 편집까지 한 화면에서 관리한다.
  const [activeTab, setActiveTab] = useState("upcoming");
  const currentUserId = useMemo(
    () => getCurrentUserKey(currentUser),
    [currentUser],
  );
  const activeTeamProfile = useMemo(() => {
    const normalizedWeekdays = normalizeTemplateWeekdays(
      team?.activityWeekdays || team?.activityDays,
    );
    return {
      ...DEFAULT_TEAM_PROFILE,
      id: toUserKey(team?.id),
      name: team?.name || DEFAULT_TEAM_PROFILE.name,
      chair:
        team?.chairNickname ||
        team?.chairName ||
        team?.chair ||
        DEFAULT_TEAM_PROFILE.chair,
      logoUrl: team?.logoUrl || DEFAULT_TEAM_PROFILE.logoUrl,
      updatedAt: team?.updatedAt || DEFAULT_TEAM_PROFILE.updatedAt,
      activityWeekdays:
        normalizedWeekdays.length > 0
          ? normalizedWeekdays
          : DEFAULT_TEAM_PROFILE.activityWeekdays,
      timeRange:
        team?.timeRange ||
        team?.preferredTimeSlot ||
        DEFAULT_TEAM_PROFILE.timeRange,
      ageBand: team?.ageBand || DEFAULT_TEAM_PROFILE.ageBand,
      genderType: team?.genderType || DEFAULT_TEAM_PROFILE.genderType,
      role: team?.role || DEFAULT_TEAM_PROFILE.role,
    };
  }, [team]);
  const teamRoleStorageKey = useMemo(
    () => `teamRole:${activeTeamProfile.id}`,
    [activeTeamProfile.id],
  );
  const defaultRecurringTemplate = useMemo(
    () => getDefaultRecurringTemplate(activeTeamProfile),
    [activeTeamProfile],
  );
  const [role, setRole] = useState(() => activeTeamProfile.role || "MEMBER");
  const [teamManageForm, setTeamManageForm] = useState(() =>
    buildTeamManageFormState(activeTeamProfile, team),
  );
  const [teamLogoFile, setTeamLogoFile] = useState(null);
  const [teamLogoPreviewUrl, setTeamLogoPreviewUrl] = useState("");
  const [teamLogoCacheKey, setTeamLogoCacheKey] = useState(() =>
    String(Date.now()),
  );
  const [teamManageError, setTeamManageError] = useState("");
  const [teamManageMessage, setTeamManageMessage] = useState("");
  const [isSavingTeamProfile, setIsSavingTeamProfile] = useState(false);
  const [isTeamLogoUploading, setIsTeamLogoUploading] = useState(false);
  const [schedules, setSchedules] = useState(INITIAL_SCHEDULES);
  const [joinRequests, setJoinRequests] = useState(INITIAL_JOIN_REQUESTS);
  const [isJoinRequestLoading, setIsJoinRequestLoading] = useState(false);
  const [joinRequestLoadError, setJoinRequestLoadError] = useState("");
  const [joinRequestActionError, setJoinRequestActionError] = useState("");
  const [joinRequestActionMessage, setJoinRequestActionMessage] = useState("");
  const [processingJoinRequestIds, setProcessingJoinRequestIds] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [recurringTemplates, setRecurringTemplates] = useState([]);
  const [selectedRecurringTemplateId, setSelectedRecurringTemplateId] =
    useState("");
  const selectedRecurringTemplateIdRef = useRef("");
  const lineupPitchRef = useRef(null);
  const teamLogoFileInputRef = useRef(null);
  const lineupTouchDraggingPlayerIdRef = useRef("");
  const lineupTouchLockedBodyOverflowRef = useRef("");
  const [recurringTemplate, setRecurringTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState(() =>
    buildDefaultTemplateFormState(defaultRecurringTemplate),
  );
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState("");
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [templateLoadError, setTemplateLoadError] = useState("");
  const [templateFormError, setTemplateFormError] = useState("");
  const [templateFormMessage, setTemplateFormMessage] = useState("");
  const [isTemplateAddressResolving, setIsTemplateAddressResolving] =
    useState(false);
  const [scheduleForm, setScheduleForm] = useState(INITIAL_FORM_STATE);
  const [formError, setFormError] = useState("");
  const [isScheduleAddressResolving, setIsScheduleAddressResolving] =
    useState(false);
  const [isAddressSearchLoading, setIsAddressSearchLoading] = useState(false);
  const [isCreatingSchedule, setIsCreatingSchedule] = useState(false);
  const [isManageCreateModalOpen, setIsManageCreateModalOpen] = useState(false);
  const [manageFilter, setManageFilter] = useState("UPCOMING");
  const [manageSchedules, setManageSchedules] = useState([]);
  const [isManageLoading, setIsManageLoading] = useState(false);
  const [manageLoadError, setManageLoadError] = useState("");
  const [editingScheduleId, setEditingScheduleId] = useState(null);
  const [manageForm, setManageForm] = useState(INITIAL_FORM_STATE);
  const [manageFormError, setManageFormError] = useState("");
  const [manageFormMessage, setManageFormMessage] = useState("");
  const [isManageAddressResolving, setIsManageAddressResolving] =
    useState(false);
  const [isUpdatingSchedule, setIsUpdatingSchedule] = useState(false);
  const [deletingScheduleIds, setDeletingScheduleIds] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isMemberLoading, setIsMemberLoading] = useState(false);
  const [memberLoadError, setMemberLoadError] = useState("");
  const [memberActionError, setMemberActionError] = useState("");
  const [memberActionMessage, setMemberActionMessage] = useState("");
  const [roleUpdatingMemberIds, setRoleUpdatingMemberIds] = useState([]);
  const [expellingMemberIds, setExpellingMemberIds] = useState([]);
  const [chairDelegatingMemberIds, setChairDelegatingMemberIds] = useState([]);
  const [upcomingPage, setUpcomingPage] = useState(0);
  const [hasMoreUpcomingSchedules, setHasMoreUpcomingSchedules] =
    useState(false);
  const [isUpcomingLoading, setIsUpcomingLoading] = useState(true);
  const [isLoadingMoreUpcoming, setIsLoadingMoreUpcoming] = useState(false);
  const [upcomingLoadError, setUpcomingLoadError] = useState("");
  const [savedLineups, setSavedLineups] = useState({});
  const [lineupPreviewSegments, setLineupPreviewSegments] = useState({});
  const [votingScheduleIds, setVotingScheduleIds] = useState([]);
  const [voteErrorMessage, setVoteErrorMessage] = useState("");
  const [attendanceCheckinsBySchedule, setAttendanceCheckinsBySchedule] =
    useState({});
  const [attendanceMessage, setAttendanceMessage] = useState("");
  const [attendanceErrorMessage, setAttendanceErrorMessage] = useState("");
  const [isAttendanceCheckingIn, setIsAttendanceCheckingIn] = useState(false);
  const [isAttendanceRefreshing, setIsAttendanceRefreshing] = useState(false);
  const [isVoteMemberListExpanded, setIsVoteMemberListExpanded] =
    useState(false);
  const [isAttendanceDetailExpanded, setIsAttendanceDetailExpanded] =
    useState(false);
  const [voteReminderMessage, setVoteReminderMessage] = useState("");
  const [voteReminderErrorMessage, setVoteReminderErrorMessage] = useState("");
  const [voteReminderPendingScheduleIds, setVoteReminderPendingScheduleIds] =
    useState([]);
  const [voteReminderCooldownBySchedule, setVoteReminderCooldownBySchedule] =
    useState({});
  const [voteReminderNowTick, setVoteReminderNowTick] = useState(() =>
    Date.now(),
  );
  const [lineupLoadStatusBySchedule, setLineupLoadStatusBySchedule] = useState(
    {},
  );
  const [lineupSavePending, setLineupSavePending] = useState(false);
  const [lineupErrorMessage, setLineupErrorMessage] = useState("");
  const [lineupCustomPlayerName, setLineupCustomPlayerName] = useState("");
  const [isClosingTeam, setIsClosingTeam] = useState(false);
  const [isCloseTeamModalOpen, setIsCloseTeamModalOpen] = useState(false);
  const [closeTeamConfirmName, setCloseTeamConfirmName] = useState("");
  const [closeTeamModalError, setCloseTeamModalError] = useState("");
  const [isLeavingTeam, setIsLeavingTeam] = useState(false);
  const [isLeaveTeamModalOpen, setIsLeaveTeamModalOpen] = useState(false);
  const [leaveTeamConfirmName, setLeaveTeamConfirmName] = useState("");
  const [leaveTeamModalError, setLeaveTeamModalError] = useState("");
  const [selectedScheduleDetailId, setSelectedScheduleDetailId] =
    useState(null);
  const [lineupDraft, setLineupDraft] = useState(() => ({
    scheduleId: null,
    segmentCount: DEFAULT_LINEUP_SEGMENT_COUNT,
    selectedSegment: "1",
    basePlayers: createDefaultLineupPlayers(),
    segments: createLineupSegments(DEFAULT_LINEUP_SEGMENT_COUNT),
  }));
  useAutoClearMessage(teamManageMessage, setTeamManageMessage);
  useAutoClearMessage(teamManageError, setTeamManageError);
  useAutoClearMessage(joinRequestActionMessage, setJoinRequestActionMessage);
  useAutoClearMessage(joinRequestActionError, setJoinRequestActionError);
  useAutoClearMessage(templateFormMessage, setTemplateFormMessage);
  useAutoClearMessage(templateFormError, setTemplateFormError);
  useAutoClearMessage(formError, setFormError);
  useAutoClearMessage(manageFormMessage, setManageFormMessage);
  useAutoClearMessage(manageFormError, setManageFormError);
  useAutoClearMessage(memberActionMessage, setMemberActionMessage);
  useAutoClearMessage(memberActionError, setMemberActionError);
  useAutoClearMessage(voteErrorMessage, setVoteErrorMessage);
  useAutoClearMessage(attendanceMessage, setAttendanceMessage, 3000);
  useAutoClearMessage(attendanceErrorMessage, setAttendanceErrorMessage, 3000);
  useAutoClearMessage(voteReminderMessage, setVoteReminderMessage, 5000);
  useAutoClearMessage(voteReminderErrorMessage, setVoteReminderErrorMessage, 5000);
  useAutoClearMessage(lineupErrorMessage, setLineupErrorMessage);
  useAutoClearMessage(closeTeamModalError, setCloseTeamModalError);
  useAutoClearMessage(leaveTeamModalError, setLeaveTeamModalError);

  const memberNameByUserId = useMemo(() => {
    const nameMap = new Map();
    teamMembers.forEach((member) => {
      const userId = toUserKey(member.userId);
      if (!userId) {
        return;
      }
      nameMap.set(userId, member.nickname || userId);
    });

    if (currentUserId && currentUser?.nickname) {
      nameMap.set(currentUserId, currentUser.nickname);
    }

    return nameMap;
  }, [teamMembers, currentUserId, currentUser]);

  const teamMemberIds = useMemo(() => {
    const memberIds = teamMembers
      .map((member) => toUserKey(member.userId))
      .filter(Boolean);
    if (currentUserId) {
      memberIds.push(currentUserId);
    }
    return Array.from(new Set(memberIds));
  }, [teamMembers, currentUserId]);

  const isStaff = STAFF_ROLES.has(role);
  const isChair = role === "CHAIR";
  const hasTeamManagePermission = isStaff;
  const hasScheduleManagePermission = isStaff;
  const hasJoinManagePermission = isStaff;
  const hasLineupManagePermission = isStaff;
  const hasMemberViewPermission = true;
  const hasMemberManagePermission = isStaff;
  const visibleTabItems = useMemo(() => {
    if (isStaff) {
      return TAB_ITEMS;
    }
    return TAB_ITEMS.filter((tab) => MEMBER_VISIBLE_TAB_KEYS.has(tab.key));
  }, [isStaff]);

  const sortedUpcomingSchedules = useMemo(() => {
    const now = new Date();
    return schedules
      .filter((schedule) => new Date(schedule.endsAt) >= now)
      .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
  }, [schedules]);

  const filteredManageSchedules = useMemo(() => {
    const now = new Date();
    const sortedSchedules = [...manageSchedules].sort(
      (a, b) => new Date(a.startsAt) - new Date(b.startsAt),
    );

    if (manageFilter === "PAST") {
      return sortedSchedules.filter(
        (schedule) => new Date(schedule.endsAt) < now,
      );
    }
    if (manageFilter === "UPCOMING") {
      return sortedSchedules.filter(
        (schedule) => new Date(schedule.endsAt) >= now,
      );
    }
    return sortedSchedules;
  }, [manageFilter, manageSchedules]);

  const scheduleCountByDate = useMemo(() => {
    return schedules.reduce((acc, schedule) => {
      const dateKey = toDateKey(schedule.startsAt);
      acc[dateKey] = (acc[dateKey] || 0) + 1;
      return acc;
    }, {});
  }, [schedules]);

  const selectedDateSchedules = useMemo(() => {
    if (!selectedDateKey) {
      return [];
    }
    return schedules
      .filter((schedule) => toDateKey(schedule.startsAt) === selectedDateKey)
      .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
  }, [schedules, selectedDateKey]);

  const calendarCells = useMemo(
    () => buildCalendarCells(calendarMonth),
    [calendarMonth],
  );
  const lineupDraftPlayers = useMemo(() => {
    return lineupDraft.segments[lineupDraft.selectedSegment] || [];
  }, [lineupDraft.segments, lineupDraft.selectedSegment]);

  const activeLineupCount = useMemo(() => {
    return lineupDraftPlayers.filter((player) => player.active).length;
  }, [lineupDraftPlayers]);

  const selectedScheduleDetail = useMemo(() => {
    if (!selectedScheduleDetailId) {
      return null;
    }
    return (
      schedules.find((schedule) => schedule.id === selectedScheduleDetailId) ||
      null
    );
  }, [schedules, selectedScheduleDetailId]);
  const isAnyModalOpen =
    isManageCreateModalOpen ||
    Boolean(editingScheduleId) ||
    isTemplateModalOpen ||
    isLeaveTeamModalOpen ||
    isCloseTeamModalOpen ||
    Boolean(selectedScheduleDetail);
  useBodyScrollLock(isAnyModalOpen);

  useEffect(() => {
    if (!selectedScheduleDetail) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setVoteReminderNowTick(Date.now());
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [selectedScheduleDetail]);

  const selectedScheduleVoteDetail = useMemo(() => {
    if (!selectedScheduleDetail) {
      return { attendees: [], absentees: [], nonResponders: [] };
    }

    const entries = Object.entries(selectedScheduleDetail.votes || {});
    const respondedUserIds = new Set(
      entries.map(([userId]) => toUserKey(userId)),
    );
    const attendees = entries
      .filter(([, vote]) => vote === "ATTEND")
      .map(([userId]) =>
        getMemberDisplayName(
          userId,
          currentUser,
          currentUserId,
          memberNameByUserId,
        ),
      );
    const absentees = entries
      .filter(([, vote]) => vote === "ABSENT")
      .map(([userId]) =>
        getMemberDisplayName(
          userId,
          currentUser,
          currentUserId,
          memberNameByUserId,
        ),
      );
    const nonResponders = teamMemberIds
      .filter((userId) => !respondedUserIds.has(userId))
      .map((userId) =>
        getMemberDisplayName(
          userId,
          currentUser,
          currentUserId,
          memberNameByUserId,
        ),
      );

    return { attendees, absentees, nonResponders };
  }, [
    selectedScheduleDetail,
    currentUser,
    currentUserId,
    memberNameByUserId,
    teamMemberIds,
  ]);

  const selectedScheduleAttendanceDetail = useMemo(() => {
    if (!selectedScheduleDetail) {
      return {
        scheduleKey: "",
        members: [],
        checkedInMembers: [],
        uncheckedMembers: [],
        myCheckedIn: false,
      };
    }

    const scheduleKey = String(selectedScheduleDetail.id);
    const votesByUserId = selectedScheduleDetail.votes || {};
    const scheduleAttendanceByUserId =
      selectedScheduleDetail.attendanceByUserId || {};
    const localAttendanceByUserId = attendanceCheckinsBySchedule[scheduleKey] || {};
    const checkedInByUserId = {
      ...scheduleAttendanceByUserId,
      ...localAttendanceByUserId,
    };
    const memberIds =
      teamMemberIds.length > 0
        ? teamMemberIds
        : Object.keys(votesByUserId).map((userId) => toUserKey(userId));
    const uniqueMemberIds = Array.from(new Set(memberIds)).filter(Boolean);
    Object.keys(checkedInByUserId).forEach((userId) => {
      const normalizedUserId = toUserKey(userId);
      if (!normalizedUserId || uniqueMemberIds.includes(normalizedUserId)) {
        return;
      }
      uniqueMemberIds.push(normalizedUserId);
    });

    const members = uniqueMemberIds
      .map((userId) => {
        const normalizedUserId = toUserKey(userId);
        const attendanceNickname =
          checkedInByUserId[normalizedUserId]?.nickname || "";
        return {
          userId: normalizedUserId,
          name:
            attendanceNickname ||
            getMemberDisplayName(
              normalizedUserId,
              currentUser,
              currentUserId,
              memberNameByUserId,
            ),
          voteType: votesByUserId[normalizedUserId] || null,
          checkedIn: Boolean(checkedInByUserId[normalizedUserId]),
          checkedInAt: checkedInByUserId[normalizedUserId]?.checkedInAt || null,
          distanceMeters:
            checkedInByUserId[normalizedUserId]?.distanceMeters ?? null,
        };
      })
      .sort((a, b) => {
        if (a.checkedIn !== b.checkedIn) {
          return a.checkedIn ? -1 : 1;
        }
        const votePriorityDiff =
          getAttendanceVotePriority(a.voteType) -
          getAttendanceVotePriority(b.voteType);
        if (votePriorityDiff !== 0) {
          return votePriorityDiff;
        }
        return String(a.name).localeCompare(String(b.name), "ko");
      });

    const checkedInMembers = members.filter((member) => member.checkedIn);
    const uncheckedMembers = members.filter((member) => !member.checkedIn);

    return {
      scheduleKey,
      members,
      checkedInMembers,
      uncheckedMembers,
      myCheckedIn: currentUserId
        ? Boolean(checkedInByUserId[currentUserId])
        : false,
    };
  }, [
    selectedScheduleDetail,
    attendanceCheckinsBySchedule,
    teamMemberIds,
    currentUser,
    currentUserId,
    memberNameByUserId,
  ]);

  const selectedScheduleReminderKey = useMemo(() => {
    if (!selectedScheduleDetail) {
      return "";
    }
    return String(selectedScheduleDetail.id);
  }, [selectedScheduleDetail]);

  const selectedScheduleReminderNextAvailableAt = useMemo(() => {
    if (!selectedScheduleReminderKey) {
      return "";
    }
    return voteReminderCooldownBySchedule[selectedScheduleReminderKey] || "";
  }, [selectedScheduleReminderKey, voteReminderCooldownBySchedule]);

  const isSelectedScheduleReminderCoolingDown = useMemo(() => {
    const nextAvailableDate = parseDateValue(
      selectedScheduleReminderNextAvailableAt,
    );
    if (!nextAvailableDate) {
      return false;
    }
    return nextAvailableDate.getTime() > voteReminderNowTick;
  }, [selectedScheduleReminderNextAvailableAt, voteReminderNowTick]);

  const selectedScheduleReminderIsPending = useMemo(() => {
    if (!selectedScheduleReminderKey) {
      return false;
    }
    return voteReminderPendingScheduleIds.includes(selectedScheduleReminderKey);
  }, [selectedScheduleReminderKey, voteReminderPendingScheduleIds]);

  const isSelectedScheduleVoteOpen = useMemo(() => {
    if (!selectedScheduleDetail) {
      return false;
    }
    return isVoteOpen(selectedScheduleDetail);
  }, [selectedScheduleDetail]);

  const selectedScheduleSavedLineup = useMemo(() => {
    if (!selectedScheduleDetail) {
      return null;
    }
    return savedLineups[selectedScheduleDetail.id] || null;
  }, [selectedScheduleDetail, savedLineups]);

  const selectedScheduleSavedLineupSegments = useMemo(() => {
    return (
      selectedScheduleSavedLineup?.segments ||
      (selectedScheduleSavedLineup?.players
        ? { 1: selectedScheduleSavedLineup.players }
        : {})
    );
  }, [selectedScheduleSavedLineup]);

  const selectedScheduleSavedSegmentCount = useMemo(() => {
    return normalizeSegmentCount(
      selectedScheduleSavedLineup?.segmentCount ??
        (selectedScheduleSavedLineup?.players
          ? 1
          : DEFAULT_LINEUP_SEGMENT_COUNT),
    );
  }, [selectedScheduleSavedLineup]);

  const selectedScheduleDetailPreviewSegmentKey = useMemo(() => {
    if (!selectedScheduleDetail) {
      return "1";
    }

    const raw = lineupPreviewSegments[selectedScheduleDetail.id] || "1";
    const parsed = Number.parseInt(raw, 10);
    return String(
      Math.min(
        selectedScheduleSavedSegmentCount,
        Math.max(1, Number.isFinite(parsed) ? parsed : 1),
      ),
    );
  }, [
    selectedScheduleDetail,
    lineupPreviewSegments,
    selectedScheduleSavedSegmentCount,
  ]);

  const selectedScheduleDetailLineupPlayers = useMemo(() => {
    if (!selectedScheduleSavedLineup) {
      return [];
    }

    return mergeLineupPlayers(
      selectedScheduleSavedLineupSegments[
        selectedScheduleDetailPreviewSegmentKey
      ],
    ).filter((player) => player.active);
  }, [
    selectedScheduleSavedLineup,
    selectedScheduleSavedLineupSegments,
    selectedScheduleDetailPreviewSegmentKey,
  ]);

  const isDetailLineupEditorOpen = useMemo(() => {
    if (!selectedScheduleDetail) {
      return false;
    }
    return lineupDraft.scheduleId === selectedScheduleDetail.id;
  }, [selectedScheduleDetail, lineupDraft.scheduleId]);

  const selectedScheduleLineupLoadStatus = useMemo(() => {
    if (!selectedScheduleDetail) {
      return { loading: false, error: "" };
    }
    return (
      lineupLoadStatusBySchedule[String(selectedScheduleDetail.id)] || {
        loading: false,
        error: "",
      }
    );
  }, [selectedScheduleDetail, lineupLoadStatusBySchedule]);

  const detailLineupPreviewPlayers = useMemo(() => {
    if (isDetailLineupEditorOpen) {
      return lineupDraftPlayers.filter((player) => player.active);
    }
    return selectedScheduleDetailLineupPlayers;
  }, [
    isDetailLineupEditorOpen,
    lineupDraftPlayers,
    selectedScheduleDetailLineupPlayers,
  ]);

  const editingManageSchedule = useMemo(() => {
    if (!editingScheduleId) {
      return null;
    }
    return (
      manageSchedules.find(
        (schedule) => String(schedule.id) === editingScheduleId,
      ) || null
    );
  }, [editingScheduleId, manageSchedules]);

  useEffect(() => {
    const isTabVisible = visibleTabItems.some((tab) => tab.key === activeTab);
    if (!isTabVisible) {
      setActiveTab("upcoming");
    }
  }, [activeTab, visibleTabItems]);

  useEffect(() => {
    if (activeTab !== "manage" && editingScheduleId) {
      setEditingScheduleId(null);
      setManageForm(INITIAL_FORM_STATE);
      setManageFormError("");
      setManageFormMessage("");
    }
  }, [activeTab, editingScheduleId]);

  useEffect(() => {
    if (activeTab !== "manage" && isManageCreateModalOpen) {
      setIsManageCreateModalOpen(false);
      setFormError("");
      setScheduleForm(INITIAL_FORM_STATE);
    }
  }, [activeTab, isManageCreateModalOpen]);

  useEffect(() => {
    if (activeTab !== "template" && isTemplateModalOpen) {
      setIsTemplateModalOpen(false);
      setEditingTemplateId("");
      setTemplateForm(buildDefaultTemplateFormState(defaultRecurringTemplate));
      setTemplateFormError("");
    }
  }, [activeTab, defaultRecurringTemplate, isTemplateModalOpen]);

  useEffect(() => {
    const savedRole = localStorage.getItem(teamRoleStorageKey);
    if (savedRole) {
      setRole(savedRole);
      return;
    }
    setRole(activeTeamProfile.role || "MEMBER");
  }, [activeTeamProfile.role, teamRoleStorageKey]);

  useEffect(() => {
    setTeamManageForm(buildTeamManageFormState(activeTeamProfile, team));
    setTeamLogoFile(null);
    setTeamLogoPreviewUrl("");
    if (teamLogoFileInputRef.current) {
      teamLogoFileInputRef.current.value = "";
    }
    setTeamManageError("");
    setTeamManageMessage("");
  }, [activeTeamProfile, team]);

  useEffect(() => {
    if (activeTeamProfile.updatedAt) {
      setTeamLogoCacheKey(String(activeTeamProfile.updatedAt));
    }
  }, [activeTeamProfile.updatedAt]);

  useEffect(() => {
    return () => {
      if (teamLogoPreviewUrl) {
        URL.revokeObjectURL(teamLogoPreviewUrl);
      }
    };
  }, [teamLogoPreviewUrl]);

  useEffect(() => {
    return () => {
      if (lineupTouchLockedBodyOverflowRef.current !== "") {
        document.body.style.overflow = lineupTouchLockedBodyOverflowRef.current;
        lineupTouchLockedBodyOverflowRef.current = "";
      }
    };
  }, []);

  const loadRecurringTemplates = useCallback(async () => {
    if (!hasScheduleManagePermission || !activeTeamProfile.id) {
      setRecurringTemplates([]);
      selectedRecurringTemplateIdRef.current = "";
      setSelectedRecurringTemplateId("");
      setRecurringTemplate(null);
      setTemplateLoadError("");
      return;
    }

    setIsTemplateLoading(true);
    setTemplateLoadError("");
    try {
      const responseData = await fetchScheduleTemplates({
        teamId: activeTeamProfile.id,
      });
      const rawItems = extractTemplateItems(responseData);
      const normalizedItems = rawItems
        .map((item) =>
          normalizeRecurringTemplate(item, defaultRecurringTemplate),
        )
        .filter((item) => Boolean(item.id));
      const selectedFromApi =
        rawItems.find(
          (item) => item?.isDefault || item?.defaultSelected || item?.selected,
        ) || null;
      const selectedFromApiId = selectedFromApi
        ? String(selectedFromApi.id ?? selectedFromApi.templateId ?? "")
        : "";
      const selectedByPreviousState = normalizedItems.some(
        (item) => item.id === selectedRecurringTemplateIdRef.current,
      )
        ? selectedRecurringTemplateIdRef.current
        : "";
      const nextSelectedTemplateId =
        selectedFromApiId &&
        normalizedItems.some((item) => item.id === selectedFromApiId)
          ? selectedFromApiId
          : selectedByPreviousState || normalizedItems[0]?.id || "";

      setRecurringTemplates(normalizedItems);
      selectedRecurringTemplateIdRef.current = nextSelectedTemplateId;
      setSelectedRecurringTemplateId(nextSelectedTemplateId);
      setTemplateForm(buildDefaultTemplateFormState(defaultRecurringTemplate));
      setEditingTemplateId("");
      setIsTemplateModalOpen(false);
      setTemplateFormError("");
      setTemplateFormMessage("");
    } catch (error) {
      setTemplateLoadError(
        error?.message || "템플릿 목록을 불러오지 못했습니다.",
      );
      setRecurringTemplates([]);
      selectedRecurringTemplateIdRef.current = "";
      setSelectedRecurringTemplateId("");
      setRecurringTemplate(null);
    } finally {
      setIsTemplateLoading(false);
    }
  }, [
    activeTeamProfile.id,
    defaultRecurringTemplate,
    hasScheduleManagePermission,
  ]);

  useEffect(() => {
    loadRecurringTemplates();
  }, [loadRecurringTemplates]);

  useEffect(() => {
    selectedRecurringTemplateIdRef.current = selectedRecurringTemplateId;
  }, [selectedRecurringTemplateId]);

  useEffect(() => {
    const activeTemplate =
      recurringTemplates.find(
        (template) => template.id === selectedRecurringTemplateId,
      ) ||
      recurringTemplates[0] ||
      null;
    setRecurringTemplate(activeTemplate);
  }, [recurringTemplates, selectedRecurringTemplateId]);

  const recurringWeekdaysLabel = useMemo(() => {
    if (!recurringTemplate || recurringTemplate.weekdays.length === 0) {
      return "-";
    }
    return recurringTemplate.weekdays.join(", ");
  }, [recurringTemplate]);

  const recurringTimeRangeLabel = useMemo(() => {
    if (!recurringTemplate) {
      return "-";
    }
    return `${recurringTemplate.startTime} ~ ${recurringTemplate.endTime}`;
  }, [recurringTemplate]);

  const teamActivityWeekdaysLabel = useMemo(() => {
    if (
      !Array.isArray(activeTeamProfile.activityWeekdays) ||
      activeTeamProfile.activityWeekdays.length === 0
    ) {
      return "-";
    }
    return activeTeamProfile.activityWeekdays.join(", ");
  }, [activeTeamProfile.activityWeekdays]);

  const teamActivityTimeRangeLabel = useMemo(() => {
    return String(activeTeamProfile.timeRange || "").trim() || "-";
  }, [activeTeamProfile.timeRange]);

  // 다가오는 일정을 페이지 단위로 가져오고 기존 목록과 병합한다.
  const loadUpcomingSchedulesPage = useCallback(
    async (page, { append = false } = {}) => {
      if (append) {
        setIsLoadingMoreUpcoming(true);
      } else {
        setIsUpcomingLoading(true);
      }
      setUpcomingLoadError("");

      try {
        const responseData = await fetchUpcomingSchedules({
          page,
          size: UPCOMING_PAGE_SIZE,
          teamId: activeTeamProfile.id || undefined,
        });
        const rawItems = extractUpcomingItems(responseData);
        const normalizedItems = rawItems
          .map(normalizeSchedulePayloadItem)
          .filter(
            (schedule) =>
              schedule.id != null && schedule.startsAt && schedule.endsAt,
          );

        setSchedules((previous) => {
          const nextSchedules = append ? [...previous] : [];
          const scheduleIndexById = new Map(
            nextSchedules.map((schedule, index) => [
              String(schedule.id),
              index,
            ]),
          );

          normalizedItems.forEach((item) => {
            const itemKey = String(item.id);
            const existingIndex = scheduleIndexById.get(itemKey);
            if (typeof existingIndex === "number") {
              nextSchedules[existingIndex] = {
                ...nextSchedules[existingIndex],
                ...item,
              };
              return;
            }
            scheduleIndexById.set(itemKey, nextSchedules.length);
            nextSchedules.push(item);
          });

          return nextSchedules.sort(
            (a, b) => new Date(a.startsAt) - new Date(b.startsAt),
          );
        });

        setUpcomingPage(page);
        setHasMoreUpcomingSchedules(
          extractHasNextPage(responseData, normalizedItems.length),
        );
      } catch (error) {
        setUpcomingLoadError(
          error?.message || "다가오는 일정을 불러오지 못했습니다.",
        );
      } finally {
        if (append) {
          setIsLoadingMoreUpcoming(false);
        } else {
          setIsUpcomingLoading(false);
        }
      }
    },
    [activeTeamProfile.id],
  );

  useEffect(() => {
    setSchedules(INITIAL_SCHEDULES);
    setManageSchedules([]);
    setManageLoadError("");
    setEditingScheduleId(null);
    setIsManageCreateModalOpen(false);
    setManageForm(INITIAL_FORM_STATE);
    setManageFormError("");
    setManageFormMessage("");
    setJoinRequests(INITIAL_JOIN_REQUESTS);
    setIsJoinRequestLoading(false);
    setJoinRequestLoadError("");
    setJoinRequestActionError("");
    setJoinRequestActionMessage("");
    setProcessingJoinRequestIds([]);
    setDeletingScheduleIds([]);
    setTeamMembers([]);
    setMemberLoadError("");
    setMemberActionError("");
    setMemberActionMessage("");
    setRoleUpdatingMemberIds([]);
    setExpellingMemberIds([]);
    setChairDelegatingMemberIds([]);
    setSavedLineups({});
    setLineupLoadStatusBySchedule({});
    setVotingScheduleIds([]);
    setVoteErrorMessage("");
    setAttendanceCheckinsBySchedule({});
    setAttendanceMessage("");
    setAttendanceErrorMessage("");
    setIsAttendanceCheckingIn(false);
    setIsAttendanceRefreshing(false);
    setVoteReminderMessage("");
    setVoteReminderErrorMessage("");
    setVoteReminderPendingScheduleIds([]);
    setVoteReminderCooldownBySchedule({});
    setLineupErrorMessage("");
    setSelectedScheduleDetailId(null);
    setUpcomingPage(0);
    loadUpcomingSchedulesPage(0, { append: false });
  }, [loadUpcomingSchedulesPage, activeTeamProfile.id]);

  // 운영진 일정 관리 탭용 전체 일정 로딩.
  const loadManageSchedules = useCallback(async () => {
    if (!hasScheduleManagePermission) {
      setManageSchedules([]);
      return;
    }
    if (!activeTeamProfile.id) {
      return;
    }

    setIsManageLoading(true);
    setManageLoadError("");
    try {
      const responseData = await fetchTeamSchedules({
        teamId: activeTeamProfile.id,
      });
      const normalizedItems = extractUpcomingItems(responseData)
        .map(normalizeSchedulePayloadItem)
        .filter(
          (schedule) =>
            schedule.id != null && schedule.startsAt && schedule.endsAt,
        );
      setManageSchedules(normalizedItems);
    } catch (error) {
      setManageLoadError(error?.message || "일정 목록을 불러오지 못했습니다.");
    } finally {
      setIsManageLoading(false);
    }
  }, [activeTeamProfile.id, hasScheduleManagePermission]);

  useEffect(() => {
    if (activeTab !== "manage" || !hasScheduleManagePermission) {
      return;
    }
    loadManageSchedules();
  }, [activeTab, hasScheduleManagePermission, loadManageSchedules]);

  const loadTeamMembers = useCallback(async () => {
    if (!hasMemberViewPermission) {
      setTeamMembers([]);
      return [];
    }
    if (!activeTeamProfile.id) {
      return [];
    }

    setIsMemberLoading(true);
    setMemberLoadError("");
    try {
      const responseData = await fetchTeamMembers({
        teamId: activeTeamProfile.id,
      });
      const normalizedMembers = extractTeamMemberItems(responseData)
        .map(normalizeTeamMemberItem)
        .filter((member) => member.userId && member.status === "APPROVED");
      setTeamMembers(sortTeamMembersByRoleAndName(normalizedMembers));
      return normalizedMembers;
    } catch (error) {
      setTeamMembers([]);
      setMemberLoadError(error?.message || "회원 목록을 불러오지 못했습니다.");
      return [];
    } finally {
      setIsMemberLoading(false);
    }
  }, [activeTeamProfile.id, hasMemberViewPermission]);

  useEffect(() => {
    if (activeTab !== "members" || !hasMemberViewPermission) {
      return;
    }
    loadTeamMembers();
  }, [activeTab, hasMemberViewPermission, loadTeamMembers]);

  useEffect(() => {
    if (
      !selectedScheduleDetailId ||
      teamMembers.length > 0 ||
      isMemberLoading
    ) {
      return;
    }
    loadTeamMembers();
  }, [
    selectedScheduleDetailId,
    teamMembers.length,
    isMemberLoading,
    loadTeamMembers,
  ]);

  // 가입 신청 목록을 서버에서 조회해 운영진/회장 검토 탭에 표시한다.
  const loadJoinRequests = useCallback(async () => {
    if (!hasJoinManagePermission) {
      setJoinRequests([]);
      setJoinRequestLoadError("");
      return;
    }
    if (!activeTeamProfile.id) {
      setJoinRequests([]);
      setJoinRequestLoadError("");
      return;
    }

    setIsJoinRequestLoading(true);
    setJoinRequestLoadError("");
    try {
      const responseData = await fetchTeamJoinRequests({
        teamId: activeTeamProfile.id,
      });
      const normalizedRequests = extractJoinRequestItems(responseData)
        .map(normalizeJoinRequestItem)
        .filter((request) => request.id)
        .sort((a, b) => {
          const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          if (aTime !== bTime) {
            return bTime - aTime;
          }
          return Number(b.id) - Number(a.id);
        });

      setJoinRequests(normalizedRequests);
    } catch (error) {
      setJoinRequests([]);
      setJoinRequestLoadError(
        error?.message || "가입 신청 목록을 불러오지 못했습니다.",
      );
    } finally {
      setIsJoinRequestLoading(false);
    }
  }, [activeTeamProfile.id, hasJoinManagePermission]);

  useEffect(() => {
    if (activeTab !== "join" || !hasJoinManagePermission) {
      return;
    }
    loadJoinRequests();
  }, [activeTab, hasJoinManagePermission, loadJoinRequests]);

  // 상세 모달에서 필요한 라인업을 서버에서 조회해 캐시에 저장한다.
  const loadLineupForSchedule = useCallback(
    async (scheduleId) => {
      if (!scheduleId || !activeTeamProfile.id) {
        return;
      }

      const scheduleKey = String(scheduleId);
      setLineupLoadStatusBySchedule((previous) => ({
        ...previous,
        [scheduleKey]: {
          loading: true,
          error: "",
        },
      }));

      try {
        const lineupResponse = await fetchScheduleLineup({
          teamId: activeTeamProfile.id,
          scheduleId,
        });
        const normalizedLineup = normalizeLineupResponseItem(lineupResponse);

        setSavedLineups((previous) => {
          const nextLineups = { ...previous };
          if (normalizedLineup) {
            nextLineups[scheduleKey] = normalizedLineup;
          } else {
            delete nextLineups[scheduleKey];
          }
          return nextLineups;
        });

        setLineupLoadStatusBySchedule((previous) => ({
          ...previous,
          [scheduleKey]: {
            loading: false,
            error: "",
          },
        }));
        return normalizedLineup;
      } catch (error) {
        setLineupLoadStatusBySchedule((previous) => ({
          ...previous,
          [scheduleKey]: {
            loading: false,
            error: error?.message || "라인업 조회에 실패했습니다.",
          },
        }));
        return null;
      }
    },
    [activeTeamProfile.id],
  );

  const loadAttendancesForSchedule = useCallback(
    async (scheduleId, { showMessage = false } = {}) => {
      if (!scheduleId || !activeTeamProfile.id) {
        return;
      }

      const scheduleKey = String(scheduleId);
      setAttendanceErrorMessage("");
      setIsAttendanceRefreshing(true);

      try {
        const responseData = await fetchScheduleAttendances({
          teamId: activeTeamProfile.id,
          scheduleId,
        });
        const attendanceItems = extractAttendanceItems(responseData);
        const attendanceByUserId = attendanceItems.reduce((acc, item) => {
          const userId = toUserKey(item?.userId ?? item?.id);
          if (!userId) {
            return acc;
          }
          acc[userId] = {
            checkedInAt: item?.checkedInAt || item?.checkInAt || null,
            distanceMeters:
              item?.distanceMeters != null ? Number(item.distanceMeters) : null,
            nickname: item?.nickname || "",
          };
          return acc;
        }, {});

        setAttendanceCheckinsBySchedule((previous) => ({
          ...previous,
          [scheduleKey]: attendanceByUserId,
        }));
        setSchedules((previous) =>
          previous.map((schedule) => {
            if (String(schedule.id) !== scheduleKey) {
              return schedule;
            }
            return {
              ...schedule,
              attendanceByUserId,
            };
          }),
        );
        if (showMessage) {
          setAttendanceMessage("출석 현황을 새로고침했습니다.");
        }
      } catch (error) {
        setAttendanceErrorMessage(
          getAttendanceFriendlyErrorMessage(error) ||
            "출석 현황 새로고침에 실패했습니다.",
        );
      } finally {
        setIsAttendanceRefreshing(false);
      }
    },
    [activeTeamProfile.id],
  );

  const handleOpenCloseTeamModal = () => {
    if (!isChair) {
      return;
    }
    if (isClosingTeam) {
      return;
    }

    setCloseTeamConfirmName("");
    setCloseTeamModalError("");
    setIsCloseTeamModalOpen(true);
  };

  const handleDismissCloseTeamModal = () => {
    if (isClosingTeam) {
      return;
    }
    setIsCloseTeamModalOpen(false);
    setCloseTeamConfirmName("");
    setCloseTeamModalError("");
  };

  const handleCloseTeam = async () => {
    if (!isChair || isClosingTeam) {
      return;
    }

    const expectedTeamName = String(activeTeamProfile.name || "").trim();
    const enteredTeamName = String(closeTeamConfirmName || "").trim();
    if (!enteredTeamName || enteredTeamName !== expectedTeamName) {
      setCloseTeamModalError("팀명을 정확히 입력해 주세요.");
      return;
    }

    try {
      setIsClosingTeam(true);
      setCloseTeamModalError("");
      await closeTeam({
        teamId: activeTeamProfile.id,
      });

      if (onRefreshTeams) {
        await onRefreshTeams();
      }

      setIsCloseTeamModalOpen(false);
      window.location.replace("/team");
    } catch (error) {
      setCloseTeamModalError(error?.message || "팀 폐쇄에 실패했습니다.");
    } finally {
      setIsClosingTeam(false);
    }
  };

  const handleOpenLeaveTeamModal = () => {
    if (isLeavingTeam) {
      return;
    }

    setLeaveTeamConfirmName("");
    setLeaveTeamModalError("");
    setIsLeaveTeamModalOpen(true);
  };

  const handleDismissLeaveTeamModal = () => {
    if (isLeavingTeam) {
      return;
    }

    setIsLeaveTeamModalOpen(false);
    setLeaveTeamConfirmName("");
    setLeaveTeamModalError("");
  };

  const handleLeaveTeam = async () => {
    if (isLeavingTeam) {
      return;
    }

    if (isChair) {
      setLeaveTeamModalError(
        "회장은 먼저 다른 멤버에게 회장을 위임한 뒤 팀 탈퇴할 수 있습니다.",
      );
      return;
    }

    const expectedTeamName = String(activeTeamProfile.name || "").trim();
    const enteredTeamName = String(leaveTeamConfirmName || "").trim();
    if (!enteredTeamName || enteredTeamName !== expectedTeamName) {
      setLeaveTeamModalError("팀명을 정확히 입력해 주세요.");
      return;
    }

    try {
      setIsLeavingTeam(true);
      setLeaveTeamModalError("");
      await leaveTeam({
        teamId: activeTeamProfile.id,
      });

      if (onRefreshTeams) {
        await onRefreshTeams();
      }

      setIsLeaveTeamModalOpen(false);
      window.location.replace("/team");
    } catch (error) {
      if (error?.status === 404) {
        setLeaveTeamModalError(
          "팀 탈퇴 API를 찾지 못했습니다. 백엔드 경로를 확인해 주세요.",
        );
        return;
      }
      setLeaveTeamModalError(error?.message || "팀 탈퇴에 실패했습니다.");
    } finally {
      setIsLeavingTeam(false);
    }
  };

  const handleTeamManageFieldChange = (field, value) => {
    setTeamManageError("");
    setTeamManageMessage("");
    setTeamManageForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleTeamManageWeekdayToggle = (weekdayLabel) => {
    setTeamManageError("");
    setTeamManageMessage("");
    setTeamManageForm((previous) => {
      const hasWeekday = previous.activityWeekdays.includes(weekdayLabel);
      if (hasWeekday) {
        if (previous.activityWeekdays.length === 1) {
          return previous;
        }
        return {
          ...previous,
          activityWeekdays: previous.activityWeekdays.filter(
            (item) => item !== weekdayLabel,
          ),
        };
      }

      return {
        ...previous,
        activityWeekdays: getSortedWeekdays([
          ...previous.activityWeekdays,
          weekdayLabel,
        ]),
      };
    });
  };

  const handleTeamLogoFileChange = (event) => {
    const nextFile = event.target.files?.[0] || null;
    if (teamLogoPreviewUrl) {
      URL.revokeObjectURL(teamLogoPreviewUrl);
    }

    setTeamManageError("");
    setTeamManageMessage("");
    setTeamLogoFile(nextFile);
    setTeamLogoPreviewUrl(nextFile ? URL.createObjectURL(nextFile) : "");
  };

  const handleResetTeamLogoToDefault = () => {
    if (teamLogoPreviewUrl) {
      URL.revokeObjectURL(teamLogoPreviewUrl);
    }
    if (teamLogoFileInputRef.current) {
      teamLogoFileInputRef.current.value = "";
    }

    setTeamManageError("");
    setTeamManageMessage("");
    setTeamLogoFile(null);
    setTeamLogoPreviewUrl("");
    setTeamManageForm((previous) => ({
      ...previous,
      logoUrl: "",
    }));
  };

  const handleSaveTeamProfile = async (event) => {
    event.preventDefault();

    if (!hasTeamManagePermission) {
      setTeamManageError("팀 정보 수정 권한이 없습니다.");
      return;
    }
    if (!activeTeamProfile.id) {
      setTeamManageError("유효한 팀 정보를 찾을 수 없습니다.");
      return;
    }

    if (!String(teamManageForm.name || "").trim()) {
      setTeamManageError("팀 이름을 입력해 주세요.");
      return;
    }
    if (
      !Array.isArray(teamManageForm.activityWeekdays) ||
      teamManageForm.activityWeekdays.length === 0
    ) {
      setTeamManageError("최소 1개 이상의 활동 요일을 선택해 주세요.");
      return;
    }
    if (!teamManageForm.startTime || !teamManageForm.endTime) {
      setTeamManageError("활동 시작/종료 시간을 입력해 주세요.");
      return;
    }
    if (teamManageForm.startTime >= teamManageForm.endTime) {
      setTeamManageError("활동 종료 시간은 시작 시간보다 늦어야 합니다.");
      return;
    }

    setTeamManageError("");
    setTeamManageMessage("");
    setIsSavingTeamProfile(true);

    try {
      let nextLogoUrl = String(teamManageForm.logoUrl || "").trim();
      if (teamLogoFile) {
        setIsTeamLogoUploading(true);
        try {
          const uploadResponse = await uploadTeamLogoForTeam({
            teamId: activeTeamProfile.id,
            file: teamLogoFile,
          });
          nextLogoUrl = String(
            uploadResponse?.url ||
              uploadResponse?.fileUrl ||
              uploadResponse?.publicUrl ||
              nextLogoUrl,
          ).trim();
        } finally {
          setIsTeamLogoUploading(false);
        }
      }

      const sortedWeekdays = getSortedWeekdays(teamManageForm.activityWeekdays);
      const preferredTimeSlot = `${teamManageForm.startTime} ~ ${teamManageForm.endTime}`;
      const normalizedDescription = String(
        teamManageForm.description || "",
      ).trim();
      const normalizedGenderType = normalizeTeamGenderType(
        teamManageForm.genderType,
      );

      await updateTeamInfo({
        teamId: activeTeamProfile.id,
        payload: {
          name: String(teamManageForm.name).trim(),
          chairName: String(activeTeamProfile.chair || "").trim() || null,
          logoUrl: nextLogoUrl || null,
          activityWeekdays: sortedWeekdays,
          activityDays: sortedWeekdays
            .map((weekday) => WEEKDAY_LABEL_TO_ENUM[weekday])
            .filter(Boolean),
          timeRange: preferredTimeSlot,
          preferredTimeSlot,
          ageBand: teamManageForm.ageBand,
          averageAgeGroup: teamManageForm.ageBand,
          genderType: normalizedGenderType,
          description: normalizedDescription || null,
        },
      });

      if (onRefreshTeams) {
        await onRefreshTeams();
      }

      setTeamManageForm((previous) => ({
        ...previous,
        logoUrl: nextLogoUrl,
      }));
      setTeamLogoCacheKey(String(Date.now()));
      setTeamLogoFile(null);
      if (teamLogoPreviewUrl) {
        URL.revokeObjectURL(teamLogoPreviewUrl);
      }
      setTeamLogoPreviewUrl("");
      if (teamLogoFileInputRef.current) {
        teamLogoFileInputRef.current.value = "";
      }
      setTeamManageMessage("팀 정보를 저장했습니다.");
    } catch (error) {
      setTeamManageError(error?.message || "팀 정보 저장에 실패했습니다.");
    } finally {
      setIsSavingTeamProfile(false);
      setIsTeamLogoUploading(false);
    }
  };

  const handleOpenScheduleDetail = (scheduleId) => {
    setVoteErrorMessage("");
    setAttendanceMessage("");
    setAttendanceErrorMessage("");
    setIsAttendanceCheckingIn(false);
    setIsAttendanceRefreshing(false);
    setIsVoteMemberListExpanded(false);
    setIsAttendanceDetailExpanded(false);
    setVoteReminderMessage("");
    setVoteReminderErrorMessage("");
    setLineupErrorMessage("");
    setSelectedScheduleDetailId(scheduleId);
    loadAttendancesForSchedule(scheduleId);
    const scheduleKey = String(scheduleId);
    const selectedSchedule = schedules.find(
      (schedule) => String(schedule.id) === scheduleKey,
    );

    const scheduleReminderSentAt = parseDateValue(
      selectedSchedule?.voteReminderLastSentAt,
    );
    if (scheduleReminderSentAt) {
      const nextAvailableAt = getNextDayStartDate(scheduleReminderSentAt);
      if (nextAvailableAt.getTime() > Date.now()) {
        setVoteReminderCooldownBySchedule((previous) => ({
          ...previous,
          [scheduleKey]: nextAvailableAt.toISOString(),
        }));
      }
    }

    if (
      selectedSchedule?.category === "MATCH" &&
      !savedLineups[scheduleKey] &&
      !lineupLoadStatusBySchedule[scheduleKey]?.loading
    ) {
      loadLineupForSchedule(scheduleId);
    }
  };

  const handleCloseScheduleDetail = () => {
    setSelectedScheduleDetailId(null);
    setVoteErrorMessage("");
    setAttendanceMessage("");
    setAttendanceErrorMessage("");
    setIsAttendanceCheckingIn(false);
    setIsAttendanceRefreshing(false);
    setVoteReminderMessage("");
    setVoteReminderErrorMessage("");
    setLineupErrorMessage("");
  };

  const handleRefreshUpcoming = () => {
    if (isUpcomingLoading || isLoadingMoreUpcoming) {
      return;
    }
    loadUpcomingSchedulesPage(0, { append: false });
  };

  const handleShowMoreUpcoming = () => {
    if (
      isUpcomingLoading ||
      isLoadingMoreUpcoming ||
      !hasMoreUpcomingSchedules
    ) {
      return;
    }

    loadUpcomingSchedulesPage(upcomingPage + 1, { append: true });
  };

  // 투표 저장: 서버 반영 성공 후 화면 상태를 동기화한다.
  const handleVote = async (scheduleId, voteValue) => {
    const scheduleKey = String(scheduleId);
    const selectedSchedule = schedules.find(
      (schedule) => String(schedule.id) === scheduleKey,
    );
    if (!selectedSchedule || !isVoteOpen(selectedSchedule)) {
      return;
    }
    if (!activeTeamProfile.id) {
      return;
    }

    if (votingScheduleIds.includes(scheduleKey)) {
      return;
    }

    const currentVote = selectedSchedule?.votes?.[currentUserId] || null;
    const isToggleCancel = currentVote === voteValue;

    setVoteErrorMessage("");
    setVotingScheduleIds((previous) =>
      previous.includes(scheduleKey) ? previous : [...previous, scheduleKey],
    );

    try {
      if (isToggleCancel) {
        await cancelScheduleVote({
          teamId: activeTeamProfile.id,
          scheduleId,
        });
      } else {
        await voteSchedule({
          teamId: activeTeamProfile.id,
          scheduleId,
          voteType: voteValue,
        });
      }

      setSchedules((previous) =>
        previous.map((schedule) => {
          if (String(schedule.id) !== scheduleKey) {
            return schedule;
          }

          if (isToggleCancel) {
            const nextVotes = { ...(schedule.votes || {}) };
            delete nextVotes[currentUserId];
            return {
              ...schedule,
              votes: nextVotes,
            };
          }

          return {
            ...schedule,
            votes: {
              ...(schedule.votes || {}),
              [currentUserId]: voteValue,
            },
          };
        }),
      );
    } catch (error) {
      if (isToggleCancel && error?.status === 404) {
        setVoteErrorMessage(
          "투표 취소 API가 아직 백엔드에 없습니다. DELETE /vote 엔드포인트를 추가해 주세요.",
        );
        return;
      }
      setVoteErrorMessage(error?.message || "투표 저장에 실패했습니다.");
    } finally {
      setVotingScheduleIds((previous) =>
        previous.filter((id) => id !== scheduleKey),
      );
    }
  };

  const handleCheckInAttendance = async () => {
    if (!selectedScheduleAttendanceDetail.scheduleKey || !selectedScheduleDetail) {
      return;
    }
    if (!currentUserId) {
      setAttendanceErrorMessage("로그인 사용자 정보를 확인할 수 없습니다.");
      return;
    }
    if (!activeTeamProfile.id) {
      setAttendanceErrorMessage("팀 정보를 확인할 수 없습니다.");
      return;
    }
    if (selectedScheduleAttendanceDetail.myCheckedIn) {
      setAttendanceMessage("이미 출석 체크가 완료되었습니다.");
      return;
    }
    if (isAttendanceCheckingIn) {
      return;
    }

    setIsAttendanceDetailExpanded(true);
    setAttendanceMessage("");
    setAttendanceErrorMessage("");
    setIsAttendanceCheckingIn(true);

    try {
      const currentPosition = await getCurrentDevicePosition();
      const response = await checkScheduleAttendance({
        teamId: activeTeamProfile.id,
        scheduleId: selectedScheduleDetail.id,
        latitude: currentPosition.latitude,
        longitude: currentPosition.longitude,
      });
      const responsePayload =
        response?.data && typeof response.data === "object"
          ? response.data
          : response;
      const scheduleKey = selectedScheduleAttendanceDetail.scheduleKey;
      const nextAttendanceEntry = {
        checkedInAt: responsePayload?.checkedInAt || new Date().toISOString(),
        distanceMeters:
          responsePayload?.distanceMeters != null
            ? Number(responsePayload.distanceMeters)
            : null,
      };
      setAttendanceCheckinsBySchedule((previous) => {
        const previousCheckins = previous[scheduleKey] || {};
        return {
          ...previous,
          [scheduleKey]: {
            ...previousCheckins,
            [currentUserId]: nextAttendanceEntry,
          },
        };
      });
      setSchedules((previous) =>
        previous.map((schedule) => {
          if (String(schedule.id) !== scheduleKey) {
            return schedule;
          }
          return {
            ...schedule,
            attendanceByUserId: {
              ...(schedule.attendanceByUserId || {}),
              [currentUserId]: nextAttendanceEntry,
            },
          };
        }),
      );

      const distanceLabel =
        nextAttendanceEntry.distanceMeters != null
          ? ` (거리 ${nextAttendanceEntry.distanceMeters}m)`
          : "";
      setAttendanceMessage(`출석 체크가 완료되었습니다.${distanceLabel}`);
      loadAttendancesForSchedule(selectedScheduleDetail.id);
    } catch (error) {
      if (getErrorText(error).includes("이미 출석 체크")) {
        const scheduleKey = selectedScheduleAttendanceDetail.scheduleKey;
        const duplicatedAttendanceEntry = {
          checkedInAt: new Date().toISOString(),
          distanceMeters: null,
        };
        setAttendanceCheckinsBySchedule((previous) => {
          const previousCheckins = previous[scheduleKey] || {};
          return {
            ...previous,
            [scheduleKey]: {
              ...previousCheckins,
              [currentUserId]: duplicatedAttendanceEntry,
            },
          };
        });
        setSchedules((previous) =>
          previous.map((schedule) => {
            if (String(schedule.id) !== scheduleKey) {
              return schedule;
            }
            return {
              ...schedule,
              attendanceByUserId: {
                ...(schedule.attendanceByUserId || {}),
                [currentUserId]: duplicatedAttendanceEntry,
              },
            };
          }),
        );
        setAttendanceMessage("이미 출석 체크가 완료되었습니다.");
        loadAttendancesForSchedule(selectedScheduleDetail.id);
      } else {
        setAttendanceErrorMessage(getAttendanceFriendlyErrorMessage(error));
      }
    } finally {
      setIsAttendanceCheckingIn(false);
    }
  };

  const handleRefreshAttendanceStatus = async () => {
    if (!selectedScheduleDetail || !activeTeamProfile.id) {
      return;
    }
    if (isAttendanceRefreshing) {
      return;
    }

    setIsAttendanceDetailExpanded(true);
    setAttendanceMessage("");
    setAttendanceErrorMessage("");
    await loadAttendancesForSchedule(selectedScheduleDetail.id, {
      showMessage: true,
    });
  };

  const handleSendVoteReminder = async (scheduleId) => {
    if (!hasScheduleManagePermission || !activeTeamProfile.id) {
      return;
    }

    const scheduleKey = String(scheduleId);
    const selectedSchedule = schedules.find(
      (schedule) => String(schedule.id) === scheduleKey,
    );
    if (!selectedSchedule || !isVoteOpen(selectedSchedule)) {
      setVoteReminderErrorMessage(
        "투표 가능 기간에만 독촉 알림을 보낼 수 있습니다.",
      );
      return;
    }

    const nextAvailableAt = voteReminderCooldownBySchedule[scheduleKey];
    if (
      nextAvailableAt &&
      parseDateValue(nextAvailableAt)?.getTime() > Date.now()
    ) {
      setVoteReminderErrorMessage(
        "오늘은 이미 독촉 알림을 보냈습니다. 내일 다시 시도해 주세요.",
      );
      return;
    }

    if (voteReminderPendingScheduleIds.includes(scheduleKey)) {
      return;
    }

    setVoteReminderMessage("");
    setVoteReminderErrorMessage("");
    setVoteReminderPendingScheduleIds((previous) =>
      previous.includes(scheduleKey) ? previous : [...previous, scheduleKey],
    );

    try {
      const response = await sendScheduleVoteReminder({
        teamId: activeTeamProfile.id,
        scheduleId,
      });
      const responseNextAvailableAt =
        toIsoStringOrEmpty(response?.nextAvailableAt) ||
        toIsoStringOrEmpty(
          getNextDayStartDate(parseDateValue(response?.sentAt) || new Date()),
        );

      setVoteReminderCooldownBySchedule((previous) => ({
        ...previous,
        [scheduleKey]: responseNextAvailableAt,
      }));
      setSchedules((previous) =>
        previous.map((schedule) => {
          if (String(schedule.id) !== scheduleKey) {
            return schedule;
          }

          return {
            ...schedule,
            voteReminderLastSentAt: response?.sentAt || new Date().toISOString(),
          };
        }),
      );
      setVoteReminderMessage("미응답자에게 투표 독촉 알림을 전송했습니다.");
    } catch (error) {
      let errorNextAvailableAt = extractReminderNextAvailableAtFromError(error);
      if (!errorNextAvailableAt && isDailyReminderLimitError(error)) {
        errorNextAvailableAt = toIsoStringOrEmpty(getNextDayStartDate(new Date()));
      }
      if (errorNextAvailableAt) {
        setVoteReminderCooldownBySchedule((previous) => ({
          ...previous,
          [scheduleKey]: errorNextAvailableAt,
        }));
      }
      setVoteReminderErrorMessage(getVoteReminderFriendlyErrorMessage(error));
    } finally {
      setVoteReminderPendingScheduleIds((previous) =>
        previous.filter((id) => id !== scheduleKey),
      );
    }
  };

  const resolveAddressCoordinatesFromApi = useCallback(async (addressText) => {
    const normalizedAddress = String(addressText || "").trim();
    if (!normalizedAddress) {
      throw new Error("주소를 입력해 주세요.");
    }
    return searchAddressCoordinates({
      query: normalizedAddress,
    });
  }, []);

  const handleOpenScheduleAddressSearchModal = () => {
    setFormError("");
    if (isAddressSearchLoading) {
      return;
    }

    setIsAddressSearchLoading(true);
    loadKakaoPostcodeScript()
      .then(() => {
        if (!window.daum?.Postcode) {
          throw new Error("카카오 주소 검색창을 열지 못했습니다.");
        }

        openKakaoPostcodeLayer({
          onComplete: async (data) => {
            const selectedAddress = String(
              data?.roadAddress || data?.jibunAddress || data?.address || "",
            ).trim();
            if (!selectedAddress) {
              setFormError("선택한 주소를 확인할 수 없습니다.");
              return;
            }

            setIsScheduleAddressResolving(true);
            try {
              const resolved =
                await resolveAddressCoordinatesFromApi(selectedAddress);
              setScheduleForm((previous) => ({
                ...previous,
                address: resolved.address,
                detailAddress: "",
                latitude: String(resolved.latitude),
                longitude: String(resolved.longitude),
              }));
            } catch (error) {
              setFormError(error?.message || "주소 좌표 조회에 실패했습니다.");
            } finally {
              setIsScheduleAddressResolving(false);
            }
          },
          onError: (error) => {
            setFormError(error?.message || "주소 검색창을 열지 못했습니다.");
          },
          onClose: () => {
            setIsAddressSearchLoading(false);
          },
        });
      })
      .catch((error) => {
        setFormError(error?.message || "주소 검색창을 열지 못했습니다.");
        setIsAddressSearchLoading(false);
      });
  };

  const handleOpenManageAddressSearchModal = () => {
    setManageFormError("");
    setManageFormMessage("");
    if (isAddressSearchLoading) {
      return;
    }

    setIsAddressSearchLoading(true);
    loadKakaoPostcodeScript()
      .then(() => {
        if (!window.daum?.Postcode) {
          throw new Error("카카오 주소 검색창을 열지 못했습니다.");
        }

        openKakaoPostcodeLayer({
          onComplete: async (data) => {
            const selectedAddress = String(
              data?.roadAddress || data?.jibunAddress || data?.address || "",
            ).trim();
            if (!selectedAddress) {
              setManageFormError("선택한 주소를 확인할 수 없습니다.");
              return;
            }

            setIsManageAddressResolving(true);
            try {
              const resolved =
                await resolveAddressCoordinatesFromApi(selectedAddress);
              setManageForm((previous) => ({
                ...previous,
                address: resolved.address,
                detailAddress: "",
                latitude: String(resolved.latitude),
                longitude: String(resolved.longitude),
              }));
            } catch (error) {
              setManageFormError(
                error?.message || "주소 좌표 조회에 실패했습니다.",
              );
            } finally {
              setIsManageAddressResolving(false);
            }
          },
          onError: (error) => {
            setManageFormError(error?.message || "주소 검색창을 열지 못했습니다.");
          },
          onClose: () => {
            setIsAddressSearchLoading(false);
          },
        });
      })
      .catch((error) => {
        setManageFormError(error?.message || "주소 검색창을 열지 못했습니다.");
        setIsAddressSearchLoading(false);
      });
  };

  const handleOpenTemplateAddressSearchModal = () => {
    setTemplateFormError("");
    setTemplateFormMessage("");
    if (isAddressSearchLoading) {
      return;
    }

    setIsAddressSearchLoading(true);
    loadKakaoPostcodeScript()
      .then(() => {
        if (!window.daum?.Postcode) {
          throw new Error("카카오 주소 검색창을 열지 못했습니다.");
        }

        openKakaoPostcodeLayer({
          onComplete: async (data) => {
            const selectedAddress = String(
              data?.roadAddress || data?.jibunAddress || data?.address || "",
            ).trim();
            if (!selectedAddress) {
              setTemplateFormError("선택한 주소를 확인할 수 없습니다.");
              return;
            }

            setIsTemplateAddressResolving(true);
            try {
              const resolved =
                await resolveAddressCoordinatesFromApi(selectedAddress);
              setTemplateForm((previous) => ({
                ...previous,
                address: resolved.address,
                detailAddress: "",
                latitude: String(resolved.latitude),
                longitude: String(resolved.longitude),
              }));
            } catch (error) {
              setTemplateFormError(
                error?.message || "주소 좌표 조회에 실패했습니다.",
              );
            } finally {
              setIsTemplateAddressResolving(false);
            }
          },
          onError: (error) => {
            setTemplateFormError(
              error?.message || "주소 검색창을 열지 못했습니다.",
            );
          },
          onClose: () => {
            setIsAddressSearchLoading(false);
          },
        });
      })
      .catch((error) => {
        setTemplateFormError(
          error?.message || "주소 검색창을 열지 못했습니다.",
        );
        setIsAddressSearchLoading(false);
      });
  };

  const handleScheduleFormChange = (field, value) => {
    setFormError("");
    setScheduleForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleCreateSchedule = async (event) => {
    event.preventDefault();
    setFormError("");

    if (!hasScheduleManagePermission) {
      setFormError("일정 등록 권한이 없습니다.");
      return;
    }

    let nextScheduleForm = { ...scheduleForm };
    if (
      nextScheduleForm.address &&
      (toNullableCoordinate(nextScheduleForm.latitude) === null ||
        toNullableCoordinate(nextScheduleForm.longitude) === null)
    ) {
      try {
        const resolved = await resolveAddressCoordinatesFromApi(
          nextScheduleForm.address,
        );
        nextScheduleForm = {
          ...nextScheduleForm,
          address: resolved.address,
          latitude: String(resolved.latitude),
          longitude: String(resolved.longitude),
        };
        setScheduleForm(nextScheduleForm);
      } catch (error) {
        setFormError(error?.message || "주소 좌표 조회에 실패했습니다.");
        return;
      }
    }

    const validationError = validateScheduleForm(nextScheduleForm);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    try {
      setIsCreatingSchedule(true);
      await createSchedule({
        teamId: activeTeamProfile.id,
        payload: toSchedulePayload(nextScheduleForm, activeTeamProfile.id),
      });

      setScheduleForm(INITIAL_FORM_STATE);
      if (isManageCreateModalOpen) {
        setIsManageCreateModalOpen(false);
        setActiveTab("manage");
      } else {
        setActiveTab("upcoming");
      }
      await Promise.all([
        loadUpcomingSchedulesPage(0, { append: false }),
        hasScheduleManagePermission ? loadManageSchedules() : Promise.resolve(),
      ]);
    } catch (error) {
      setFormError(
        error?.message ||
          "일정 등록에 실패했습니다. 잠시 후 다시 시도해 주세요.",
      );
    } finally {
      setIsCreatingSchedule(false);
    }
  };

  const handleManageFormChange = (field, value) => {
    setManageFormError("");
    setManageFormMessage("");
    setManageForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleOpenCreateScheduleModal = () => {
    if (selectedScheduleDetailId) {
      handleCloseScheduleDetail();
    }
    if (editingScheduleId) {
      handleCancelEditSchedule();
    }
    setFormError("");
    setScheduleForm(INITIAL_FORM_STATE);
    loadRecurringTemplates();
    setIsManageCreateModalOpen(true);
  };

  const handleCloseCreateScheduleModal = () => {
    setIsManageCreateModalOpen(false);
    setFormError("");
  };

  const handleStartEditSchedule = (schedule) => {
    if (selectedScheduleDetailId) {
      handleCloseScheduleDetail();
    }
    setEditingScheduleId(String(schedule.id));
    setManageForm(toScheduleFormState(schedule));
    setManageFormError("");
    setManageFormMessage("");
  };

  const handleCancelEditSchedule = () => {
    setEditingScheduleId(null);
    setManageForm(INITIAL_FORM_STATE);
    setManageFormError("");
    setManageFormMessage("");
  };

  const handleUpdateSchedule = async (event) => {
    event.preventDefault();
    setManageFormError("");
    setManageFormMessage("");

    if (!editingScheduleId) {
      setManageFormError("수정할 일정을 먼저 선택해 주세요.");
      return;
    }

    let nextManageForm = { ...manageForm };
    if (
      nextManageForm.address &&
      (toNullableCoordinate(nextManageForm.latitude) === null ||
        toNullableCoordinate(nextManageForm.longitude) === null)
    ) {
      try {
        const resolved = await resolveAddressCoordinatesFromApi(
          nextManageForm.address,
        );
        nextManageForm = {
          ...nextManageForm,
          address: resolved.address,
          latitude: String(resolved.latitude),
          longitude: String(resolved.longitude),
        };
        setManageForm(nextManageForm);
      } catch (error) {
        setManageFormError(error?.message || "주소 좌표 조회에 실패했습니다.");
        return;
      }
    }

    const validationError = validateScheduleForm(nextManageForm);
    if (validationError) {
      setManageFormError(validationError);
      return;
    }

    try {
      setIsUpdatingSchedule(true);
      await updateSchedule({
        teamId: activeTeamProfile.id,
        scheduleId: editingScheduleId,
        payload: toSchedulePayload(nextManageForm, activeTeamProfile.id),
      });

      await Promise.all([
        loadUpcomingSchedulesPage(0, { append: false }),
        loadManageSchedules(),
      ]);
      setEditingScheduleId(null);
      setManageForm(INITIAL_FORM_STATE);
    } catch (error) {
      setManageFormError(error?.message || "일정 수정에 실패했습니다.");
    } finally {
      setIsUpdatingSchedule(false);
    }
  };

  const handleDeleteManagedSchedule = async (schedule) => {
    const scheduleKey = String(schedule.id);
    if (deletingScheduleIds.includes(scheduleKey)) {
      return;
    }

    const shouldDelete = window.confirm(
      `"${schedule.title}" 일정을 삭제할까요?`,
    );
    if (!shouldDelete) {
      return;
    }

    setManageFormError("");
    setManageFormMessage("");
    setDeletingScheduleIds((previous) =>
      previous.includes(scheduleKey) ? previous : [...previous, scheduleKey],
    );

    try {
      await deleteSchedule({
        teamId: activeTeamProfile.id,
        scheduleId: schedule.id,
      });

      if (
        selectedScheduleDetailId &&
        String(selectedScheduleDetailId) === scheduleKey
      ) {
        handleCloseScheduleDetail();
      }
      if (editingScheduleId === scheduleKey) {
        handleCancelEditSchedule();
      }

      await Promise.all([
        loadUpcomingSchedulesPage(0, { append: false }),
        loadManageSchedules(),
      ]);
    } catch (error) {
      setManageFormError(error?.message || "일정 삭제에 실패했습니다.");
    } finally {
      setDeletingScheduleIds((previous) =>
        previous.filter((id) => id !== scheduleKey),
      );
    }
  };

  const handleJoinDecision = async (joinRequest, decision) => {
    if (!hasJoinManagePermission) {
      return;
    }

    const joinRequestId = String(joinRequest?.id || "");
    if (!joinRequestId) {
      return;
    }
    if (processingJoinRequestIds.includes(joinRequestId)) {
      return;
    }

    setJoinRequestActionError("");
    setJoinRequestActionMessage("");
    setProcessingJoinRequestIds((previous) =>
      previous.includes(joinRequestId)
        ? previous
        : [...previous, joinRequestId],
    );

    try {
      if (decision === "APPROVE") {
        await approveTeamJoinRequest({
          teamId: activeTeamProfile.id,
          joinRequestId,
        });
      } else {
        await rejectTeamJoinRequest({
          teamId: activeTeamProfile.id,
          joinRequestId,
        });
      }

      await Promise.all([loadJoinRequests(), loadTeamMembers()]);
      setJoinRequestActionMessage(
        `${joinRequest.requesterNickname}님의 가입 신청을 ${decision === "APPROVE" ? "승인" : "반려"}했습니다.`,
      );
    } catch (error) {
      setJoinRequestActionError(
        error?.message ||
          `가입 신청 ${decision === "APPROVE" ? "승인" : "반려"} 처리에 실패했습니다.`,
      );
    } finally {
      setProcessingJoinRequestIds((previous) =>
        previous.filter((id) => id !== joinRequestId),
      );
    }
  };

  const canPromoteMemberToStaff = (member) => {
    if (!hasMemberManagePermission) {
      return false;
    }
    if (member.role !== "MEMBER") {
      return false;
    }
    if (toUserKey(member.userId) === currentUserId) {
      return false;
    }
    return true;
  };

  const canDemoteStaffToMember = (member) => {
    if (!isChair) {
      return false;
    }
    if (member.role !== "STAFF") {
      return false;
    }
    if (toUserKey(member.userId) === currentUserId) {
      return false;
    }
    return true;
  };

  const canExpelMember = (member) => {
    if (!hasMemberManagePermission) {
      return false;
    }
    if (member.role === "CHAIR") {
      return false;
    }
    if (toUserKey(member.userId) === currentUserId) {
      return false;
    }

    if (isChair) {
      return true;
    }
    return member.role === "MEMBER";
  };

  const canDelegateChairToMember = (member) => {
    if (!isChair) {
      return false;
    }
    if (member.role === "CHAIR") {
      return false;
    }
    if (toUserKey(member.userId) === currentUserId) {
      return false;
    }
    return true;
  };

  const handleUpdateMemberRole = async (member, nextRole) => {
    const memberId = toUserKey(member.userId);
    if (!memberId) {
      return;
    }
    if (!hasMemberManagePermission) {
      setMemberActionError("회원 권한 변경 권한이 없습니다.");
      return;
    }
    if (member.role === "CHAIR") {
      setMemberActionError(
        "회장 권한은 직접 변경할 수 없습니다. 회장 위임을 사용해 주세요.",
      );
      return;
    }
    if (nextRole !== "MEMBER" && nextRole !== "STAFF") {
      return;
    }

    if (nextRole === "STAFF" && !canPromoteMemberToStaff(member)) {
      setMemberActionError("해당 회원을 운영진으로 변경할 권한이 없습니다.");
      return;
    }
    if (nextRole === "MEMBER" && !canDemoteStaffToMember(member)) {
      setMemberActionError("운영진 강등은 회장만 가능합니다.");
      return;
    }

    setMemberActionError("");
    setMemberActionMessage("");
    setRoleUpdatingMemberIds((previous) =>
      previous.includes(memberId) ? previous : [...previous, memberId],
    );

    try {
      await updateTeamMemberRole({
        teamId: activeTeamProfile.id,
        userId: memberId,
        role: nextRole,
      });
      await loadTeamMembers();
      setMemberActionMessage(
        `${member.nickname}님의 권한을 ${getTeamMemberRoleLabel(nextRole)}으로 변경했습니다.`,
      );
    } catch (error) {
      setMemberActionError(error?.message || "회원 권한 변경에 실패했습니다.");
    } finally {
      setRoleUpdatingMemberIds((previous) =>
        previous.filter((id) => id !== memberId),
      );
    }
  };

  const handleExpelMember = async (member) => {
    const memberId = toUserKey(member.userId);
    if (!memberId) {
      return;
    }
    if (!canExpelMember(member)) {
      setMemberActionError("해당 회원을 추방할 권한이 없습니다.");
      return;
    }

    const shouldExpel = window.confirm(
      `${member.nickname}님을 팀에서 추방할까요?`,
    );
    if (!shouldExpel) {
      return;
    }

    setMemberActionError("");
    setMemberActionMessage("");
    setExpellingMemberIds((previous) =>
      previous.includes(memberId) ? previous : [...previous, memberId],
    );

    try {
      await removeTeamMember({
        teamId: activeTeamProfile.id,
        userId: memberId,
      });
      await loadTeamMembers();
      setMemberActionMessage(`${member.nickname}님을 팀에서 추방했습니다.`);
    } catch (error) {
      setMemberActionError(error?.message || "회원 추방에 실패했습니다.");
    } finally {
      setExpellingMemberIds((previous) =>
        previous.filter((id) => id !== memberId),
      );
    }
  };

  const handleDelegateChair = async (member) => {
    const memberId = toUserKey(member.userId);
    if (!memberId) {
      return;
    }
    if (!canDelegateChairToMember(member)) {
      setMemberActionError("회장 위임 권한이 없습니다.");
      return;
    }

    const shouldDelegate = window.confirm(
      `${member.nickname}님에게 회장 권한을 위임할까요? 이 작업은 회장만 수행할 수 있으며 되돌리기 어렵습니다.`,
    );
    if (!shouldDelegate) {
      return;
    }

    setMemberActionError("");
    setMemberActionMessage("");
    setChairDelegatingMemberIds((previous) =>
      previous.includes(memberId) ? previous : [...previous, memberId],
    );

    try {
      await delegateTeamChair({
        teamId: activeTeamProfile.id,
        userId: memberId,
      });
      await loadTeamMembers();
      setMemberActionMessage(
        `${member.nickname}님에게 회장 권한을 위임했습니다.`,
      );

      try {
        const refreshedTeams = onRefreshTeams
          ? await onRefreshTeams()
          : await getMyTeams();
        const activeMembership = refreshedTeams.find(
          (teamItem) => String(teamItem.id) === String(activeTeamProfile.id),
        );
        if (activeMembership?.role) {
          setRole(activeMembership.role);
          localStorage.setItem(teamRoleStorageKey, activeMembership.role);
        }
      } catch (error) {
        // 역할 재조회 실패 시 화면 권한 상태는 기존 값을 유지한다.
      }
    } catch (error) {
      setMemberActionError(error?.message || "회장 위임에 실패했습니다.");
    } finally {
      setChairDelegatingMemberIds((previous) =>
        previous.filter((id) => id !== memberId),
      );
    }
  };

  const applyRecurringScheduleTemplate = (weekdayLabel) => {
    if (!recurringTemplate) {
      return;
    }

    const now = new Date();
    const startTimeText = recurringTemplate.startTime || "19:30";
    const endTimeText = recurringTemplate.endTime || "22:00";
    const targetWeekdayLabel =
      weekdayLabel ||
      getNearestWeekdayLabel(recurringTemplate.weekdays, startTimeText, now);
    if (!targetWeekdayLabel) {
      return;
    }
    const voteCloseHours = DEFAULT_TEMPLATE_VOTE_CLOSE_HOURS;
    const startsAtDate = getNextWeekdayDateTime(
      targetWeekdayLabel,
      startTimeText,
      now,
    );
    const endsAtDate = getNextWeekdayDateTime(
      targetWeekdayLabel,
      endTimeText,
      now,
    );
    const voteEndDate = new Date(
      startsAtDate.getTime() - voteCloseHours * 60 * 60 * 1000,
    );

    if (endsAtDate <= startsAtDate) {
      endsAtDate.setDate(endsAtDate.getDate() + 1);
    }

    const safeVoteEndDate =
      voteEndDate > now
        ? voteEndDate
        : new Date(now.getTime() + 60 * 60 * 1000);

    const fallbackTitle =
      recurringTemplate.category === "MATCH"
        ? `${targetWeekdayLabel} 정기 경기`
        : recurringTemplate.category === "MEETING"
          ? `${targetWeekdayLabel} 정기 미팅`
          : `${targetWeekdayLabel} 정기 훈련`;

    setScheduleForm({
      title: recurringTemplate.title || fallbackTitle,
      category: recurringTemplate.category || "TRAINING",
      venueName: recurringTemplate.venueName || "",
      address: recurringTemplate.address || "",
      detailAddress: recurringTemplate.detailAddress || "",
      latitude: recurringTemplate.latitude || "",
      longitude: recurringTemplate.longitude || "",
      startsAt: formatDateTimeLocal(startsAtDate),
      endsAt: formatDateTimeLocal(endsAtDate),
      voteEndAt: formatDateTimeLocal(safeVoteEndDate),
      description:
        String(recurringTemplate.description || "").trim() ||
        `정기 활동 요일(${
          recurringTemplate.weekdays.length > 0
            ? recurringTemplate.weekdays.join(", ")
            : activeTeamProfile.activityWeekdays.join(", ")
        }) 기준으로 생성된 일정입니다.`,
    });
    setFormError("");
  };

  const handleTemplateWeekdayToggle = (weekdayLabel) => {
    setTemplateFormError("");
    setTemplateForm((previous) => {
      const hasWeekday = previous.weekdays.includes(weekdayLabel);
      const nextWeekdays = hasWeekday
        ? previous.weekdays.filter((day) => day !== weekdayLabel)
        : [...previous.weekdays, weekdayLabel];

      return {
        ...previous,
        weekdays: getSortedWeekdays(nextWeekdays),
      };
    });
  };

  const handleTemplateFormChange = (field, value) => {
    setTemplateFormError("");
    setTemplateForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const handleOpenTemplateCreateModal = () => {
    setEditingTemplateId("");
    setTemplateForm({
      ...buildDefaultTemplateFormState(defaultRecurringTemplate),
      name: getAutoTemplateName(recurringTemplates.length),
    });
    setTemplateFormError("");
    setTemplateFormMessage("");
    setIsTemplateModalOpen(true);
  };

  const handleOpenTemplateEditModal = (template) => {
    const normalizedTemplate = normalizeRecurringTemplate(
      template,
      defaultRecurringTemplate,
    );
    setEditingTemplateId(String(template.id));
    setTemplateForm({
      ...normalizedTemplate,
      id: String(template.id),
      weekdays: [...normalizedTemplate.weekdays],
    });
    setTemplateFormError("");
    setTemplateFormMessage("");
    setIsTemplateModalOpen(true);
  };

  const handleCloseTemplateModal = () => {
    setIsTemplateModalOpen(false);
    setEditingTemplateId("");
    setTemplateForm(buildDefaultTemplateFormState(defaultRecurringTemplate));
    setTemplateFormError("");
  };

  const handleSelectTemplate = (templateId) => {
    const nextSelectedTemplateId = String(templateId);
    selectedRecurringTemplateIdRef.current = nextSelectedTemplateId;
    setSelectedRecurringTemplateId(nextSelectedTemplateId);
    setTemplateFormMessage("기본 적용 템플릿을 변경했습니다.");
    setTemplateFormError("");
  };

  const handleDeleteTemplate = async (templateId) => {
    const shouldDelete = window.confirm("선택한 템플릿을 삭제할까요?");
    if (!shouldDelete) {
      return;
    }

    const deletedTemplateId = String(templateId);

    try {
      await deleteScheduleTemplate({
        teamId: activeTeamProfile.id,
        templateId: deletedTemplateId,
      });

      if (editingTemplateId === deletedTemplateId) {
        handleCloseTemplateModal();
      }

      if (selectedRecurringTemplateId === deletedTemplateId) {
        selectedRecurringTemplateIdRef.current = "";
        setSelectedRecurringTemplateId("");
      }

      await loadRecurringTemplates();
      setTemplateFormMessage("템플릿을 삭제했습니다.");
      setTemplateFormError("");
    } catch (error) {
      setTemplateFormError(error?.message || "템플릿 삭제에 실패했습니다.");
    }
  };

  const handleSaveTemplateForm = async (event) => {
    event.preventDefault();
    setTemplateFormError("");
    setTemplateFormMessage("");

    const validationError = validateTemplateForm(templateForm);
    if (validationError) {
      setTemplateFormError(validationError);
      return;
    }

    const isEditing = Boolean(editingTemplateId);
    const payload = toTemplatePayload(templateForm, activeTeamProfile.id);

    try {
      const responseData = isEditing
        ? await updateScheduleTemplate({
            teamId: activeTeamProfile.id,
            templateId: editingTemplateId,
            payload,
          })
        : await createScheduleTemplate({
            teamId: activeTeamProfile.id,
            payload,
          });

      const createdTemplateId = String(
        responseData?.id ?? responseData?.templateId ?? "",
      );
      if (!isEditing && createdTemplateId) {
        selectedRecurringTemplateIdRef.current = createdTemplateId;
        setSelectedRecurringTemplateId(createdTemplateId);
      }

      await loadRecurringTemplates();
      setIsTemplateModalOpen(false);
      setEditingTemplateId("");
      setTemplateForm(buildDefaultTemplateFormState(defaultRecurringTemplate));
      setTemplateFormMessage(
        isEditing ? "템플릿을 수정했습니다." : "템플릿을 추가했습니다.",
      );
    } catch (error) {
      setTemplateFormError(error?.message || "템플릿 저장에 실패했습니다.");
    }
  };

  const handleOpenLineupEditor = async (scheduleId) => {
    if (!hasLineupManagePermission) {
      return;
    }

    setLineupErrorMessage("");

    let availableMembers = teamMembers;
    if (availableMembers.length === 0) {
      const loadedMembers = await loadTeamMembers();
      if (Array.isArray(loadedMembers) && loadedMembers.length > 0) {
        availableMembers = loadedMembers;
      }
    }

    const scheduleKey = String(scheduleId);
    const selectedSchedule = schedules.find(
      (schedule) => String(schedule.id) === scheduleKey,
    );
    const votesByUserId = selectedSchedule?.votes || {};
    const allMemberPlayers = availableMembers
      .map((member, index) => {
        const userId = toUserKey(member.userId);
        if (!userId) {
          return null;
        }
        const voteType = votesByUserId[userId] || null;
        return {
          id: userId,
          name:
            member.nickname ||
            getMemberDisplayName(
              userId,
              currentUser,
              currentUserId,
              memberNameByUserId,
            ),
          voteType,
          order: index,
        };
      })
      .filter((player) => player && player.voteType !== "ABSENT")
      .filter(Boolean)
      .sort((a, b) => {
        const resolveVotePriority = (voteType) => {
          if (voteType === "ATTEND") {
            return 0;
          }
          if (!voteType) {
            return 1;
          }
          return 2;
        };

        const aPriority = resolveVotePriority(a.voteType);
        const bPriority = resolveVotePriority(b.voteType);
        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }
        return a.order - b.order;
      });

    const attendeePlayerIds = new Set(
      allMemberPlayers
        .filter((player) => player.voteType === "ATTEND")
        .map((player) => player.id),
    );
    const lineupBasePlayers = createLineupPlayersFromPool(
      allMemberPlayers.map((player) => ({
        id: player.id,
        name: player.name,
      })),
    ).map((player) => ({
      ...player,
      active: attendeePlayerIds.has(player.id),
    }));

    let savedLineup = savedLineups[scheduleKey];
    if (!savedLineup) {
      savedLineup = await loadLineupForSchedule(scheduleId);
    }

    const sourceSegments =
      savedLineup?.segments ||
      (savedLineup?.players ? { 1: savedLineup.players } : {});
    const defaultCount = savedLineup?.players
      ? 1
      : DEFAULT_LINEUP_SEGMENT_COUNT;
    const segmentCount = normalizeSegmentCount(
      savedLineup?.segmentCount ?? defaultCount,
    );
    const initialSegments = createLineupSegments(
      segmentCount,
      sourceSegments,
      lineupBasePlayers,
    );
    const initialCustomPlayers = collectCustomLineupPlayersFromSegments(
      initialSegments,
      segmentCount,
    );

    setLineupDraft({
      scheduleId,
      segmentCount,
      selectedSegment: "1",
      basePlayers: lineupBasePlayers,
      segments: appendCustomPlayersToSegments(
        initialSegments,
        segmentCount,
        initialCustomPlayers,
      ),
    });
    setLineupCustomPlayerName("");
  };

  const handleCloseLineupEditor = () => {
    setLineupDraft((previous) => ({
      ...previous,
      scheduleId: null,
    }));
    setLineupCustomPlayerName("");
  };

  const handleToggleLineupPlayerActive = (playerId) => {
    setLineupDraft((previous) => {
      const segmentKey = previous.selectedSegment;
      const basePlayers = Array.isArray(previous.basePlayers)
        ? previous.basePlayers
        : createDefaultLineupPlayers();
      const currentSegmentPlayers =
        previous.segments[segmentKey] || basePlayers;
      return {
        ...previous,
        segments: {
          ...previous.segments,
          [segmentKey]: currentSegmentPlayers.map((player) =>
            player.id === playerId
              ? { ...player, active: !player.active }
              : player,
          ),
        },
      };
    });
  };

  const handleLineupPlayerDragStart = (event, playerId) => {
    event.dataTransfer.setData("text/plain", playerId);
  };

  const moveLineupPlayerToClientPosition = useCallback(
    (playerId, clientX, clientY) => {
      const pitchElement = lineupPitchRef.current;
      if (!pitchElement) {
        return;
      }

      const pitchRect = pitchElement.getBoundingClientRect();
      const nextX = ((clientX - pitchRect.left) / pitchRect.width) * 100;
      const nextY = ((clientY - pitchRect.top) / pitchRect.height) * 100;

      setLineupDraft((previous) => {
        const segmentKey = previous.selectedSegment;
        const basePlayers = Array.isArray(previous.basePlayers)
          ? previous.basePlayers
          : createDefaultLineupPlayers();
        const currentSegmentPlayers =
          previous.segments[segmentKey] || basePlayers;
        return {
          ...previous,
          segments: {
            ...previous.segments,
            [segmentKey]: currentSegmentPlayers.map((player) =>
              player.id === playerId
                ? {
                    ...player,
                    active: true,
                    x: clampPitchPercent(nextX),
                    y: clampPitchPercent(nextY),
                  }
                : player,
            ),
          },
        };
      });
    },
    [],
  );

  const handleLineupPlayerTouchPointerDown = (event, playerId) => {
    if (!hasLineupManagePermission || event.pointerType !== "touch") {
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    lineupTouchDraggingPlayerIdRef.current = String(playerId);
    if (lineupTouchLockedBodyOverflowRef.current === "") {
      lineupTouchLockedBodyOverflowRef.current = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    }

    if (event.currentTarget.setPointerCapture) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (error) {
        // 일부 브라우저는 setPointerCapture를 지원하지 않는다.
      }
    }

    moveLineupPlayerToClientPosition(
      String(playerId),
      event.clientX,
      event.clientY,
    );
  };

  const handleLineupPlayerTouchPointerMove = (event) => {
    if (event.pointerType !== "touch") {
      return;
    }

    const draggingPlayerId = lineupTouchDraggingPlayerIdRef.current;
    if (!draggingPlayerId) {
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    moveLineupPlayerToClientPosition(
      draggingPlayerId,
      event.clientX,
      event.clientY,
    );
  };

  const handleLineupPlayerTouchPointerEnd = (event) => {
    if (event.pointerType !== "touch") {
      return;
    }

    if (!lineupTouchDraggingPlayerIdRef.current) {
      return;
    }

    lineupTouchDraggingPlayerIdRef.current = "";
    if (lineupTouchLockedBodyOverflowRef.current !== "") {
      document.body.style.overflow = lineupTouchLockedBodyOverflowRef.current;
      lineupTouchLockedBodyOverflowRef.current = "";
    }

    if (event.currentTarget.releasePointerCapture) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch (error) {
        // pointer capture 미적용 상태면 무시한다.
      }
    }
  };

  const handleLineupPitchDrop = (event) => {
    event.preventDefault();

    const draggedPlayerId = event.dataTransfer.getData("text/plain");
    if (!draggedPlayerId) {
      return;
    }

    const pitchRect = event.currentTarget.getBoundingClientRect();
    const nextX = ((event.clientX - pitchRect.left) / pitchRect.width) * 100;
    const nextY = ((event.clientY - pitchRect.top) / pitchRect.height) * 100;

    setLineupDraft((previous) => {
      const segmentKey = previous.selectedSegment;
      const basePlayers = Array.isArray(previous.basePlayers)
        ? previous.basePlayers
        : createDefaultLineupPlayers();
      const currentSegmentPlayers =
        previous.segments[segmentKey] || basePlayers;
      return {
        ...previous,
        segments: {
          ...previous.segments,
          [segmentKey]: currentSegmentPlayers.map((player) =>
            player.id === draggedPlayerId
              ? {
                  ...player,
                  active: true,
                  x: clampPitchPercent(nextX),
                  y: clampPitchPercent(nextY),
                }
              : player,
          ),
        },
      };
    });
  };

  const handleResetLineup = () => {
    setLineupDraft((previous) => {
      const segmentKey = previous.selectedSegment;
      const basePlayers = Array.isArray(previous.basePlayers)
        ? previous.basePlayers
        : createDefaultLineupPlayers();
      const currentSegmentPlayers = previous.segments[segmentKey] || [];
      const customPlayers = currentSegmentPlayers
        .filter((player) => isCustomLineupPlayerId(player.id))
        .map((player, index) =>
          normalizeLineupPlayer(player, `custom-${index + 1}`),
        )
        .map((player) => ({
          ...player,
          active: false,
        }));
      return {
        ...previous,
        segments: {
          ...previous.segments,
          [segmentKey]: [...basePlayers, ...customPlayers],
        },
      };
    });
  };

  const handleSelectLineupSegment = (segmentKey) => {
    setLineupDraft((previous) => ({
      ...previous,
      selectedSegment: segmentKey,
    }));
  };

  const handleSelectLineupPreviewSegment = (scheduleId, segmentKey) => {
    setLineupPreviewSegments((previous) => ({
      ...previous,
      [scheduleId]: segmentKey,
    }));
  };

  const handleLineupCustomPlayerNameChange = (event) => {
    setLineupCustomPlayerName(event.target.value);
  };

  const handleAddLineupCustomPlayer = () => {
    const trimmedName = lineupCustomPlayerName.trim();
    if (!trimmedName) {
      setLineupErrorMessage("추가할 선수 이름을 입력해 주세요.");
      return;
    }

    setLineupDraft((previous) => {
      const currentSegmentPlayers =
        previous.segments[previous.selectedSegment] || [];
      const initialPosition = getNextLineupAutoPosition(currentSegmentPlayers);
      const newCustomPlayer = createCustomLineupPlayer(
        trimmedName,
        initialPosition,
      );
      const baseCustomPlayers = collectCustomLineupPlayersFromSegments(
        previous.segments,
        previous.segmentCount,
      );
      const nextCustomPlayers = [...baseCustomPlayers, newCustomPlayer];
      const nextSegments = appendCustomPlayersToSegments(
        previous.segments,
        previous.segmentCount,
        nextCustomPlayers,
      );
      const selectedSegmentPlayers =
        nextSegments[previous.selectedSegment] || [];

      return {
        ...previous,
        segments: {
          ...nextSegments,
          [previous.selectedSegment]: selectedSegmentPlayers.map((player) =>
            player.id === newCustomPlayer.id
              ? {
                  ...player,
                  active: true,
                }
              : player,
          ),
        },
      };
    });
    setLineupCustomPlayerName("");
    setLineupErrorMessage("");
  };

  const handleRemoveLineupCustomPlayer = (playerId) => {
    setLineupDraft((previous) => {
      const nextSegments = {};
      Object.entries(previous.segments).forEach(([segmentKey, players]) => {
        nextSegments[segmentKey] = (players || []).filter(
          (player) => player.id !== playerId,
        );
      });
      return {
        ...previous,
        segments: nextSegments,
      };
    });
  };

  const handleLineupSegmentCountChange = (nextCountValue) => {
    setLineupDraft((previous) => {
      const nextSegmentCount = normalizeSegmentCount(nextCountValue);
      const basePlayers = Array.isArray(previous.basePlayers)
        ? previous.basePlayers
        : createDefaultLineupPlayers();
      const customPlayers = collectCustomLineupPlayersFromSegments(
        previous.segments,
        previous.segmentCount,
      );
      const nextSegments = appendCustomPlayersToSegments(
        createLineupSegments(nextSegmentCount, previous.segments, basePlayers),
        nextSegmentCount,
        customPlayers,
      );
      const selectedSegmentNumber = Number.parseInt(
        previous.selectedSegment,
        10,
      );
      const nextSelectedSegment =
        selectedSegmentNumber > nextSegmentCount
          ? String(nextSegmentCount)
          : previous.selectedSegment;

      return {
        ...previous,
        segmentCount: nextSegmentCount,
        selectedSegment: nextSelectedSegment,
        segments: nextSegments,
      };
    });
  };

  const handleSaveLineup = async () => {
    // 라인업 저장은 현재 편집중 draft를 API payload로 직렬화해서 전송한다.
    if (!lineupDraft.scheduleId || !hasLineupManagePermission) {
      return;
    }
    if (!activeTeamProfile.id) {
      return;
    }

    const scheduleKey = String(lineupDraft.scheduleId);
    const payloadSegments = {};
    for (
      let segmentIndex = 1;
      segmentIndex <= lineupDraft.segmentCount;
      segmentIndex += 1
    ) {
      const segmentKey = String(segmentIndex);
      payloadSegments[segmentKey] = (
        lineupDraft.segments[segmentKey] || []
      ).map((player) => ({
        playerId: String(player.id),
        name: player.name,
        x: player.x,
        y: player.y,
        active: Boolean(player.active),
      }));
    }

    setLineupErrorMessage("");
    setLineupSavePending(true);

    try {
      const response = await saveScheduleLineup({
        teamId: activeTeamProfile.id,
        scheduleId: lineupDraft.scheduleId,
        payload: {
          segmentCount: lineupDraft.segmentCount,
          segments: payloadSegments,
        },
      });
      const savedLineup = normalizeLineupResponseItem(response) || {
        segmentCount: lineupDraft.segmentCount,
        segments: cloneLineupSegments(lineupDraft.segments),
      };

      setSavedLineups((previous) => ({
        ...previous,
        [scheduleKey]: savedLineup,
      }));
      setLineupLoadStatusBySchedule((previous) => ({
        ...previous,
        [scheduleKey]: {
          loading: false,
          error: "",
        },
      }));
      handleCloseLineupEditor();
    } catch (error) {
      setLineupErrorMessage(error?.message || "라인업 저장에 실패했습니다.");
    } finally {
      setLineupSavePending(false);
    }
  };

  return (
    <main
      className="min-h-screen px-6 py-8 md:px-10 md:py-10"
      style={{
        background:
          "radial-gradient(circle at 8% 0%, #dcfce7 0, transparent 30%), radial-gradient(circle at 100% 10%, #dbeafe 0, transparent 34%), linear-gradient(160deg, #f8fafc 0%, #eef2ff 100%)",
      }}
    >
      <div className="mx-auto flex w-full max-w-[1180px] flex-col gap-6">
        <header className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_16px_40px_rgba(15,23,42,0.08)] backdrop-blur-sm">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="size-16 min-h-16 min-w-16 shrink-0 overflow-hidden rounded-2xl border border-[#d0d5dd] bg-[#f8fafc] shadow-sm">
                <img
                  src={
                    appendCacheKeyToImageUrl(
                      activeTeamProfile.logoUrl,
                      activeTeamProfile.updatedAt || teamLogoCacheKey,
                    ) || DEFAULT_TEAM_LOGO_URL
                  }
                  alt={`${activeTeamProfile.name} 로고`}
                  onError={(event) => {
                    event.currentTarget.src = DEFAULT_TEAM_LOGO_URL;
                  }}
                  className="h-full w-full object-cover"
                />
              </div>

              <div>
                <p className="m-0 inline-block rounded-full bg-[#111827] px-3 py-1 text-xs font-bold tracking-[0.08em] text-white">
                  TEAM WORKSPACE
                </p>
                <h1 className="mt-3 text-[clamp(26px,4.5vw,36px)] font-semibold leading-tight tracking-[-0.03em] text-[#0f172a]">
                  {activeTeamProfile.name}
                </h1>
                <div className="mt-2 space-y-1 text-sm text-[#475467]">
                  <p className="m-0">회장 : {activeTeamProfile.chair || "-"}</p>
                  <p className="m-0">주 활동 : {teamActivityWeekdaysLabel}</p>
                  <p className="m-0">주 시간 : {teamActivityTimeRangeLabel}</p>
                  <p className="m-0">평균연령 : {activeTeamProfile.ageBand || "-"}</p>
                  <p className="m-0">
                    성별 :{" "}
                    {activeTeamProfile.genderType === "MALE"
                      ? "남성"
                      : activeTeamProfile.genderType === "FEMALE"
                        ? "여성"
                        : activeTeamProfile.genderType === "MIXED"
                          ? "혼성"
                          : activeTeamProfile.genderType || "-"}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {myTeams.length > 1 && (
                <select
                  id="team-switcher"
                  value={activeTeamProfile.id}
                  onChange={(event) => {
                    if (onSelectTeam) {
                      onSelectTeam(event.target.value);
                    }
                  }}
                  className="h-10 rounded-lg border border-[#d0d5dd] bg-white px-3 text-sm font-semibold text-[#344054] outline-none"
                >
                  {myTeams.map((teamOption) => (
                    <option key={teamOption.id} value={teamOption.id}>
                      {teamOption.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </header>

        <section className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-4 shadow-[0_12px_30px_rgba(15,23,42,0.06)] md:p-5">
          <div className="flex flex-wrap gap-2">
            {visibleTabItems.map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`h-10 rounded-xl px-4 text-sm font-semibold transition ${
                    isActive
                      ? "bg-[#111827] text-white"
                      : "border border-[#d0d5dd] bg-white text-[#344054] hover:bg-[#f8fafc]"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </section>

        {activeTab === "team" && (
          <section className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="m-0 text-lg font-semibold text-[#0f172a]">
                  팀관리
                </h2>
                <p className="mt-1 text-sm text-[#667085]">
                  팀 기본 정보와 로고를 관리합니다. 회장 정보는 위임 기능으로만
                  변경됩니다.
                </p>
              </div>
            </div>

            {teamManageMessage && (
              <p className="mt-4 rounded-lg border border-[#abefc6] bg-[#ecfdf3] px-3 py-2 text-sm text-[#027a48]">
                {teamManageMessage}
              </p>
            )}

            {teamManageError && (
              <p className="mt-4 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
                {teamManageError}
              </p>
            )}

            <form
              className="mt-5 grid gap-4 md:grid-cols-2"
              onSubmit={handleSaveTeamProfile}
            >
              <div className="md:col-span-2">
                <p className="mb-2 block text-sm font-semibold text-[#344054]">
                  팀 로고
                </p>
                <div className="flex flex-wrap items-center gap-4 rounded-xl border border-[#eaecf0] bg-[#f8fafc] p-3">
                  <div className="size-20 min-h-20 min-w-20 shrink-0 overflow-hidden rounded-xl border border-[#d0d5dd] bg-white">
                    <img
                      src={
                        teamLogoPreviewUrl ||
                        appendCacheKeyToImageUrl(
                          teamManageForm.logoUrl,
                          activeTeamProfile.updatedAt || teamLogoCacheKey,
                        ) ||
                        DEFAULT_TEAM_LOGO_URL
                      }
                      alt={`${teamManageForm.name || activeTeamProfile.name} 로고`}
                      onError={(event) => {
                        event.currentTarget.src = DEFAULT_TEAM_LOGO_URL;
                      }}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <input
                      ref={teamLogoFileInputRef}
                      id="team-manage-logo-file"
                      type="file"
                      accept="image/*"
                      onChange={handleTeamLogoFileChange}
                      className="h-10 w-full rounded-lg border border-[#d0d5dd] bg-white px-3 py-2 text-sm outline-none file:mr-3 file:rounded-md file:border-0 file:bg-[#111827] file:px-3 file:py-1 file:text-xs file:font-semibold file:text-white"
                    />
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={handleResetTeamLogoToDefault}
                        disabled={isSavingTeamProfile || isTeamLogoUploading}
                        className="h-8 rounded-md border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054] transition hover:bg-[#f2f4f7] disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        기본 로고로 되돌리기
                      </button>
                      <p className="m-0 text-xs text-[#667085]">
                        저장 시 적용됩니다.
                      </p>
                    </div>
                    {teamLogoFile && (
                      <p className="mt-1 text-xs font-medium text-[#344054]">
                        선택 파일: {teamLogoFile.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label
                  className="mb-2 block text-sm font-semibold text-[#344054]"
                  htmlFor="team-manage-name"
                >
                  팀 이름
                </label>
                <input
                  id="team-manage-name"
                  type="text"
                  value={teamManageForm.name}
                  onChange={(event) =>
                    handleTeamManageFieldChange("name", event.target.value)
                  }
                  className="h-11 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                />
              </div>

              <div>
                <label
                  className="mb-2 block text-sm font-semibold text-[#344054]"
                  htmlFor="team-manage-chair"
                >
                  회장
                </label>
                <div
                  id="team-manage-chair"
                  className="flex h-11 items-center rounded-lg border border-[#d0d5dd] bg-[#f9fafb] px-3 text-sm font-semibold text-[#344054]"
                >
                  {teamManageForm.chairName || "-"}
                </div>
                <p className="mt-1 text-xs text-[#667085]">
                  회장 변경은 회원 메뉴의 위임 기능으로만 가능합니다.
                </p>
              </div>

              <div>
                <label
                  className="mb-2 block text-sm font-semibold text-[#344054]"
                  htmlFor="team-manage-age-band"
                >
                  평균 연령대
                </label>
                <select
                  id="team-manage-age-band"
                  value={teamManageForm.ageBand}
                  onChange={(event) =>
                    handleTeamManageFieldChange("ageBand", event.target.value)
                  }
                  className="h-11 w-full rounded-lg border border-[#d0d5dd] bg-white px-3 text-sm outline-none focus:border-[#667085]"
                >
                  {TEAM_AGE_BAND_OPTIONS.map((option) => (
                    <option key={`team-manage-age-${option}`} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <p className="mb-2 block text-sm font-semibold text-[#344054]">
                  성별 타입
                </p>
                <div className="flex flex-wrap gap-2">
                  {TEAM_GENDER_TYPE_OPTIONS.map((option) => {
                    const isSelected =
                      teamManageForm.genderType === option.value;
                    return (
                      <button
                        key={`team-manage-gender-${option.value}`}
                        type="button"
                        onClick={() =>
                          handleTeamManageFieldChange(
                            "genderType",
                            option.value,
                          )
                        }
                        className={`h-9 rounded-lg px-3 text-sm font-semibold ${
                          isSelected
                            ? "bg-[#111827] text-white"
                            : "border border-[#d0d5dd] bg-white text-[#344054]"
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="min-w-0 overflow-hidden">
                <label
                  className="mb-2 block text-sm font-semibold text-[#344054]"
                  htmlFor="team-manage-start-time"
                >
                  시작 시간
                </label>
                <input
                  id="team-manage-start-time"
                  type="time"
                  value={teamManageForm.startTime}
                  onChange={(event) =>
                    handleTeamManageFieldChange("startTime", event.target.value)
                  }
                  className="h-11 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                />
              </div>

              <div className="min-w-0 overflow-hidden">
                <label
                  className="mb-2 block text-sm font-semibold text-[#344054]"
                  htmlFor="team-manage-end-time"
                >
                  종료 시간
                </label>
                <input
                  id="team-manage-end-time"
                  type="time"
                  value={teamManageForm.endTime}
                  onChange={(event) =>
                    handleTeamManageFieldChange("endTime", event.target.value)
                  }
                  className="h-11 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                />
              </div>

              <div className="md:col-span-2">
                <p className="mb-2 text-sm font-semibold text-[#344054]">
                  주 활동 요일
                </p>
                <div className="flex flex-wrap gap-2">
                  {DAY_NAMES.map((weekday) => {
                    const selected =
                      teamManageForm.activityWeekdays.includes(weekday);
                    return (
                      <button
                        key={`team-manage-weekday-${weekday}`}
                        type="button"
                        onClick={() => handleTeamManageWeekdayToggle(weekday)}
                        className={`h-9 rounded-lg px-3 text-sm font-semibold ${
                          selected
                            ? "bg-[#111827] text-white"
                            : "border border-[#d0d5dd] bg-white text-[#344054]"
                        }`}
                      >
                        {weekday}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="md:col-span-2">
                <label
                  className="mb-2 block text-sm font-semibold text-[#344054]"
                  htmlFor="team-manage-description"
                >
                  팀 소개
                </label>
                <textarea
                  id="team-manage-description"
                  value={teamManageForm.description}
                  onChange={(event) =>
                    handleTeamManageFieldChange(
                      "description",
                      event.target.value,
                    )
                  }
                  className="min-h-[110px] w-full rounded-lg border border-[#d0d5dd] px-3 py-2 text-sm outline-none focus:border-[#667085]"
                  placeholder="팀 스타일, 운영 방식, 참여 규칙 등을 입력해 주세요."
                />
              </div>

              <div className="md:col-span-2 flex flex-wrap gap-2">
                <button
                  type="submit"
                  disabled={isSavingTeamProfile || isTeamLogoUploading}
                  className="h-11 rounded-lg bg-[#111827] px-5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#98a2b3]"
                >
                  {isSavingTeamProfile || isTeamLogoUploading
                    ? "저장 중..."
                    : "팀 정보 저장"}
                </button>
              </div>

              <div className="md:col-span-2 mt-2 rounded-2xl border border-[#fecdca] bg-[#fef3f2] p-4">
                <p className="m-0 text-sm font-semibold text-[#b42318]">
                  위험 작업
                </p>
                <p className="mt-1 text-xs text-[#b42318]">
                  팀 폐쇄 시 팀/일정/회원 데이터가 복구되지 않을 수 있습니다.
                </p>
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={handleOpenCloseTeamModal}
                    disabled={!isChair || isClosingTeam}
                    className="h-10 rounded-lg border border-[#fecdca] bg-white px-4 text-sm font-semibold text-[#b42318] transition hover:border-[#fda29b] hover:bg-[#fee4e2] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isClosingTeam ? "폐쇄 중..." : "팀 폐쇄"}
                  </button>
                </div>
              </div>
            </form>
          </section>
        )}

        {activeTab === "upcoming" && (
          <section>
            <article className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="m-0 text-lg font-semibold text-[#0f172a]">
                    다가오는 일정
                  </h2>
                  <p className="mt-1 text-sm text-[#667085]">
                    일정별로 참여/불참 투표가 가능하며, 투표 마감 시간은 운영진이
                    설정합니다.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleRefreshUpcoming}
                  disabled={isUpcomingLoading || isLoadingMoreUpcoming}
                  className="h-9 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isUpcomingLoading || isLoadingMoreUpcoming
                    ? "불러오는 중..."
                    : "목록 새로고침"}
                </button>
              </div>
              {sortedUpcomingSchedules.length > 0 && !isUpcomingLoading && (
                <p className="mt-2 text-xs font-medium text-[#667085]">
                  현재 {sortedUpcomingSchedules.length}건 불러옴
                </p>
              )}

              <div className="mt-4 space-y-3">
                {isUpcomingLoading && (
                  <div className="rounded-xl border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-4 py-5 text-sm text-[#667085]">
                    다가오는 일정을 불러오는 중입니다...
                  </div>
                )}

                {!isUpcomingLoading &&
                  upcomingLoadError &&
                  sortedUpcomingSchedules.length === 0 && (
                    <div className="rounded-xl border border-[#fecdca] bg-[#fef3f2] px-4 py-5 text-sm text-[#b42318]">
                      <p className="m-0">{upcomingLoadError}</p>
                      <button
                        type="button"
                        onClick={() =>
                          loadUpcomingSchedulesPage(0, { append: false })
                        }
                        className="mt-3 h-9 rounded-lg border border-[#fecdca] bg-white px-3 text-sm font-semibold text-[#b42318]"
                      >
                        다시 불러오기
                      </button>
                    </div>
                  )}

                {!isUpcomingLoading &&
                  !upcomingLoadError &&
                  sortedUpcomingSchedules.length === 0 && (
                    <div className="rounded-xl border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-4 py-5 text-sm text-[#667085]">
                      등록된 일정이 없습니다.
                    </div>
                  )}

                {sortedUpcomingSchedules.map((schedule) => (
                  <ScheduleVoteCard
                    key={schedule.id}
                    schedule={schedule}
                    currentUserId={currentUserId}
                    onOpenDetail={handleOpenScheduleDetail}
                    onVote={handleVote}
                    isVoting={votingScheduleIds.includes(String(schedule.id))}
                  />
                ))}

                {voteErrorMessage && (
                  <p className="rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-xs text-[#b42318]">
                    {voteErrorMessage}
                  </p>
                )}

                {hasMoreUpcomingSchedules && (
                  <div className="pt-1 text-center">
                    <button
                      type="button"
                      onClick={handleShowMoreUpcoming}
                      disabled={isUpcomingLoading || isLoadingMoreUpcoming}
                      className="h-10 rounded-lg border border-[#d0d5dd] bg-white px-4 text-sm font-semibold text-[#344054] hover:bg-[#f8fafc]"
                    >
                      {isLoadingMoreUpcoming
                        ? "불러오는 중..."
                        : `일정 더 보기 (+${UPCOMING_PAGE_SIZE})`}
                    </button>
                    {!isUpcomingLoading &&
                      upcomingLoadError &&
                      sortedUpcomingSchedules.length > 0 && (
                        <p className="mt-2 text-xs text-[#b42318]">
                          {upcomingLoadError}
                        </p>
                      )}
                  </div>
                )}
              </div>
            </article>
          </section>
        )}

        {activeTab === "calendar" && (
          <section className="grid gap-5 lg:grid-cols-12">
            <article className="lg:col-span-8 rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="m-0 text-lg font-semibold text-[#0f172a]">
                  팀 일정 캘린더
                </h2>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleRefreshUpcoming}
                    disabled={isUpcomingLoading || isLoadingMoreUpcoming}
                    className="h-8 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isUpcomingLoading || isLoadingMoreUpcoming
                      ? "불러오는 중..."
                      : "목록 새로고침"}
                  </button>
                  <button
                    type="button"
                    aria-label="이전 달"
                    onClick={() =>
                      setCalendarMonth(
                        (previous) =>
                          new Date(
                            previous.getFullYear(),
                            previous.getMonth() - 1,
                            1,
                          ),
                      )
                    }
                    className="h-8 rounded-lg border border-[#d0d5dd] bg-white px-3 text-sm text-[#344054]"
                  >
                    ‹
                  </button>
                  <strong className="text-sm text-[#344054]">
                    {formatMonthLabel(calendarMonth)}
                  </strong>
                  <button
                    type="button"
                    aria-label="다음 달"
                    onClick={() =>
                      setCalendarMonth(
                        (previous) =>
                          new Date(
                            previous.getFullYear(),
                            previous.getMonth() + 1,
                            1,
                          ),
                      )
                    }
                    className="h-8 rounded-lg border border-[#d0d5dd] bg-white px-3 text-sm text-[#344054]"
                  >
                    ›
                  </button>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-7 gap-2">
                {DAY_NAMES.map((dayName) => (
                  <div
                    key={dayName}
                    className="rounded-lg bg-[#f2f4f7] py-2 text-center text-xs font-semibold text-[#475467]"
                  >
                    {dayName}
                  </div>
                ))}

                {calendarCells.map((dateValue) => {
                  const dateKey = toDateKey(dateValue);
                  const scheduleCount = scheduleCountByDate[dateKey] || 0;
                  const isCurrentMonth =
                    dateValue.getMonth() === calendarMonth.getMonth();
                  const isSelected = selectedDateKey === dateKey;
                  return (
                    <button
                      key={`${dateKey}-${dateValue.getMonth()}`}
                      type="button"
                      onClick={() => setSelectedDateKey(dateKey)}
                      className={`min-h-[56px] rounded-xl border p-1.5 text-left transition md:min-h-[64px] md:p-2 flex flex-col items-center justify-between ${
                        isSelected
                          ? "border-[#3538cd] bg-[#eef4ff]"
                          : "border-[#eaecf0] bg-white hover:bg-[#f9fafb]"
                      }`}
                    >
                      <span
                        className={`text-sm font-semibold leading-none ${isCurrentMonth ? "text-[#101828]" : "text-[#98a2b3]"}`}
                      >
                        {dateValue.getDate()}
                      </span>
                      {scheduleCount > 0 && (
                        <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#111827] px-1 text-[11px] font-bold text-white">
                          {scheduleCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </article>

            <article className="lg:col-span-4 rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
              <h3 className="m-0 text-base font-semibold text-[#0f172a]">
                {selectedDateKey
                  ? `${formatDateOnly(selectedDateKey)} 일정`
                  : "날짜를 선택해 주세요"}
              </h3>
              <div className="mt-4 space-y-3">
                {selectedDateKey && selectedDateSchedules.length === 0 && (
                  <p className="rounded-xl bg-[#f8fafc] px-3 py-3 text-sm text-[#667085]">
                    선택한 날짜에 등록된 일정이 없습니다.
                  </p>
                )}

                {!selectedDateKey && (
                  <p className="rounded-xl bg-[#f8fafc] px-3 py-3 text-sm text-[#667085]">
                    캘린더에서 일정이 있는 날짜를 선택하면 상세 내용을 볼 수
                    있습니다.
                  </p>
                )}

                {selectedDateSchedules.map((schedule) => (
                  <ScheduleVoteCard
                    key={schedule.id}
                    schedule={schedule}
                    currentUserId={currentUserId}
                    onOpenDetail={handleOpenScheduleDetail}
                    onVote={handleVote}
                    isVoting={votingScheduleIds.includes(String(schedule.id))}
                  />
                ))}
                {voteErrorMessage && (
                  <p className="rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-xs text-[#b42318]">
                    {voteErrorMessage}
                  </p>
                )}
              </div>
            </article>
          </section>
        )}

        {activeTab === "manage" && (
          <section>
            <article className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="m-0 text-lg font-semibold text-[#0f172a]">
                    일정 관리
                  </h2>
                  <p className="mt-1 text-sm text-[#667085]">
                    등록된 일정을 조회하고 수정/삭제할 수 있습니다.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleOpenCreateScheduleModal}
                  className="h-9 rounded-lg bg-[#111827] px-3 text-xs font-semibold text-white"
                >
                  일정 등록
                </button>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {MANAGE_FILTER_OPTIONS.map((option) => {
                  const isSelected = manageFilter === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setManageFilter(option.value)}
                      className={`h-8 rounded-md px-3 text-xs font-semibold ${
                        isSelected
                          ? "bg-[#111827] text-white"
                          : "border border-[#d0d5dd] bg-white text-[#344054]"
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>

              {manageFormMessage && (
                <p className="mt-3 rounded-lg border border-[#abefc6] bg-[#ecfdf3] px-3 py-2 text-sm text-[#027a48]">
                  {manageFormMessage}
                </p>
              )}
              {manageFormError && !editingScheduleId && (
                <p className="mt-3 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
                  {manageFormError}
                </p>
              )}

              <div className="mt-4 space-y-3">
                {isManageLoading && (
                  <div className="rounded-xl border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-4 py-5 text-sm text-[#667085]">
                    일정 목록을 불러오는 중입니다...
                  </div>
                )}

                {!isManageLoading && manageLoadError && (
                  <div className="rounded-xl border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318]">
                    {manageLoadError}
                  </div>
                )}

                {!isManageLoading &&
                  !manageLoadError &&
                  filteredManageSchedules.length === 0 && (
                    <div className="rounded-xl border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-4 py-5 text-sm text-[#667085]">
                      조건에 맞는 일정이 없습니다.
                    </div>
                  )}

                {!isManageLoading &&
                  !manageLoadError &&
                  filteredManageSchedules.map((schedule) => {
                    const scheduleKey = String(schedule.id);
                    const isEditing = editingScheduleId === scheduleKey;
                    const isDeleting =
                      deletingScheduleIds.includes(scheduleKey);

                    return (
                      <article
                        key={`manage-schedule-${scheduleKey}`}
                        className={`rounded-2xl border px-4 py-4 transition ${
                          isEditing
                            ? "border-[#3538cd] bg-[#eef4ff]"
                            : "border-[#eaecf0] bg-white"
                        }`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="m-0 text-xs font-semibold tracking-wide text-[#475467]">
                              {schedule.category}
                            </p>
                            <button
                              type="button"
                              onClick={() =>
                                handleOpenScheduleDetail(schedule.id)
                              }
                              className="mt-1 p-0 text-left text-base font-semibold text-[#101828] underline-offset-2 hover:underline"
                            >
                              {schedule.title}
                            </button>
                            <div className="mt-2 space-y-1 text-sm text-[#475467]">
                              <p className="m-0">
                                일시 : {formatDateTime(schedule.startsAt)} ~{" "}
                                {formatDateTime(schedule.endsAt)}
                              </p>
                              <p className="m-0">
                                장소 : {schedule.venueName || "-"}
                              </p>
                              <p className="m-0">
                                주소 : {schedule.address || "-"}
                                {schedule.detailAddress
                                  ? ` (${schedule.detailAddress})`
                                  : ""}
                              </p>
                              <p className="m-0 text-xs text-[#667085]">
                                투표 마감 : {formatDateTime(schedule.voteEndAt)}
                                까지
                              </p>
                            </div>
                          </div>

                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleStartEditSchedule(schedule)}
                              className="h-9 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054] hover:bg-[#f9fafb]"
                            >
                              수정
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                handleDeleteManagedSchedule(schedule)
                              }
                              disabled={isDeleting}
                              className="h-9 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 text-xs font-semibold text-[#b42318] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {isDeleting ? "삭제 중..." : "삭제"}
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
              </div>
            </article>

            {isManageCreateModalOpen && (
              <div className="fixed inset-0 z-50">
                <div
                  className="absolute inset-0 bg-[#0f172a]/55 backdrop-blur-[1px]"
                  onClick={handleCloseCreateScheduleModal}
                  aria-hidden="true"
                />
                <div
                  className="relative z-10 h-full overflow-y-auto px-4 py-8 [overscroll-behavior:contain] [webkit-overflow-scrolling:touch]"
                  style={{ touchAction: "pan-y" }}
                >
                  <section className="mx-auto w-full max-w-[860px] rounded-3xl border border-[#dbe4f4] bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.3)] md:p-6">
                    <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="m-0 text-xs font-semibold tracking-wide text-[#475467]">
                        일정 등록
                      </p>
                      <h3 className="mt-1 text-xl font-semibold text-[#0f172a]">
                        새 일정 추가
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={handleCloseCreateScheduleModal}
                      aria-label="일정 등록 모달 닫기"
                      className="grid h-9 w-9 place-items-center rounded-lg border border-[#d0d5dd] bg-white text-base font-semibold text-[#344054]"
                    >
                      X
                    </button>
                  </div>

                    <div className="mt-4 rounded-xl border border-[#dbe4f4] bg-[#f8fafc] px-4 py-3">
                    <p className="text-sm font-semibold text-[#344054]">
                      정기 일정 빠른 입력
                    </p>
                    <p className="mt-1 text-xs text-[#667085]">
                      템플릿 기준(요일: {recurringWeekdaysLabel} / 시간:{" "}
                      {recurringTimeRangeLabel})으로
                      제목/유형/장소/주소/시간/투표기간을 자동 입력합니다.
                    </p>
                    {isTemplateLoading && (
                      <p className="mt-2 text-xs text-[#475467]">
                        템플릿 정보를 불러오는 중입니다...
                      </p>
                    )}
                    {!isTemplateLoading && recurringTemplates.length > 0 && (
                      <div className="mt-3 grid gap-3">
                        <div>
                          <label
                            className="mb-1 block text-xs font-semibold text-[#344054]"
                            htmlFor="manage-create-template-select"
                          >
                            적용 템플릿 선택
                          </label>
                          <select
                            id="manage-create-template-select"
                            value={selectedRecurringTemplateId}
                            onChange={(event) =>
                              handleSelectTemplate(event.target.value)
                            }
                            className="h-9 w-full rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054] outline-none focus:border-[#667085]"
                          >
                            {recurringTemplates.map((template) => (
                              <option
                                key={`manage-template-option-${template.id}`}
                                value={template.id}
                              >
                                {template.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => applyRecurringScheduleTemplate()}
                            className="h-8 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054]"
                          >
                            템플릿 적용
                          </button>
                        </div>
                      </div>
                    )}
                    {!isTemplateLoading && recurringTemplates.length === 0 && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <p className="text-xs font-medium text-[#b42318]">
                          등록된 템플릿이 없습니다.
                        </p>
                        <button
                          type="button"
                          onClick={() => setActiveTab("template")}
                          className="h-8 rounded-md border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054]"
                        >
                          템플릿 설정으로 이동
                        </button>
                      </div>
                    )}
                    {templateLoadError && (
                      <p className="mt-2 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-2.5 py-2 text-xs text-[#b42318]">
                        {templateLoadError}
                      </p>
                    )}
                  </div>

                    <form
                      className="mt-4 grid gap-3 md:grid-cols-2 [&>*]:min-w-0"
                      onSubmit={handleCreateSchedule}
                    >
                    <div className="min-w-0">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-create-title"
                      >
                        일정 제목 <span className="text-[#d92d20]">*</span>
                      </label>
                      <input
                        id="manage-create-title"
                        type="text"
                        value={scheduleForm.title}
                        onChange={(event) =>
                          handleScheduleFormChange("title", event.target.value)
                        }
                        className="h-10 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                      />
                    </div>

                    <div className="min-w-0">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-create-category"
                      >
                        일정 유형
                      </label>
                      <select
                        id="manage-create-category"
                        value={scheduleForm.category}
                        onChange={(event) =>
                          handleScheduleFormChange(
                            "category",
                            event.target.value,
                          )
                        }
                        className="h-10 w-full rounded-lg border border-[#d0d5dd] bg-white px-3 text-sm outline-none focus:border-[#667085]"
                      >
                        <option value="TRAINING">TRAINING</option>
                        <option value="MATCH">MATCH</option>
                        <option value="MEETING">MEETING</option>
                      </select>
                    </div>

                    <div className="min-w-0">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-create-venue"
                      >
                        경기장 이름 <span className="text-[#d92d20]">*</span>
                      </label>
                      <input
                        id="manage-create-venue"
                        type="text"
                        value={scheduleForm.venueName}
                        onChange={(event) =>
                          handleScheduleFormChange(
                            "venueName",
                            event.target.value,
                          )
                        }
                        className="h-10 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                      />
                    </div>

                    <div>
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-create-address"
                      >
                        위치(주소) <span className="text-[#d92d20]">*</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          id="manage-create-address"
                          type="text"
                          value={scheduleForm.address}
                          readOnly
                          className="h-10 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                          placeholder="주소 찾기로 선택해 주세요."
                        />
                        <button
                          type="button"
                          onClick={handleOpenScheduleAddressSearchModal}
                          disabled={
                            isAddressSearchLoading || isScheduleAddressResolving
                          }
                          className="h-10 shrink-0 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054]"
                        >
                          {isScheduleAddressResolving
                            ? "좌표 확인 중..."
                            : "주소 찾기"}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-create-detail-address"
                      >
                        상세주소
                      </label>
                      <input
                        id="manage-create-detail-address"
                        type="text"
                        value={scheduleForm.detailAddress}
                        onChange={(event) =>
                          handleScheduleFormChange(
                            "detailAddress",
                            event.target.value,
                          )
                        }
                        className="h-10 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                        placeholder="예: 3층 2번 코트, A동 201호"
                      />
                    </div>

                    <div className="min-w-0 overflow-hidden">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-create-starts-at"
                      >
                        시작 시간 <span className="text-[#d92d20]">*</span>
                      </label>
                      <input
                        id="manage-create-starts-at"
                        type="datetime-local"
                        value={scheduleForm.startsAt}
                        onChange={(event) =>
                          handleScheduleFormChange(
                            "startsAt",
                            event.target.value,
                          )
                        }
                        className="h-10 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                      />
                    </div>

                    <div className="min-w-0 overflow-hidden">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-create-ends-at"
                      >
                        종료 시간 <span className="text-[#d92d20]">*</span>
                      </label>
                      <input
                        id="manage-create-ends-at"
                        type="datetime-local"
                        value={scheduleForm.endsAt}
                        onChange={(event) =>
                          handleScheduleFormChange("endsAt", event.target.value)
                        }
                        className="h-10 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                      />
                    </div>

                    <div className="min-w-0 overflow-hidden">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-create-vote-end"
                      >
                        투표 종료 <span className="text-[#d92d20]">*</span>
                      </label>
                      <input
                        id="manage-create-vote-end"
                        type="datetime-local"
                        value={scheduleForm.voteEndAt}
                        onChange={(event) =>
                          handleScheduleFormChange(
                            "voteEndAt",
                            event.target.value,
                          )
                        }
                        className="h-10 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-create-description"
                      >
                        메모
                      </label>
                      <textarea
                        id="manage-create-description"
                        value={scheduleForm.description}
                        onChange={(event) =>
                          handleScheduleFormChange(
                            "description",
                            event.target.value,
                          )
                        }
                        className="min-h-[92px] w-full rounded-lg border border-[#d0d5dd] px-3 py-2 text-sm outline-none focus:border-[#667085]"
                      />
                    </div>

                    {formError && (
                      <p className="md:col-span-2 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
                        {formError}
                      </p>
                    )}

                    <div className="md:col-span-2 flex flex-wrap gap-2 pt-1">
                      <button
                        type="submit"
                        disabled={isCreatingSchedule}
                        className="h-10 rounded-lg bg-[#111827] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#98a2b3]"
                      >
                        {isCreatingSchedule ? "등록 중..." : "일정 등록"}
                      </button>
                      <button
                        type="button"
                        onClick={handleCloseCreateScheduleModal}
                        className="h-10 rounded-lg border border-[#d0d5dd] bg-white px-4 text-sm font-semibold text-[#344054]"
                      >
                        취소
                      </button>
                    </div>
                    </form>
                  </section>
                </div>
              </div>
            )}

            {editingScheduleId && (
              <div className="fixed inset-0 z-50">
                <div
                  className="absolute inset-0 bg-[#0f172a]/55 backdrop-blur-[1px]"
                  onClick={handleCancelEditSchedule}
                  aria-hidden="true"
                />
                <div
                  className="relative z-10 h-full overflow-y-auto px-4 py-8 [overscroll-behavior:contain] [webkit-overflow-scrolling:touch]"
                  style={{ touchAction: "pan-y" }}
                >
                  <section className="mx-auto w-full max-w-[780px] rounded-3xl border border-[#dbe4f4] bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.3)] md:p-6">
                    <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="m-0 text-xs font-semibold tracking-wide text-[#475467]">
                        일정 수정
                      </p>
                      <h3 className="mt-1 text-xl font-semibold text-[#0f172a]">
                        {editingManageSchedule?.title || "수정할 일정"}
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={handleCancelEditSchedule}
                      aria-label="일정 수정 모달 닫기"
                      className="grid h-9 w-9 place-items-center rounded-lg border border-[#d0d5dd] bg-white text-base font-semibold text-[#344054]"
                    >
                      X
                    </button>
                  </div>

                    <form
                      className="mt-4 grid gap-3 md:grid-cols-2 [&>*]:min-w-0"
                      onSubmit={handleUpdateSchedule}
                    >
                    <div className="min-w-0">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-title"
                      >
                        일정 제목 <span className="text-[#d92d20]">*</span>
                      </label>
                      <input
                        id="manage-title"
                        type="text"
                        value={manageForm.title}
                        onChange={(event) =>
                          handleManageFormChange("title", event.target.value)
                        }
                        className="h-10 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                      />
                    </div>

                    <div className="min-w-0">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-category"
                      >
                        일정 유형
                      </label>
                      <select
                        id="manage-category"
                        value={manageForm.category}
                        onChange={(event) =>
                          handleManageFormChange("category", event.target.value)
                        }
                        className="h-10 w-full rounded-lg border border-[#d0d5dd] bg-white px-3 text-sm outline-none focus:border-[#667085]"
                      >
                        <option value="TRAINING">TRAINING</option>
                        <option value="MATCH">MATCH</option>
                        <option value="MEETING">MEETING</option>
                      </select>
                    </div>

                    <div className="min-w-0">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-venue"
                      >
                        경기장 이름 <span className="text-[#d92d20]">*</span>
                      </label>
                      <input
                        id="manage-venue"
                        type="text"
                        value={manageForm.venueName}
                        onChange={(event) =>
                          handleManageFormChange(
                            "venueName",
                            event.target.value,
                          )
                        }
                        className="h-10 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                      />
                    </div>

                    <div>
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-address"
                      >
                        위치(주소) <span className="text-[#d92d20]">*</span>
                      </label>
                      <div className="flex gap-2">
                        <input
                          id="manage-address"
                          type="text"
                          value={manageForm.address}
                          readOnly
                          className="h-10 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                          placeholder="주소 찾기로 선택해 주세요."
                        />
                        <button
                          type="button"
                          onClick={handleOpenManageAddressSearchModal}
                          disabled={
                            isAddressSearchLoading || isManageAddressResolving
                          }
                          className="h-10 shrink-0 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054]"
                        >
                          {isManageAddressResolving
                            ? "좌표 확인 중..."
                            : "주소 찾기"}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-detail-address"
                      >
                        상세주소
                      </label>
                      <input
                        id="manage-detail-address"
                        type="text"
                        value={manageForm.detailAddress}
                        onChange={(event) =>
                          handleManageFormChange(
                            "detailAddress",
                            event.target.value,
                          )
                        }
                        className="h-10 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                        placeholder="예: 3층 2번 코트, A동 201호"
                      />
                    </div>

                    <div className="min-w-0 overflow-hidden">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-starts-at"
                      >
                        시작 시간 <span className="text-[#d92d20]">*</span>
                      </label>
                      <input
                        id="manage-starts-at"
                        type="datetime-local"
                        value={manageForm.startsAt}
                        onChange={(event) =>
                          handleManageFormChange("startsAt", event.target.value)
                        }
                        className="h-10 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                      />
                    </div>

                    <div className="min-w-0 overflow-hidden">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-ends-at"
                      >
                        종료 시간 <span className="text-[#d92d20]">*</span>
                      </label>
                      <input
                        id="manage-ends-at"
                        type="datetime-local"
                        value={manageForm.endsAt}
                        onChange={(event) =>
                          handleManageFormChange("endsAt", event.target.value)
                        }
                        className="h-10 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                      />
                    </div>

                    <div className="min-w-0 overflow-hidden">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-vote-end"
                      >
                        투표 종료 <span className="text-[#d92d20]">*</span>
                      </label>
                      <input
                        id="manage-vote-end"
                        type="datetime-local"
                        value={manageForm.voteEndAt}
                        onChange={(event) =>
                          handleManageFormChange(
                            "voteEndAt",
                            event.target.value,
                          )
                        }
                        className="h-10 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="manage-description"
                      >
                        메모
                      </label>
                      <textarea
                        id="manage-description"
                        value={manageForm.description}
                        onChange={(event) =>
                          handleManageFormChange(
                            "description",
                            event.target.value,
                          )
                        }
                        className="min-h-[92px] w-full rounded-lg border border-[#d0d5dd] px-3 py-2 text-sm outline-none focus:border-[#667085]"
                      />
                    </div>

                    {manageFormError && (
                      <p className="md:col-span-2 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
                        {manageFormError}
                      </p>
                    )}

                    <div className="md:col-span-2 flex flex-wrap gap-2 pt-1">
                      <button
                        type="submit"
                        disabled={isUpdatingSchedule}
                        className="h-10 rounded-lg bg-[#111827] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#98a2b3]"
                      >
                        {isUpdatingSchedule ? "저장 중..." : "수정 저장"}
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEditSchedule}
                        className="h-10 rounded-lg border border-[#d0d5dd] bg-white px-4 text-sm font-semibold text-[#344054]"
                      >
                        취소
                      </button>
                    </div>
                    </form>
                  </section>
                </div>
              </div>
            )}
          </section>
        )}

        {activeTab === "template" && (
          <section className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="m-0 text-lg font-semibold text-[#0f172a]">
                  정기 일정 템플릿 관리
                </h2>
                <p className="mt-1 text-sm text-[#667085]">
                  템플릿을 추가/수정/삭제하고 일정 등록 시 기본 적용할 템플릿을
                  선택할 수 있습니다.
                </p>
              </div>
              <button
                type="button"
                onClick={handleOpenTemplateCreateModal}
                className="h-9 rounded-lg bg-[#111827] px-3 text-xs font-semibold text-white"
              >
                템플릿 추가
              </button>
            </div>

            {templateFormError && (
              <p className="mt-4 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
                {templateFormError}
              </p>
            )}

            {templateFormMessage && (
              <p className="mt-4 rounded-lg border border-[#abefc6] bg-[#ecfdf3] px-3 py-2 text-sm text-[#027a48]">
                {templateFormMessage}
              </p>
            )}

            <div className="mt-5 space-y-3">
              {isTemplateLoading && (
                <div className="rounded-xl border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-4 py-5 text-sm text-[#667085]">
                  템플릿 목록을 불러오는 중입니다...
                </div>
              )}

              {!isTemplateLoading && templateLoadError && (
                <div className="rounded-xl border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318]">
                  {templateLoadError}
                </div>
              )}

              {!isTemplateLoading &&
                !templateLoadError &&
                recurringTemplates.length === 0 && (
                  <div className="rounded-xl border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-4 py-5 text-sm text-[#667085]">
                    등록된 템플릿이 없습니다. 템플릿 추가 버튼으로 새 템플릿을
                    만들 수 있습니다.
                  </div>
                )}

              {recurringTemplates.map((template) => {
                const templateKey = String(template.id);
                const isSelected = templateKey === selectedRecurringTemplateId;
                return (
                  <article
                    key={`template-item-${templateKey}`}
                    className={`rounded-2xl border px-4 py-4 ${
                      isSelected
                        ? "border-[#3538cd] bg-[#eef4ff]"
                        : "border-[#eaecf0] bg-white"
                    }`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="m-0 text-base font-semibold text-[#101828]">
                          {template.name}
                        </p>
                        <div className="mt-2 space-y-1 text-sm text-[#475467]">
                          <p className="m-0">
                            일정 유형 : {template.category || "-"}
                          </p>
                          <p className="m-0">
                            활동 요일 :{" "}
                            {Array.isArray(template.weekdays) &&
                            template.weekdays.length > 0
                              ? template.weekdays.join(", ")
                              : "-"}
                          </p>
                          <p className="m-0">
                            기본 시간 : {template.startTime || "-"} ~{" "}
                            {template.endTime || "-"}
                          </p>
                          <p className="m-0">
                            기본 제목 : {template.title || "-"}
                          </p>
                          <p className="m-0">
                            기본 장소 : {template.venueName || "-"}
                          </p>
                          <p className="m-0">
                            기본 주소 : {template.address || "-"}
                            {template.detailAddress
                              ? ` (${template.detailAddress})`
                              : ""}
                          </p>
                        </div>
                        {template.description && (
                          <p className="mt-1 text-sm text-[#667085] whitespace-pre-wrap">
                            메모 : {template.description}
                          </p>
                        )}
                        {isSelected && (
                          <span className="mt-2 inline-flex h-6 items-center rounded-full bg-[#111827] px-2.5 text-[11px] font-semibold text-white">
                            기본 적용 중
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {!isSelected && (
                          <button
                            type="button"
                            onClick={() => handleSelectTemplate(templateKey)}
                            className="h-9 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054]"
                          >
                            기본으로 선택
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleOpenTemplateEditModal(template)}
                          className="h-9 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054]"
                        >
                          수정
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteTemplate(templateKey)}
                          className="h-9 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 text-xs font-semibold text-[#b42318]"
                        >
                          삭제
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            {isTemplateModalOpen && (
              <div className="fixed inset-0 z-50">
                <div
                  className="absolute inset-0 bg-[#0f172a]/55 backdrop-blur-[1px]"
                  onClick={handleCloseTemplateModal}
                  aria-hidden="true"
                />
                <div
                  className="relative z-10 h-full overflow-y-auto px-4 py-8 [overscroll-behavior:contain] [webkit-overflow-scrolling:touch]"
                  style={{ touchAction: "pan-y" }}
                >
                  <section className="mx-auto w-full max-w-[760px] rounded-3xl border border-[#dbe4f4] bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.3)] md:p-6">
                    <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="m-0 text-xs font-semibold tracking-wide text-[#475467]">
                        템플릿 관리
                      </p>
                      <h3 className="mt-1 text-xl font-semibold text-[#0f172a]">
                        {editingTemplateId ? "템플릿 수정" : "템플릿 추가"}
                      </h3>
                    </div>
                    <button
                      type="button"
                      onClick={handleCloseTemplateModal}
                      aria-label="템플릿 모달 닫기"
                      className="grid h-9 w-9 place-items-center rounded-lg border border-[#d0d5dd] bg-white text-base font-semibold text-[#344054]"
                    >
                      X
                    </button>
                  </div>

                    <form
                      className="mt-5 grid gap-4 md:grid-cols-2 [&>*]:min-w-0"
                      onSubmit={handleSaveTemplateForm}
                    >
                    <div className="md:col-span-2">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="template-name"
                      >
                        템플릿 이름 <span className="text-[#d92d20]">*</span>
                      </label>
                      <input
                        id="template-name"
                        type="text"
                        value={templateForm.name}
                        maxLength={TEMPLATE_NAME_MAX_LENGTH}
                        onChange={(event) =>
                          handleTemplateFormChange("name", event.target.value)
                        }
                        className="h-10 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                        placeholder="예: 수요일 야간 훈련"
                      />
                    </div>

                    <div>
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="template-category"
                      >
                        기본 일정 유형
                      </label>
                      <select
                        id="template-category"
                        value={templateForm.category}
                        onChange={(event) =>
                          handleTemplateFormChange(
                            "category",
                            event.target.value,
                          )
                        }
                        className="h-10 w-full rounded-lg border border-[#d0d5dd] bg-white px-3 text-sm outline-none focus:border-[#667085]"
                      >
                        <option value="TRAINING">TRAINING</option>
                        <option value="MATCH">MATCH</option>
                        <option value="MEETING">MEETING</option>
                      </select>
                    </div>

                    <div>
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="template-title"
                      >
                        기본 일정 제목
                      </label>
                      <input
                        id="template-title"
                        type="text"
                        value={templateForm.title}
                        onChange={(event) =>
                          handleTemplateFormChange("title", event.target.value)
                        }
                        className="h-10 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                        placeholder="비워두면 요일 기준 자동 제목 사용"
                      />
                    </div>

                    <div>
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="template-venue-name"
                      >
                        기본 경기장 이름
                      </label>
                      <input
                        id="template-venue-name"
                        type="text"
                        value={templateForm.venueName}
                        onChange={(event) =>
                          handleTemplateFormChange(
                            "venueName",
                            event.target.value,
                          )
                        }
                        className="h-10 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                        placeholder="예: 잠실보조경기장"
                      />
                    </div>

                    <div>
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="template-address"
                      >
                        기본 위치(주소)
                      </label>
                      <div className="flex gap-2">
                        <input
                          id="template-address"
                          type="text"
                          value={templateForm.address}
                          readOnly
                          className="h-10 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                          placeholder="주소 찾기로 선택해 주세요."
                        />
                        <button
                          type="button"
                          onClick={handleOpenTemplateAddressSearchModal}
                          disabled={
                            isAddressSearchLoading || isTemplateAddressResolving
                          }
                          className="h-10 shrink-0 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054]"
                        >
                          {isTemplateAddressResolving
                            ? "좌표 확인 중..."
                            : "주소 찾기"}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="template-detail-address"
                      >
                        기본 상세주소
                      </label>
                      <input
                        id="template-detail-address"
                        type="text"
                        value={templateForm.detailAddress}
                        onChange={(event) =>
                          handleTemplateFormChange(
                            "detailAddress",
                            event.target.value,
                          )
                        }
                        className="h-10 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                        placeholder="예: 3층 2번 코트, A동 201호"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <p className="mb-2 text-xs font-semibold text-[#344054]">
                        활동 요일 <span className="text-[#d92d20]">*</span>
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {DAY_NAMES.map((weekdayLabel) => {
                          const isSelected =
                            templateForm.weekdays.includes(weekdayLabel);
                          return (
                            <button
                              key={`template-weekday-${weekdayLabel}`}
                              type="button"
                              onClick={() =>
                                handleTemplateWeekdayToggle(weekdayLabel)
                              }
                              className={`h-9 rounded-lg px-3 text-sm font-semibold ${
                                isSelected
                                  ? "bg-[#111827] text-white"
                                  : "border border-[#d0d5dd] bg-white text-[#344054]"
                              }`}
                            >
                              {weekdayLabel}
                            </button>
                          );
                        })}
                      </div>
                      <p className="mt-2 text-xs text-[#667085]">
                        선택된 요일:{" "}
                        {templateForm.weekdays.length > 0
                          ? templateForm.weekdays.join(", ")
                          : "없음"}
                      </p>
                    </div>

                    <div className="min-w-0 overflow-hidden">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="template-start-time"
                      >
                        기본 시작 시간 <span className="text-[#d92d20]">*</span>
                      </label>
                      <input
                        id="template-start-time"
                        type="time"
                        value={templateForm.startTime}
                        onChange={(event) =>
                          handleTemplateFormChange(
                            "startTime",
                            event.target.value,
                          )
                        }
                        className="h-10 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                      />
                    </div>

                    <div className="min-w-0 overflow-hidden">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="template-end-time"
                      >
                        기본 종료 시간 <span className="text-[#d92d20]">*</span>
                      </label>
                      <input
                        id="template-end-time"
                        type="time"
                        value={templateForm.endTime}
                        onChange={(event) =>
                          handleTemplateFormChange(
                            "endTime",
                            event.target.value,
                          )
                        }
                        className="h-10 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label
                        className="mb-1 block text-xs font-semibold text-[#344054]"
                        htmlFor="template-description"
                      >
                        기본 메모
                      </label>
                      <textarea
                        id="template-description"
                        value={templateForm.description}
                        onChange={(event) =>
                          handleTemplateFormChange(
                            "description",
                            event.target.value,
                          )
                        }
                        className="min-h-[96px] w-full rounded-lg border border-[#d0d5dd] px-3 py-2 text-sm outline-none focus:border-[#667085]"
                        placeholder="비워두면 자동 안내 문구를 사용합니다."
                      />
                    </div>

                    {templateFormError && (
                      <p className="md:col-span-2 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
                        {templateFormError}
                      </p>
                    )}

                    <div className="md:col-span-2 flex flex-wrap gap-2 pt-1">
                      <button
                        type="submit"
                        className="h-10 rounded-lg bg-[#111827] px-4 text-sm font-semibold text-white"
                      >
                        {editingTemplateId ? "수정 저장" : "템플릿 추가"}
                      </button>
                      <button
                        type="button"
                        onClick={handleCloseTemplateModal}
                        className="h-10 rounded-lg border border-[#d0d5dd] bg-white px-4 text-sm font-semibold text-[#344054]"
                      >
                        취소
                      </button>
                    </div>
                    </form>
                  </section>
                </div>
              </div>
            )}
          </section>
        )}

        {activeTab === "create" && (
          <section className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
            <h2 className="m-0 text-lg font-semibold text-[#0f172a]">
              일정 등록
            </h2>
            <p className="mt-1 text-sm text-[#667085]">
              경기장 이름, 위치, 시간대와 투표 종료 시간을 함께 설정할 수
              있습니다.
            </p>
            <p className="mt-2 text-xs font-semibold text-[#475467]">
              <span className="text-[#d92d20]">*</span> 표시는 필수 입력
              항목입니다.
            </p>

            <div className="mt-4 rounded-xl border border-[#dbe4f4] bg-[#f8fafc] px-4 py-3">
              <p className="text-sm font-semibold text-[#344054]">
                정기 일정 빠른 입력
              </p>
              <p className="mt-1 text-xs text-[#667085]">
                템플릿 설정 기준(요일: {recurringWeekdaysLabel} / 시간:{" "}
                {recurringTimeRangeLabel})으로 자동 입력합니다.
              </p>
              {recurringTemplate && recurringTemplate.weekdays.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => applyRecurringScheduleTemplate()}
                    className="h-9 rounded-lg border border-[#d0d5dd] bg-white px-3 text-sm font-semibold text-[#344054] transition hover:bg-[#f2f4f7]"
                  >
                    템플릿 적용
                  </button>
                </div>
              ) : (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <p className="text-xs font-medium text-[#b42318]">
                    적용 가능한 템플릿 요일이 없습니다.
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveTab("template")}
                    className="h-8 rounded-md border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054]"
                  >
                    템플릿 설정으로 이동
                  </button>
                </div>
              )}
            </div>

            {!hasScheduleManagePermission && (
              <div className="mt-4 rounded-xl border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318]">
                현재 권한(
                {ROLE_OPTIONS.find((option) => option.value === role)?.label}
                )으로는 일정 등록이 불가합니다. 회장 또는 운영진 권한이
                필요합니다.
              </div>
            )}

            <form
              className="mt-5 grid gap-4 md:grid-cols-2 [&>*]:min-w-0"
              onSubmit={handleCreateSchedule}
            >
              <div className="min-w-0">
                <label
                  className="mb-2 block text-sm font-semibold text-[#344054]"
                  htmlFor="schedule-title"
                >
                  일정 제목 <span className="text-[#d92d20]">*</span>
                </label>
                <input
                  id="schedule-title"
                  type="text"
                  value={scheduleForm.title}
                  onChange={(event) =>
                    handleScheduleFormChange("title", event.target.value)
                  }
                  className="h-11 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                  placeholder="예: 수요일 전술 훈련"
                />
              </div>

              <div className="min-w-0">
                <label
                  className="mb-2 block text-sm font-semibold text-[#344054]"
                  htmlFor="schedule-category"
                >
                  일정 유형
                </label>
                <select
                  id="schedule-category"
                  value={scheduleForm.category}
                  onChange={(event) =>
                    handleScheduleFormChange("category", event.target.value)
                  }
                  className="h-11 w-full rounded-lg border border-[#d0d5dd] bg-white px-3 text-sm outline-none focus:border-[#667085]"
                >
                  <option value="TRAINING">TRAINING</option>
                  <option value="MATCH">MATCH</option>
                  <option value="MEETING">MEETING</option>
                </select>
              </div>

              <div className="min-w-0">
                <label
                  className="mb-2 block text-sm font-semibold text-[#344054]"
                  htmlFor="schedule-venue"
                >
                  경기장 이름 <span className="text-[#d92d20]">*</span>
                </label>
                <input
                  id="schedule-venue"
                  type="text"
                  value={scheduleForm.venueName}
                  onChange={(event) =>
                    handleScheduleFormChange("venueName", event.target.value)
                  }
                  className="h-11 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                  placeholder="예: 잠실보조경기장"
                />
              </div>

              <div>
                <label
                  className="mb-2 block text-sm font-semibold text-[#344054]"
                  htmlFor="schedule-address"
                >
                  위치(주소) <span className="text-[#d92d20]">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    id="schedule-address"
                    type="text"
                    value={scheduleForm.address}
                    readOnly
                    className="h-11 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                    placeholder="주소 찾기로 선택해 주세요."
                  />
                  <button
                    type="button"
                    onClick={handleOpenScheduleAddressSearchModal}
                    disabled={
                      isAddressSearchLoading || isScheduleAddressResolving
                    }
                    className="h-11 shrink-0 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054]"
                  >
                    {isScheduleAddressResolving
                      ? "좌표 확인 중..."
                      : "주소 찾기"}
                  </button>
                </div>
              </div>

              <div>
                <label
                  className="mb-2 block text-sm font-semibold text-[#344054]"
                  htmlFor="schedule-detail-address"
                >
                  상세주소
                </label>
                <input
                  id="schedule-detail-address"
                  type="text"
                  value={scheduleForm.detailAddress}
                  onChange={(event) =>
                    handleScheduleFormChange(
                      "detailAddress",
                      event.target.value,
                    )
                  }
                  className="h-11 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                  placeholder="예: 3층 2번 코트, A동 201호"
                />
              </div>

              <div className="min-w-0 overflow-hidden">
                <label
                  className="mb-2 block text-sm font-semibold text-[#344054]"
                  htmlFor="schedule-starts-at"
                >
                  시작 시간 <span className="text-[#d92d20]">*</span>
                </label>
                <input
                  id="schedule-starts-at"
                  type="datetime-local"
                  value={scheduleForm.startsAt}
                  onChange={(event) =>
                    handleScheduleFormChange("startsAt", event.target.value)
                  }
                  className="h-11 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                />
              </div>

              <div className="min-w-0 overflow-hidden">
                <label
                  className="mb-2 block text-sm font-semibold text-[#344054]"
                  htmlFor="schedule-ends-at"
                >
                  종료 시간 <span className="text-[#d92d20]">*</span>
                </label>
                <input
                  id="schedule-ends-at"
                  type="datetime-local"
                  value={scheduleForm.endsAt}
                  onChange={(event) =>
                    handleScheduleFormChange("endsAt", event.target.value)
                  }
                  className="h-11 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                />
              </div>

              <div className="min-w-0 overflow-hidden">
                <label
                  className="mb-2 block text-sm font-semibold text-[#344054]"
                  htmlFor="vote-ends-at"
                >
                  투표 종료 <span className="text-[#d92d20]">*</span>
                </label>
                <input
                  id="vote-ends-at"
                  type="datetime-local"
                  value={scheduleForm.voteEndAt}
                  onChange={(event) =>
                    handleScheduleFormChange("voteEndAt", event.target.value)
                  }
                  className="h-11 w-full min-w-0 rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                />
              </div>

              <div className="md:col-span-2">
                <label
                  className="mb-2 block text-sm font-semibold text-[#344054]"
                  htmlFor="schedule-description"
                >
                  메모
                </label>
                <textarea
                  id="schedule-description"
                  value={scheduleForm.description}
                  onChange={(event) =>
                    handleScheduleFormChange("description", event.target.value)
                  }
                  className="min-h-[96px] w-full rounded-lg border border-[#d0d5dd] px-3 py-2 text-sm outline-none focus:border-[#667085]"
                  placeholder="유니폼 색상, 준비물, 집합 시간 등"
                />
              </div>

              {formError && (
                <p className="md:col-span-2 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
                  {formError}
                </p>
              )}

              <div className="md:col-span-2">
                <button
                  type="submit"
                  disabled={!hasScheduleManagePermission || isCreatingSchedule}
                  className="h-11 rounded-lg bg-[#111827] px-5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#98a2b3]"
                >
                  {isCreatingSchedule ? "등록 중..." : "일정 등록하기"}
                </button>
              </div>
            </form>
          </section>
        )}

        {activeTab === "members" && (
          <section className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="m-0 text-lg font-semibold text-[#0f172a]">
                  회원
                </h2>
                <p className="mt-1 text-sm text-[#667085]">
                  팀에 속한 회원을 확인할 수 있으며, 운영진/회장은 권한에 맞는
                  관리 기능만 사용할 수 있습니다.
                </p>
              </div>
              <button
                type="button"
                onClick={loadTeamMembers}
                className="h-9 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054]"
              >
                목록 새로고침
              </button>
            </div>

            {memberActionMessage && (
              <p className="mt-4 rounded-lg border border-[#abefc6] bg-[#ecfdf3] px-3 py-2 text-sm text-[#027a48]">
                {memberActionMessage}
              </p>
            )}

            {memberActionError && (
              <p className="mt-4 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
                {memberActionError}
              </p>
            )}

            <div className="mt-4 space-y-3">
              {isMemberLoading && (
                <div className="rounded-xl border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-4 py-5 text-sm text-[#667085]">
                  회원 목록을 불러오는 중입니다...
                </div>
              )}

              {!isMemberLoading && memberLoadError && (
                <div className="rounded-xl border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318]">
                  {memberLoadError}
                </div>
              )}

              {!isMemberLoading &&
                !memberLoadError &&
                teamMembers.length === 0 && (
                  <div className="rounded-xl border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-4 py-5 text-sm text-[#667085]">
                    현재 팀 멤버 정보가 없습니다.
                  </div>
                )}

              {!isMemberLoading &&
                !memberLoadError &&
                teamMembers.map((member) => {
                  const memberKey = toUserKey(member.userId);
                  const isSelf = memberKey === currentUserId;
                  const isRoleUpdating =
                    roleUpdatingMemberIds.includes(memberKey);
                  const isExpelling = expellingMemberIds.includes(memberKey);
                  const isDelegatingChair =
                    chairDelegatingMemberIds.includes(memberKey);
                  const roleBadgeStyle =
                    member.role === "CHAIR"
                      ? "bg-[#fee4e2] text-[#b42318]"
                      : member.role === "STAFF"
                        ? "bg-[#d1fadf] text-[#067647]"
                        : "bg-[#e4e7ec] text-[#344054]";
                  const canPromote = canPromoteMemberToStaff(member);
                  const canDemote = canDemoteStaffToMember(member);
                  const canExpel = canExpelMember(member);
                  const canDelegate = canDelegateChairToMember(member);

                  return (
                    <article
                      key={`team-member-${memberKey}`}
                      className="rounded-2xl border border-[#eaecf0] bg-white px-4 py-4"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <h3 className="m-0 text-base font-semibold text-[#101828]">
                            {member.nickname}
                            {isSelf ? " (나)" : ""}
                          </h3>
                          <div className="mt-2 flex flex-wrap gap-2">
                            <span
                              className={`inline-flex h-6 items-center rounded-full px-2.5 text-[11px] font-semibold ${roleBadgeStyle}`}
                            >
                              {getTeamMemberRoleLabel(member.role)}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {canPromote && (
                            <button
                              type="button"
                              onClick={() =>
                                handleUpdateMemberRole(member, "STAFF")
                              }
                              disabled={isRoleUpdating}
                              className="h-9 rounded-lg border border-[#abefc6] bg-[#ecfdf3] px-3 text-xs font-semibold text-[#067647] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {isRoleUpdating ? "변경 중..." : "운영진 승격"}
                            </button>
                          )}
                          {canDemote && (
                            <button
                              type="button"
                              onClick={() =>
                                handleUpdateMemberRole(member, "MEMBER")
                              }
                              disabled={isRoleUpdating}
                              className="h-9 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {isRoleUpdating ? "변경 중..." : "회원 강등"}
                            </button>
                          )}
                          {canDelegate && (
                            <button
                              type="button"
                              onClick={() => handleDelegateChair(member)}
                              disabled={isDelegatingChair}
                              className="h-9 rounded-lg border border-[#f79009] bg-[#fffaeb] px-3 text-xs font-semibold text-[#b54708] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {isDelegatingChair ? "위임 중..." : "회장 위임"}
                            </button>
                          )}
                          {canExpel && (
                            <button
                              type="button"
                              onClick={() => handleExpelMember(member)}
                              disabled={isExpelling}
                              className="h-9 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 text-xs font-semibold text-[#b42318] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {isExpelling ? "추방 중..." : "추방"}
                            </button>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
            </div>

            <div className="mt-6 rounded-2xl border border-[#fecdca] bg-[#fef3f2] p-4">
              <p className="m-0 text-sm font-semibold text-[#b42318]">
                팀 탈퇴
              </p>
              <p className="mt-1 text-xs text-[#b42318]">
                팀 탈퇴 시 이 팀의 일정/투표/회원 기능 접근이 즉시 종료됩니다.
              </p>
              {isChair && (
                <p className="mt-2 text-xs font-semibold text-[#b42318]">
                  현재 회장은 탈퇴할 수 없습니다. 먼저 다른 멤버에게 회장을
                  위임해 주세요.
                </p>
              )}
              <div className="mt-3">
                <button
                  type="button"
                  onClick={handleOpenLeaveTeamModal}
                  disabled={isChair || isLeavingTeam}
                  className="h-10 rounded-lg border border-[#fecdca] bg-white px-4 text-sm font-semibold text-[#b42318] transition hover:border-[#fda29b] hover:bg-[#fee4e2] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isLeavingTeam ? "탈퇴 중..." : "팀 탈퇴"}
                </button>
              </div>
            </div>
          </section>
        )}

        {activeTab === "join" && (
          <section className="rounded-3xl border border-[#dbe4f4] bg-white/90 p-6 shadow-[0_12px_30px_rgba(15,23,42,0.06)]">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="m-0 text-lg font-semibold text-[#0f172a]">
                  가입 신청 관리
                </h2>
                <p className="mt-1 text-sm text-[#667085]">
                  회원 가입 신청은 회장/운영진 승인 후 팀 멤버로 등록됩니다.
                </p>
                {!isJoinRequestLoading && !joinRequestLoadError && (
                  <p className="mt-1 text-xs font-medium text-[#667085]">
                    총 {joinRequests.length}건
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={loadJoinRequests}
                disabled={isJoinRequestLoading}
                className="h-9 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isJoinRequestLoading ? "불러오는 중..." : "목록 새로고침"}
              </button>
            </div>

            {!hasJoinManagePermission && (
              <div className="mt-4 rounded-xl border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318]">
                현재 권한(
                {ROLE_OPTIONS.find((option) => option.value === role)?.label}
                )으로는 가입 신청을 승인할 수 없습니다.
              </div>
            )}

            {joinRequestActionMessage && (
              <p className="mt-4 rounded-lg border border-[#abefc6] bg-[#ecfdf3] px-3 py-2 text-sm text-[#027a48]">
                {joinRequestActionMessage}
              </p>
            )}

            {joinRequestActionError && (
              <p className="mt-4 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
                {joinRequestActionError}
              </p>
            )}

            <div className="mt-4 space-y-3">
              {isJoinRequestLoading && (
                <div className="rounded-xl border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-4 py-5 text-sm text-[#667085]">
                  가입 신청 목록을 불러오는 중입니다...
                </div>
              )}

              {!isJoinRequestLoading && joinRequestLoadError && (
                <div className="rounded-xl border border-[#fecdca] bg-[#fef3f2] px-4 py-3 text-sm text-[#b42318]">
                  {joinRequestLoadError}
                </div>
              )}

              {!isJoinRequestLoading &&
                !joinRequestLoadError &&
                joinRequests.length === 0 && (
                  <div className="rounded-xl border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-4 py-5 text-sm text-[#667085]">
                    가입 신청 내역이 없습니다.
                  </div>
                )}

              {!isJoinRequestLoading &&
                !joinRequestLoadError &&
                joinRequests.map((request) => {
                  const requestId = String(request.id);
                  const isProcessing =
                    processingJoinRequestIds.includes(requestId);
                  return (
                    <article
                      key={requestId}
                      className="rounded-2xl border border-[#eaecf0] bg-white px-4 py-4"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="m-0 text-base font-semibold text-[#101828]">
                              {request.requesterNickname}
                            </h3>
                            <span
                              className={`inline-flex h-6 items-center rounded-full px-2.5 text-[11px] font-semibold ${getJoinRequestStatusBadgeClass(request.status)}`}
                            >
                              {getJoinRequestStatusLabel(request.status)}
                            </span>
                          </div>
                          {request.createdAt && (
                            <p className="mt-1 text-xs text-[#667085]">
                              신청 시각: {formatDateTime(request.createdAt)}
                            </p>
                          )}
                          {request.reviewedAt && request.reviewedByNickname && (
                            <p className="mt-1 text-xs text-[#667085]">
                              처리 시각: {formatDateTime(request.reviewedAt)} (
                              {request.reviewedByNickname})
                            </p>
                          )}
                          <p className="mt-1 whitespace-pre-wrap text-sm text-[#667085]">
                            {request.message || "가입 신청 메시지가 없습니다."}
                          </p>
                        </div>

                        {hasJoinManagePermission &&
                          request.status === "PENDING" && (
                            <div className="flex gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  handleJoinDecision(request, "APPROVE")
                                }
                                disabled={isProcessing}
                                className="h-9 rounded-lg bg-[#067647] px-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {isProcessing ? "처리 중..." : "승인"}
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  handleJoinDecision(request, "REJECT")
                                }
                                disabled={isProcessing}
                                className="h-9 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 text-sm font-semibold text-[#b42318] disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {isProcessing ? "처리 중..." : "반려"}
                              </button>
                            </div>
                          )}
                      </div>
                    </article>
                  );
                })}
            </div>
          </section>
        )}

        {isLeaveTeamModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8">
            <div
              className="absolute inset-0 bg-[#0f172a]/55 backdrop-blur-[1px]"
              onClick={handleDismissLeaveTeamModal}
              aria-hidden="true"
            />

            <section className="relative z-10 w-full max-w-[560px] rounded-3xl border border-[#fecdca] bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.3)] md:p-6">
              <div>
                <p className="m-0 text-xs font-semibold tracking-wide text-[#b42318]">
                  팀 탈퇴 확인
                </p>
                <h3 className="mt-1 text-xl font-semibold text-[#0f172a]">
                  {activeTeamProfile.name}
                </h3>
                <p className="mt-1 text-sm text-[#667085]">
                  탈퇴하면 이 팀의 멤버 권한이 해제되고 내 팀 목록에서
                  제거됩니다.
                </p>
              </div>

              <div className="mt-4">
                <label
                  className="mb-1 block text-xs font-semibold text-[#344054]"
                  htmlFor="leave-team-confirm-name"
                >
                  확인용 팀명 재입력
                </label>
                <input
                  id="leave-team-confirm-name"
                  type="text"
                  value={leaveTeamConfirmName}
                  onChange={(event) => {
                    setLeaveTeamConfirmName(event.target.value);
                    setLeaveTeamModalError("");
                  }}
                  placeholder={activeTeamProfile.name}
                  className="h-10 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                />
                <p className="mt-2 text-xs text-[#667085]">
                  팀명과 정확히 일치해야 팀 탈퇴 버튼이 활성화됩니다.
                </p>
              </div>

              {leaveTeamModalError && (
                <p className="mt-3 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
                  {leaveTeamModalError}
                </p>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleLeaveTeam}
                  disabled={
                    isLeavingTeam ||
                    isChair ||
                    String(leaveTeamConfirmName || "").trim() !==
                      String(activeTeamProfile.name || "").trim()
                  }
                  className="h-10 rounded-lg border border-[#f97066] bg-[#d92d20] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isLeavingTeam ? "탈퇴 중..." : "팀 탈퇴 실행"}
                </button>
                <button
                  type="button"
                  onClick={handleDismissLeaveTeamModal}
                  disabled={isLeavingTeam}
                  className="h-10 rounded-lg border border-[#d0d5dd] bg-white px-4 text-sm font-semibold text-[#344054] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  취소
                </button>
              </div>
            </section>
          </div>
        )}

        {isCloseTeamModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8">
            <div
              className="absolute inset-0 bg-[#0f172a]/55 backdrop-blur-[1px]"
              onClick={handleDismissCloseTeamModal}
              aria-hidden="true"
            />

            <section className="relative z-10 w-full max-w-[560px] rounded-3xl border border-[#fecdca] bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.3)] md:p-6">
              <div>
                <div>
                  <p className="m-0 text-xs font-semibold tracking-wide text-[#b42318]">
                    팀 폐쇄 확인
                  </p>
                  <h3 className="mt-1 text-xl font-semibold text-[#0f172a]">
                    {activeTeamProfile.name}
                  </h3>
                  <p className="mt-1 text-sm text-[#667085]">
                    폐쇄 후에는 팀/일정/회원 정보가 복구되지 않을 수 있습니다.
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <label
                  className="mb-1 block text-xs font-semibold text-[#344054]"
                  htmlFor="close-team-confirm-name"
                >
                  확인용 팀명 재입력
                </label>
                <input
                  id="close-team-confirm-name"
                  type="text"
                  value={closeTeamConfirmName}
                  onChange={(event) => {
                    setCloseTeamConfirmName(event.target.value);
                    setCloseTeamModalError("");
                  }}
                  placeholder={activeTeamProfile.name}
                  className="h-10 w-full rounded-lg border border-[#d0d5dd] px-3 text-sm outline-none focus:border-[#667085]"
                />
                <p className="mt-2 text-xs text-[#667085]">
                  팀명과 정확히 일치해야 팀 폐쇄 버튼이 활성화됩니다.
                </p>
              </div>

              {closeTeamModalError && (
                <p className="mt-3 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-sm text-[#b42318]">
                  {closeTeamModalError}
                </p>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleCloseTeam}
                  disabled={
                    isClosingTeam ||
                    String(closeTeamConfirmName || "").trim() !==
                      String(activeTeamProfile.name || "").trim()
                  }
                  className="h-10 rounded-lg border border-[#f97066] bg-[#d92d20] px-4 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isClosingTeam ? "폐쇄 중..." : "팀 폐쇄 실행"}
                </button>
                <button
                  type="button"
                  onClick={handleDismissCloseTeamModal}
                  disabled={isClosingTeam}
                  className="h-10 rounded-lg border border-[#d0d5dd] bg-white px-4 text-sm font-semibold text-[#344054] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  취소
                </button>
              </div>
            </section>
          </div>
        )}

        {selectedScheduleDetail && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8">
            <div
              className="absolute inset-0 bg-[#0f172a]/55 backdrop-blur-[1px]"
              onClick={handleCloseScheduleDetail}
              aria-hidden="true"
            />

            <section className="relative z-10 max-h-[92vh] w-full max-w-[920px] overflow-y-auto overflow-x-hidden rounded-3xl border border-[#dbe4f4] bg-white p-5 shadow-[0_24px_70px_rgba(15,23,42,0.3)] md:p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="m-0 text-xs font-semibold tracking-wide text-[#475467]">
                    {selectedScheduleDetail.category} 상세
                  </p>
                  <h3 className="mt-1 text-xl font-semibold text-[#0f172a]">
                    {selectedScheduleDetail.title}
                  </h3>
                  <div className="mt-2 space-y-1 text-sm text-[#475467]">
                    <p className="m-0">
                      일시 : {formatDateTime(selectedScheduleDetail.startsAt)} ~{" "}
                      {formatDateTime(selectedScheduleDetail.endsAt)}
                    </p>
                    <p className="m-0">
                      장소 : {selectedScheduleDetail.venueName || "-"}
                    </p>
                    <p className="m-0">
                      주소 : {selectedScheduleDetail.address || "-"}
                      {selectedScheduleDetail.detailAddress
                        ? ` (${selectedScheduleDetail.detailAddress})`
                        : ""}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleCloseScheduleDetail}
                  aria-label="상세 모달 닫기"
                  className="grid h-9 w-9 place-items-center rounded-lg border border-[#d0d5dd] bg-white text-base font-semibold text-[#344054]"
                >
                  X
                </button>
              </div>

              <div className="mt-4 grid gap-4 md:grid-cols-12">
                <article className="rounded-2xl border border-[#dbe4f4] bg-[#f8fafc] p-4 md:col-span-5">
                  <h4 className="m-0 text-sm font-semibold text-[#0f172a]">
                    참여 현황
                  </h4>
                  <p className="mt-1 text-xs text-[#667085]">
                    투표 마감: {formatDateTime(selectedScheduleDetail.voteEndAt)}
                    까지
                  </p>
                  {hasScheduleManagePermission &&
                    selectedScheduleDetail.category === "MATCH" && (
                      <div className="mt-3 rounded-lg border border-[#eaecf0] bg-white p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="m-0 text-xs font-semibold text-[#344054]">
                              투표 독촉
                            </p>
                            <p className="mt-1 text-[11px] text-[#667085]">
                              미응답 {selectedScheduleVoteDetail.nonResponders.length}
                              명
                              {selectedScheduleReminderNextAvailableAt &&
                                isSelectedScheduleReminderCoolingDown &&
                                ` · 다음 가능: ${formatDateTime(selectedScheduleReminderNextAvailableAt)}`}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() =>
                              handleSendVoteReminder(selectedScheduleDetail.id)
                            }
                            disabled={
                              selectedScheduleReminderIsPending ||
                              isSelectedScheduleReminderCoolingDown ||
                              selectedScheduleVoteDetail.nonResponders.length ===
                                0 ||
                              !isSelectedScheduleVoteOpen
                            }
                            className="h-8 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054] disabled:cursor-not-allowed disabled:bg-[#f2f4f7] disabled:text-[#98a2b3]"
                          >
                            {selectedScheduleReminderIsPending
                              ? "전송 중..."
                              : isSelectedScheduleReminderCoolingDown
                                ? "오늘 전송 완료"
                                : "투표 독촉"}
                          </button>
                        </div>
                        {!isSelectedScheduleVoteOpen && (
                          <p className="mt-2 text-[11px] text-[#b42318]">
                            투표 가능 기간이 아닐 때는 독촉을 보낼 수 없습니다.
                          </p>
                        )}
                        {voteReminderMessage && (
                          <p className="mt-2 text-[11px] text-[#027a48]">
                            {voteReminderMessage}
                          </p>
                        )}
                        {voteReminderErrorMessage && (
                          <p className="mt-2 text-[11px] text-[#b42318]">
                            {voteReminderErrorMessage}
                          </p>
                        )}
                      </div>
                    )}
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <div className="rounded-lg bg-white px-3 py-2">
                      <p className="m-0 text-xs text-[#667085]">참여</p>
                      <p className="mt-1 text-base font-bold text-[#027a48]">
                        {selectedScheduleVoteDetail.attendees.length}명
                      </p>
                    </div>
                    <div className="rounded-lg bg-white px-3 py-2">
                      <p className="m-0 text-xs text-[#667085]">불참</p>
                      <p className="mt-1 text-base font-bold text-[#b42318]">
                        {selectedScheduleVoteDetail.absentees.length}명
                      </p>
                    </div>
                    <div className="rounded-lg bg-white px-3 py-2">
                      <p className="m-0 text-xs text-[#667085]">미참여</p>
                      <p className="mt-1 text-base font-bold text-[#344054]">
                        {selectedScheduleVoteDetail.nonResponders.length}명
                      </p>
                    </div>
                  </div>
                  <p className="mt-3 text-xs text-[#667085]">
                    내 응답:{" "}
                    <strong className="text-[#344054]">
                      {selectedScheduleDetail.votes?.[currentUserId] ===
                      "ATTEND"
                        ? "참여"
                        : selectedScheduleDetail.votes?.[currentUserId] ===
                            "ABSENT"
                          ? "불참"
                          : "미응답"}
                    </strong>
                  </p>
                  {selectedScheduleDetail.description && (
                    <div className="mt-3 rounded-lg border border-[#eaecf0] bg-white px-3 py-2 text-sm text-[#475467]">
                      {selectedScheduleDetail.description}
                    </div>
                  )}
                </article>

                <article className="rounded-2xl border border-[#dbe4f4] bg-[#f8fafc] p-4 md:col-span-7">
                  <div className="flex items-center justify-between gap-2">
                    <p className="m-0 text-sm font-semibold text-[#0f172a]">
                      투표 응답자 상세
                    </p>
                    <button
                      type="button"
                      aria-expanded={isVoteMemberListExpanded}
                      onClick={() =>
                        setIsVoteMemberListExpanded((previous) => !previous)
                      }
                      className="h-8 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054]"
                    >
                      {isVoteMemberListExpanded ? "▲ 접기" : "▼ 펼치기"}
                    </button>
                  </div>
                  {isVoteMemberListExpanded && (
                    <div className="mt-3 grid gap-3 md:grid-cols-3">
                      <div className="rounded-xl border border-[#eaecf0] bg-white p-3">
                        <p className="m-0 text-xs font-semibold text-[#027a48]">
                          참여자
                        </p>
                        <div className="mt-2 space-y-1">
                          {selectedScheduleVoteDetail.attendees.length === 0 && (
                            <p className="m-0 text-xs text-[#667085]">
                              참여 응답이 없습니다.
                            </p>
                          )}
                          {selectedScheduleVoteDetail.attendees.map(
                            (name, index) => (
                              <p
                                key={`attendee-${name}-${index}`}
                                className="m-0 text-sm font-medium text-[#101828]"
                              >
                                {name}
                              </p>
                            ),
                          )}
                        </div>
                      </div>

                      <div className="rounded-xl border border-[#eaecf0] bg-white p-3">
                        <p className="m-0 text-xs font-semibold text-[#b42318]">
                          불참자
                        </p>
                        <div className="mt-2 space-y-1">
                          {selectedScheduleVoteDetail.absentees.length === 0 && (
                            <p className="m-0 text-xs text-[#667085]">
                              불참 응답이 없습니다.
                            </p>
                          )}
                          {selectedScheduleVoteDetail.absentees.map(
                            (name, index) => (
                              <p
                                key={`absentee-${name}-${index}`}
                                className="m-0 text-sm font-medium text-[#101828]"
                              >
                                {name}
                              </p>
                            ),
                          )}
                        </div>
                      </div>

                      <div className="rounded-xl border border-[#eaecf0] bg-white p-3">
                        <p className="m-0 text-xs font-semibold text-[#344054]">
                          미참여자
                        </p>
                        <div className="mt-2 space-y-1">
                          {selectedScheduleVoteDetail.nonResponders.length ===
                            0 && (
                            <p className="m-0 text-xs text-[#667085]">
                              모든 팀원이 응답했습니다.
                            </p>
                          )}
                          {selectedScheduleVoteDetail.nonResponders.map(
                            (name, index) => (
                              <p
                                key={`non-responder-${name}-${index}`}
                                className="m-0 text-sm font-medium text-[#101828]"
                              >
                                {name}
                              </p>
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </article>
              </div>

              <article className="mt-4 rounded-2xl border border-[#dbe4f4] bg-[#f8fafc] p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h4 className="m-0 text-sm font-semibold text-[#0f172a]">
                      출석 체크
                    </h4>
                    <p className="mt-1 text-xs text-[#667085]">
                      내 위치 기반으로 출석 체크하고, 팀원 도착 상태를 확인할 수
                      있습니다.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={handleRefreshAttendanceStatus}
                      disabled={isAttendanceRefreshing}
                      className="h-8 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054] disabled:cursor-not-allowed disabled:bg-[#f2f4f7] disabled:text-[#98a2b3]"
                    >
                      {isAttendanceRefreshing ? "새로고침 중..." : "새로고침"}
                    </button>
                    <button
                      type="button"
                      onClick={handleCheckInAttendance}
                      disabled={
                        isAttendanceCheckingIn ||
                        !currentUserId ||
                        selectedScheduleAttendanceDetail.myCheckedIn
                      }
                      className="h-8 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054] disabled:cursor-not-allowed disabled:bg-[#f2f4f7] disabled:text-[#98a2b3]"
                    >
                      {isAttendanceCheckingIn
                        ? "출석 처리 중..."
                        : selectedScheduleAttendanceDetail.myCheckedIn
                          ? "출석 완료"
                          : "내 출석 체크"}
                    </button>
                  </div>
                </div>

                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    aria-expanded={isAttendanceDetailExpanded}
                    onClick={() =>
                      setIsAttendanceDetailExpanded((previous) => !previous)
                    }
                    className="h-8 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054]"
                  >
                    {isAttendanceDetailExpanded ? "▲ 접기" : "▼ 펼치기"}
                  </button>
                </div>

                {isAttendanceDetailExpanded && (
                  <>
                    {attendanceMessage && (
                      <p className="mt-3 rounded-lg border border-[#abefc6] bg-[#ecfdf3] px-3 py-2 text-xs text-[#027a48]">
                        {attendanceMessage}
                      </p>
                    )}
                    {attendanceErrorMessage && (
                      <p className="mt-3 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-xs text-[#b42318]">
                        {attendanceErrorMessage}
                      </p>
                    )}
                    <div className="mt-3 grid gap-2 sm:grid-cols-3">
                      <div className="rounded-lg bg-white px-3 py-2">
                        <p className="m-0 text-xs text-[#667085]">대상 인원</p>
                        <p className="mt-1 text-base font-bold text-[#101828]">
                          {selectedScheduleAttendanceDetail.members.length}명
                        </p>
                      </div>
                      <div className="rounded-lg bg-white px-3 py-2">
                        <p className="m-0 text-xs text-[#667085]">출석 완료</p>
                        <p className="mt-1 text-base font-bold text-[#027a48]">
                          {selectedScheduleAttendanceDetail.checkedInMembers.length}
                          명
                        </p>
                      </div>
                      <div className="rounded-lg bg-white px-3 py-2">
                        <p className="m-0 text-xs text-[#667085]">미출석</p>
                        <p className="mt-1 text-base font-bold text-[#475467]">
                          {selectedScheduleAttendanceDetail.uncheckedMembers.length}
                          명
                        </p>
                      </div>
                    </div>

                    <div className="mt-3 max-h-[240px] overflow-y-auto rounded-lg border border-[#eaecf0] bg-white p-2">
                      {selectedScheduleAttendanceDetail.members.length === 0 && (
                        <p className="m-0 px-2 py-2 text-xs text-[#667085]">
                          표시할 팀원 정보가 없습니다.
                        </p>
                      )}
                      <div className="space-y-2">
                        {selectedScheduleAttendanceDetail.members.map((member) => (
                          <div
                            key={`attendance-member-${selectedScheduleDetail.id}-${member.userId}`}
                            className="flex items-center justify-between gap-2 rounded-md border border-[#eaecf0] px-2 py-2"
                          >
                            <div className="min-w-0">
                              <p className="m-0 truncate text-sm font-semibold text-[#101828]">
                                {member.name}
                              </p>
                              <span
                                className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${getAttendanceVoteBadgeClass(member.voteType)}`}
                              >
                                {getAttendanceVoteLabel(member.voteType)}
                              </span>
                            </div>
                            <span
                              className={`inline-flex h-6 items-center rounded-full px-2 text-[11px] font-semibold ${
                                member.checkedIn
                                  ? "bg-[#ecfdf3] text-[#027a48]"
                                  : "bg-[#f2f4f7] text-[#475467]"
                              }`}
                            >
                              {member.checkedIn ? "도착 완료" : "미도착"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </article>
              {voteErrorMessage && (
                <p className="mt-3 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-xs text-[#b42318]">
                  {voteErrorMessage}
                </p>
              )}
              {selectedScheduleDetail.category === "MATCH" && (
                <article className="mt-4 rounded-2xl border border-[#dbe4f4] bg-[#f8fafc] p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="m-0 text-sm font-semibold text-[#0f172a]">
                        라인업 조회
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {!isDetailLineupEditorOpen && (
                        <p className="m-0 text-xs font-semibold text-[#475467]">
                          {selectedScheduleLineupLoadStatus.loading
                            ? "라인업 불러오는 중..."
                            : selectedScheduleSavedLineup
                              ? `저장됨 (${selectedScheduleSavedSegmentCount}쿼터)`
                              : "아직 라인업이 등록되지 않았어요!"}
                        </p>
                      )}
                      {hasLineupManagePermission && (
                        <button
                          type="button"
                          onClick={() =>
                            isDetailLineupEditorOpen
                              ? handleCloseLineupEditor()
                              : handleOpenLineupEditor(
                                  selectedScheduleDetail.id,
                                )
                          }
                          disabled={
                            selectedScheduleLineupLoadStatus.loading ||
                            lineupSavePending
                          }
                          className="h-8 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054]"
                        >
                          {isDetailLineupEditorOpen
                            ? "편집 닫기"
                            : "라인업 편집"}
                        </button>
                      )}
                    </div>
                  </div>

                  {selectedScheduleLineupLoadStatus.error && (
                    <p className="mt-3 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-xs text-[#b42318]">
                      {selectedScheduleLineupLoadStatus.error}
                    </p>
                  )}
                  {lineupErrorMessage && (
                    <p className="mt-3 rounded-lg border border-[#fecdca] bg-[#fef3f2] px-3 py-2 text-xs text-[#b42318]">
                      {lineupErrorMessage}
                    </p>
                  )}

                  {!isDetailLineupEditorOpen && selectedScheduleSavedLineup && (
                    <>
                      {selectedScheduleSavedSegmentCount > 1 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {Array.from(
                            { length: selectedScheduleSavedSegmentCount },
                            (_, index) => {
                              const segmentKey = String(index + 1);
                              const isActive =
                                selectedScheduleDetailPreviewSegmentKey ===
                                segmentKey;
                              return (
                                <button
                                  key={`detail-lineup-segment-${segmentKey}`}
                                  type="button"
                                  onClick={() =>
                                    handleSelectLineupPreviewSegment(
                                      selectedScheduleDetail.id,
                                      segmentKey,
                                    )
                                  }
                                  className={`h-8 rounded-md px-3 text-xs font-semibold ${
                                    isActive
                                      ? "bg-[#111827] text-white"
                                      : "border border-[#d0d5dd] bg-white text-[#344054]"
                                  }`}
                                >
                                  {segmentKey}쿼터
                                </button>
                              );
                            },
                          )}
                        </div>
                      )}

                      <div className="mt-3 mx-auto w-full max-w-[240px]">
                        <div
                          className="relative w-full aspect-[24/34] overflow-hidden rounded-md bg-[linear-gradient(180deg,#22c55e_0%,#16a34a_100%)]"
                          style={IOS_TEXT_SELECTION_GUARD_STYLE}
                        >
                          <div className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-white/30" />
                          <div className="absolute left-1/2 top-1/2 h-20 w-20 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/30" />
                          <div className="absolute left-1/2 top-0 h-14 w-32 -translate-x-1/2 rounded-b-xl border border-white/30" />
                          <div className="absolute bottom-0 left-1/2 h-14 w-32 -translate-x-1/2 rounded-t-xl border border-white/30" />
                          {detailLineupPreviewPlayers.map((player) => (
                            <div
                              key={`detail-saved-lineup-${selectedScheduleDetail.id}-${player.id}`}
                              className="absolute w-max -translate-x-1/2 -translate-y-1/2 whitespace-nowrap rounded-full bg-white px-2 py-1 text-[10px] font-semibold leading-none text-[#111827]"
                              style={{
                                left: `${player.x}%`,
                                top: `${player.y}%`,
                                ...IOS_TEXT_SELECTION_GUARD_STYLE,
                              }}
                            >
                              {player.name}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {isDetailLineupEditorOpen && (
                    <div className="mt-3 rounded-lg border border-[#d0d5dd] bg-white p-3">
                      <div className="mb-3 rounded-lg border border-[#eaecf0] bg-[#fcfcfd] p-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <label
                              htmlFor={`detail-lineup-segment-count-${selectedScheduleDetail.id}`}
                              className="text-xs font-semibold text-[#475467]"
                            >
                              쿼터 수
                            </label>
                            <select
                              id={`detail-lineup-segment-count-${selectedScheduleDetail.id}`}
                              value={lineupDraft.segmentCount}
                              onChange={(event) =>
                                handleLineupSegmentCountChange(
                                  event.target.value,
                                )
                              }
                              className="h-8 rounded-md border border-[#d0d5dd] bg-white px-2 text-xs font-semibold text-[#344054] outline-none"
                            >
                              {Array.from(
                                {
                                  length:
                                    MAX_LINEUP_SEGMENT_COUNT -
                                    MIN_LINEUP_SEGMENT_COUNT +
                                    1,
                                },
                                (_, index) => {
                                  const count =
                                    MIN_LINEUP_SEGMENT_COUNT + index;
                                  return (
                                    <option key={count} value={count}>
                                      {count}쿼터
                                    </option>
                                  );
                                },
                              )}
                            </select>
                          </div>
                          <p className="m-0 text-xs font-semibold text-[#667085]">
                            {lineupDraft.selectedSegment}쿼터 편집 중
                          </p>
                        </div>

                        <div className="mt-2 flex flex-wrap gap-2">
                          {Array.from(
                            { length: lineupDraft.segmentCount },
                            (_, index) => {
                              const segmentKey = String(index + 1);
                              const isActiveSegment =
                                lineupDraft.selectedSegment === segmentKey;
                              return (
                                <button
                                  key={`detail-lineup-segment-tab-${selectedScheduleDetail.id}-${segmentKey}`}
                                  type="button"
                                  onClick={() =>
                                    handleSelectLineupSegment(segmentKey)
                                  }
                                  className={`h-8 rounded-md px-3 text-xs font-semibold ${
                                    isActiveSegment
                                      ? "bg-[#111827] text-white"
                                      : "border border-[#d0d5dd] bg-white text-[#344054]"
                                  }`}
                                >
                                  {segmentKey}쿼터
                                </button>
                              );
                            },
                          )}
                        </div>
                      </div>

                      <div className="grid gap-3 lg:grid-cols-12">
                        <div className="min-w-0 lg:col-span-8">
                          <div
                            ref={lineupPitchRef}
                            className="relative mx-auto w-full max-w-[320px] aspect-[32/46] overflow-hidden rounded-lg bg-[linear-gradient(180deg,#22c55e_0%,#16a34a_100%)]"
                            onDragOver={(event) => event.preventDefault()}
                            onDrop={handleLineupPitchDrop}
                            style={LINEUP_TOUCH_GUARD_STYLE}
                          >
                            <div className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-white/40" />
                            <div className="absolute left-1/2 top-1/2 h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full border border-white/40" />
                            <div className="absolute left-1/2 top-0 h-16 w-36 -translate-x-1/2 rounded-b-xl border border-white/35" />
                            <div className="absolute bottom-0 left-1/2 h-16 w-36 -translate-x-1/2 rounded-t-xl border border-white/35" />

                            {lineupDraftPlayers
                              .filter((player) => player.active)
                              .map((player) => (
                                <div
                                  key={`detail-draft-lineup-${selectedScheduleDetail.id}-${player.id}`}
                                  draggable={hasLineupManagePermission}
                                  onDragStart={(event) =>
                                    handleLineupPlayerDragStart(
                                      event,
                                      player.id,
                                    )
                                  }
                                  onPointerDown={(event) =>
                                    handleLineupPlayerTouchPointerDown(
                                      event,
                                      player.id,
                                    )
                                  }
                                  onPointerMove={
                                    handleLineupPlayerTouchPointerMove
                                  }
                                  onPointerUp={
                                    handleLineupPlayerTouchPointerEnd
                                  }
                                  onPointerCancel={
                                    handleLineupPlayerTouchPointerEnd
                                  }
                                  className="absolute w-max -translate-x-1/2 -translate-y-1/2 cursor-move whitespace-nowrap rounded-full border border-white bg-[#111827] px-2 py-1 text-[10px] font-semibold leading-none text-white shadow"
                                  style={{
                                    left: `${player.x}%`,
                                    top: `${player.y}%`,
                                    ...LINEUP_TOUCH_GUARD_STYLE,
                                  }}
                                >
                                  {player.name}
                                </div>
                              ))}
                          </div>
                          <p className="mt-2 text-xs text-[#667085]">
                            선수 원을 드래그해서 원하는 위치에 자유 배치하세요.
                          </p>
                        </div>

                        <div className="min-w-0 lg:col-span-4">
                          <div className="overflow-hidden rounded-lg border border-[#eaecf0] bg-[#fcfcfd] p-3">
                            <div className="flex items-center justify-between">
                              <p className="m-0 text-xs font-semibold text-[#475467]">
                                선수 목록
                              </p>
                              <span className="text-xs font-semibold text-[#344054]">
                                활성 {activeLineupCount}명
                              </span>
                            </div>

                            <div className="mt-2 max-h-[270px] space-y-2 overflow-y-auto overflow-x-hidden">
                              <div className="rounded-md border border-dashed border-[#d0d5dd] bg-white px-2 py-2">
                                <p className="m-0 text-[11px] font-semibold text-[#667085]">
                                  용병 추가
                                </p>
                                <div className="mt-2 flex min-w-0 items-center gap-2">
                                  <input
                                    type="text"
                                    value={lineupCustomPlayerName}
                                    onChange={
                                      handleLineupCustomPlayerNameChange
                                    }
                                    onKeyDown={(event) => {
                                      if (event.key === "Enter") {
                                        event.preventDefault();
                                        handleAddLineupCustomPlayer();
                                      }
                                    }}
                                    placeholder="예: 용병1"
                                    maxLength={20}
                                    className="h-8 min-w-0 flex-1 rounded-md border border-[#d0d5dd] px-2 text-xs text-[#101828] outline-none"
                                  />
                                  <button
                                    type="button"
                                    onClick={handleAddLineupCustomPlayer}
                                    className="h-8 shrink-0 rounded-md border border-[#d0d5dd] bg-white px-2 text-xs font-semibold text-[#344054]"
                                  >
                                    추가
                                  </button>
                                </div>
                              </div>
                              {lineupDraftPlayers.length === 0 && (
                                <p className="m-0 rounded-md border border-[#eaecf0] bg-white px-2 py-2 text-xs text-[#667085]">
                                  팀원 목록이 비어 있어요. 용병을 추가해 배치할
                                  수 있어요.
                                </p>
                              )}
                              {lineupDraftPlayers.map((player) => (
                                <div
                                  key={`detail-lineup-player-${selectedScheduleDetail.id}-${player.id}`}
                                  className="flex min-w-0 items-center gap-2 rounded-md border border-[#eaecf0] bg-white px-2 py-2"
                                >
                                  <input
                                    type="checkbox"
                                    checked={player.active}
                                    onChange={() =>
                                      handleToggleLineupPlayerActive(player.id)
                                    }
                                    className="h-4 w-4 rounded border-[#d0d5dd]"
                                  />
                                  <div
                                    draggable={hasLineupManagePermission}
                                    onDragStart={(event) =>
                                      handleLineupPlayerDragStart(
                                        event,
                                        player.id,
                                      )
                                    }
                                  className={`min-w-0 max-w-full truncate rounded-md px-2 py-1 text-xs font-semibold ${
                                    player.active
                                      ? "bg-[#111827] text-white"
                                      : "bg-[#eaecf0] text-[#475467]"
                                  }`}
                                  title={player.name}
                                  style={IOS_TEXT_SELECTION_GUARD_STYLE}
                                >
                                  {player.name}
                                </div>
                                  {isCustomLineupPlayerId(player.id) && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleRemoveLineupCustomPlayer(
                                          player.id,
                                        )
                                      }
                                      className="ml-auto h-7 rounded-md border border-[#fda29b] bg-white px-2 text-[11px] font-semibold text-[#b42318]"
                                    >
                                      삭제
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>

                            <div className="mt-3 flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={handleResetLineup}
                                className="h-8 rounded-lg border border-[#d0d5dd] bg-white px-3 text-xs font-semibold text-[#344054]"
                              >
                                초기 배치
                              </button>
                              <button
                                type="button"
                                onClick={handleSaveLineup}
                                disabled={lineupSavePending}
                                className="h-8 rounded-lg bg-[#111827] px-3 text-xs font-semibold text-white"
                              >
                                {lineupSavePending
                                  ? "저장 중..."
                                  : "라인업 저장"}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </article>
              )}
            </section>
          </div>
        )}
      </div>
    </main>
  );
}

export default TeamDetailPage;
